import React, { useState, useEffect, useLayoutEffect, useReducer, useRef, useContext } from 'react';
import { createClient } from '@supabase/supabase-js';
import { AreaFilter, AreaSource, AreaSummary, MapOptions } from './components/AreaControls';
import { withGeozones } from './catalog/geozoneCatalog';
import { createGeozoneLayer, styleGeozoneLayer } from './map/geozoneLayer';
// lucide's Map icon is aliased to MapIcon below on purpose — a bare `Map` import here would shadow the global Map constructor; use a plain object or new window.Map() instead of `new Map()`
import { MapPin, TreePine, Utensils, ShoppingBag, X, Phone, Globe, Clock, ChevronRight, ChevronLeft, ChevronDown, Compass, Map as MapIcon, Calendar, ArrowLeft, Navigation, NavigationOff, Loader2, Search, Coffee, Landmark, BedDouble, Sun, Share2, ExternalLink, CalendarPlus, Bookmark, Heart, Star, Plus, Check, ListChecks, Send, Sparkles, Stethoscope } from 'lucide-react';
import CATEGORIES, { StickCross, catIcons, catColors, catBgs, markerColors, mutedCatColors, catEmoji, getCategoryLabel } from './config/categories';
import { VISITOR_PALETTES, visitorMapStyles, visitorTheme, visitorThemeStorage } from './theme/visitorTheme';
import { useVisitorTheme } from './theme/useVisitorTheme';
import {
  loadCatalog,
  readCatalogRowsFromSupabase,
} from './catalog/catalogLoader';
import {
  catalogVillageFilterId,
  filterEvents,
  filterExploreCatalogItems,
  filterLocationsByViewport,
  filterMapCatalogLocations,
  planMapSearchSelection,
  getCatalogVillageGroups,
  getDistanceKm,
  getExploreCatalogItems,
  getFavoritedCatalogItems,
  getMapCatalogLocations,
  getNearbyCatalogItems,
  hasUsableCoordinates,
  parseCatalogVillageFilterId,
  sortCatalogItemsForDisplay,
} from './catalog/catalogQueries';
import { computeVisibleMapCards } from './catalog/mapCards';
import { MARKER_TIER_KEYFRAMES, clearMarkerTierGhost, isInAnimationBox, prefersReducedMotion, swapMarkerTierContent, viewportAnimationBox } from './map/markerTierTransition';
import { pressScaleHandlers } from './map/pressScale';
import { isBelowMarkerHideFloor, markerContentTier, overlayZoomTier } from './map/zoomTiers';
import { GEO_IDLE, GEO_NOTICE_MS, GEO_PENDING, GEO_UNAVAILABLE, geolocationMessage, isGeolocationFailure, requestVisitorLocation } from './map/geolocationFeedback';
import { getPublicEnglishCatalogItems, withDistinctPublicEnglishNames } from './catalog/publicEnglishCatalog';
import { beirutDayKey, eventRangeYearParts, eventShortDayLabel, formatBeirutEventDate, googleCalendarAllDayRange } from './domain/eventTime';
import {
  addDays,
  bucketUpcomingEvents,
  eventDirectionsUrl,
  eventNights,
  eventRunDayKeys,
  eventSourceCredit,
  firstRemainingNight,
  weekBoardDays,
  weekBoardSplit,
} from './events/eventBoard';
import { cleanLocalizedText, strictLocalizedText, textMatchesActiveLanguage } from './domain/localizedText';
import { PUBLIC_LOCALITY_UNDER_REVIEW_LABELS, catalogCazaLabel, catalogCazaLabelOrNeutral, catalogCazaShortLabel, catalogCazaShortLabelOrNeutral, normalizeCatalogCaza, sharedCatalogCaza } from './domain/tourismRules';
import { splitOpenHoursForDisplay } from './catalog/openHoursFormatter';
import {
  findItemBySlug,
  getAlternateLinks,
  getCanonicalPath,
  getCanonicalUrl,
  parseSeoPath,
  routeDescription,
  routeForItem,
  routeForTab,
  routeTitle,
  stripTrailingSlash,
} from './seo/seoRoutes';
import { isSeoZghartaAttributable } from './seo/catalogEligibility';
import { buildJsonLdForRoute } from './seo/structuredData';
import {
  ASK_ZGHARTA_EXAMPLES,
  ASK_ZGHARTA_STORAGE_KEY,
  LEGACY_ASK_ZGHARTA_STORAGE_KEY,
  EMPTY_SAVED_ASK_ZGHARTA_PLANS,
  buildAskZghartaReferenceIndex,
  buildSavedAskZghartaPlanShareMessage,
  createSavedAskZghartaPlanRecord,
  isAskZghartaPlanSaved,
  localizedAskZghartaText,
  parseSavedAskZghartaPlans,
  removeAskZghartaPlanState,
  saveAskZghartaPlanState,
  validateAskZghartaPlan,
} from './askZgharta/askZghartaPlanner';
import {
  askZghartaPlannerConfig,
  planAskZgharta,
} from './askZgharta/askZghartaPlannerService';
import {
  ASK_ZGHARTA_CONVERSATION_ACTIONS,
  askZghartaConversationReducer,
  buildMessagesForRequest,
  createAskZghartaConversationState,
} from './askZgharta/conversationReducer';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
// The visitor app never signs anyone in. It reads the public catalog with the anon key and
// nothing else — it makes no authentication call at all. So every auth behaviour
// the library switches on by default is dead weight here, and on `/admin` it is worse than dead
// weight: a SECOND GoTrue client in the same browser context, watching the same URL the admin
// dashboard is trying to complete its sign-in on (`src/admin/adminSupabase.js`).
//
// `detectSessionInUrl: false` is the load-bearing one — it is the option that would let this
// client treat an auth callback in the address bar as its own. `persistSession: false` keeps it
// out of localStorage entirely (which also means no BroadcastChannel and no cross-tab auth
// chatter), and `autoRefreshToken: false` stops it arming a refresh timer for a session it can
// never have. A client that cannot hold a session cannot race for one.
const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { detectSessionInUrl: false, persistSession: false, autoRefreshToken: false },
  })
  : null;

// Static style objects (no RTL dependency)
const premiumTheme = {
  // Every `premiumTheme.colors.*` read now resolves to a `var(--visitor-*)` string; the values
  // live in src/theme/visitorTheme.js and are unchanged (#422).
  colors: visitorTheme,
  radii: {
    card: 20,
    control: 12,
    pill: 9999,
  },
  shadows: {
    action: visitorTheme.shadowAction,
    card: visitorTheme.shadowCard,
    mapControl: visitorTheme.shadowMapControl,
    soft: visitorTheme.shadowSoft,
  },
  sizes: {
    minTouchTarget: 44,
    bottomNavClearance: 96,
  },
};

const premiumStyles = {
  actionButton: { minWidth: 0, minHeight: 48, padding: '13px 14px', borderRadius: premiumTheme.radii.control, border: '1px solid transparent', textDecoration: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, lineHeight: 1.15, textAlign: 'center', overflowWrap: 'anywhere', transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease' },
  circleButton: { width: premiumTheme.sizes.minTouchTarget, height: premiumTheme.sizes.minTouchTarget, borderRadius: premiumTheme.radii.pill, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stickyHeader: { position: 'sticky', top: 0, zIndex: 40, background: premiumTheme.colors.surface, borderBottom: `1px solid ${premiumTheme.colors.hairline}` },
};

const eventCatStyles = visitorTheme.eventCat;
const circleBtn = premiumStyles.circleButton;
const primaryBtn = { ...premiumStyles.actionButton, minHeight: 48, fontWeight: 600, background: premiumTheme.colors.accent, color: visitorTheme.onAccent, boxShadow: 'none' };
const secondaryBtn = { ...premiumStyles.actionButton, minHeight: 48, background: premiumTheme.colors.surface, color: premiumTheme.colors.ink, border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none' };
const stickyHeader = premiumStyles.stickyHeader;
const cleanSans = "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const arabicSans = "'Tajawal', sans-serif";
const type = {
  display: { family: cleanSans, weight: 700, ls: '0' },
  body: { family: cleanSans, weight: 400 },
  ui: { family: cleanSans, weight: 500 },
  small: { family: cleanSans, weight: 500, tt: 'uppercase', ls: '0' },
};
const modalStyles = {
  actionPanel: { padding: '20px 0', borderTop: `1px solid ${premiumTheme.colors.warmLine}`, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, marginBottom: 30 },
  bodyText: { color: premiumTheme.colors.mutedInk, lineHeight: 1.75, fontSize: 16, marginBottom: 26 },
  content: { padding: '24px 22px 38px', background: premiumTheme.colors.paper },
  heroButton: { ...circleBtn, width: 44, height: 44, background: premiumTheme.colors.surface, color: premiumTheme.colors.ink, border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none' },
  heroChip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: premiumTheme.colors.surface, color: premiumTheme.colors.ink, border: 'none', borderRadius: 8, padding: '8px 11px', fontSize: 13, fontWeight: 500, textTransform: 'capitalize' },
  metaPanel: { display: 'grid', gap: 0, marginBottom: 26, borderTop: `1px solid ${premiumTheme.colors.warmLine}` },
  metaTile: { background: 'transparent', borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, padding: '18px 0', boxShadow: 'none' },
  nearbyCard: { flexShrink: 0, width: 176, background: premiumTheme.colors.paper, border: 'none', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', boxShadow: 'none', padding: 0, textAlign: 'inherit' },
  sectionTitle: { fontWeight: 600, color: premiumTheme.colors.ink, fontSize: 23, marginBottom: 16, letterSpacing: 0, fontFamily: cleanSans },
  title: { fontSize: 34, fontWeight: 700, color: premiumTheme.colors.ink, marginBottom: 16, lineHeight: 1.12, letterSpacing: 0, overflowWrap: 'anywhere', fontFamily: cleanSans },
};

const fontFor = (isRTL, role = 'body') => {
  if (isRTL) return arabicSans;
  return role === 'display' ? type.display.family : type[role]?.family || type.body.family;
};
const mapFontFor = isRTL => isRTL ? arabicSans : cleanSans;

const smallCapsStyle = (isRTL, extra = {}) => ({
  fontFamily: 'inherit',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: isRTL ? 0 : type.small.ls,
  textTransform: isRTL ? 'none' : 'uppercase',
  color: premiumTheme.colors.mutedInk,
  ...extra,
});

function catalogCazaFromSource(source) {
  if (Array.isArray(source)) return sharedCatalogCaza(source);
  if (source && typeof source === 'object') return normalizeCatalogCaza(source.caza);
  return normalizeCatalogCaza(source);
}

const displayCazaName = (source, isRTL) => catalogCazaLabelOrNeutral(catalogCazaFromSource(source), isRTL ? 'ar' : 'en');
const displayCazaShort = (source, isRTL) => catalogCazaShortLabelOrNeutral(catalogCazaFromSource(source), isRTL ? 'ar' : 'en');
// Area pill tokens (#361 Variant B). Both backgrounds are OPAQUE on purpose: the pill also
// renders over photography (map carousel card, event hero), where a translucent background lets
// the dark ink sit straight on a dark image and the label stops being readable — browser QA on
// #372 caught the Zgharta pill doing exactly that beside a legible Bsharri one. `#ECF1EF` is the
// resolution's `rgba(15,81,50,0.08)` composited over white, so every light surface it was
// designed against renders within 2/255 per channel of the original token.
const CAZA_AREA_BADGE = visitorTheme.cazaBadge;
// Neutral tint for a village group whose rows carry no recognised caza. Deliberately NOT the
// Zgharta token: colouring unowned rows green is the same cross-attribution claim in paint that
// defaulting their caza would be in data (#379).
const CAZA_AREA_BADGE_NEUTRAL = visitorTheme.cazaBadge.neutral;
// Geometry and identity only. The two areas' COLOURS moved out in #424, because a Maps polygon
// option is a value and not a `var()`, so a frozen `VISITOR_PALETTES.light.*` here is the one
// place a dark map would still be painted with the light palette. `strokeStyle` stays: solid
// Zgharta / dashed Bsharri is the shape cue that keeps the two areas apart without hue, in either
// mode, for a colourblind reader (#421 D7).
const CAZA_MAP_AREAS = [
  {
    id: 'zgharta',
    query: 'Zgharta District Lebanon',
    strokeStyle: 'solid',
    returnCenter: { lat: 34.3955, lng: 35.8945 },
    fallbackBounds: { minLat: 34.26, maxLat: 34.43, minLng: 35.83, maxLng: 36.01 },
  },
  {
    id: 'bsharri',
    query: 'Bsharri District Lebanon',
    strokeStyle: 'dashed',
    returnCenter: { lat: 34.2508, lng: 36.0106 },
    fallbackBounds: { minLat: 34.18, maxLat: 34.32, minLng: 35.92, maxLng: 36.15 },
  },
];
const paletteFor = mode => VISITOR_PALETTES[mode] || VISITOR_PALETTES.light;

// The per-mode category ink register (#428). `visitorTheme.categoryInk[id]` is the `var()` shape,
// so a surface that reads it repaints on a mode change with no React work at all — that is the
// form every CSS-parsed use below takes. `paletteFor(mode).categoryInk[id]` is the same entry as
// VALUES, for the two kinds of place where a `var()` is not a colour: an SVG presentation
// attribute, and a string concatenated with an alpha pair. An unrecognised category — no catalog
// row is, the database CHECK pins it to the eight — reads the register's own neutral entry rather
// than a light-pinned literal.
const categoryInkVars = id => visitorTheme.categoryInk[id] || visitorTheme.categoryInk.fallback;

// PlaceImage and MapCardImage are module-level components with a dozen call sites between them,
// and the single palette VALUE they need is the fallback hue for a row whose category is missing.
// It has to be a value rather than a `var()` because it is concatenated into a gradient stop
// (`${color}1f`), and threading a prop through every call site for that one case is worse than
// publishing the resolved palette once.
const VisitorPaletteContext = React.createContext(VISITOR_PALETTES.light);
// Fill opacity is a MODE decision rather than a colour: 5% of the area hue over Google's light
// raster reads as a tint, while 5% of it over the dark array is not visible at all. The stroke
// keeps its own opacity and weight in both modes.
const CAZA_AREA_FILL_OPACITY = { light: 0.05, dark: 0.11 };
const cazaAreaPaint = (areaId, mode) => {
  const palette = paletteFor(mode);
  return {
    color: palette.cazaArea[areaId] || palette.accent,
    fillOpacity: CAZA_AREA_FILL_OPACITY[mode] || CAZA_AREA_FILL_OPACITY.light,
  };
};
const cazaAreaDashIcons = color => ([{
  icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 3 },
  offset: '0',
  repeat: '14px',
}]);
const RETURN_AREA_ZOOM = 13;
// Nominatim allows at most one request per second; the boundary fetch is serialised on this.
const NOMINATIM_MIN_REQUEST_GAP_MS = 1100;
// Each boundary request is individually bounded. `fetch` has no default timeout, so without this a
// first request that STALLS rather than rejects would hold the serialised loop open forever and the
// second area would never be requested at all — head-of-line blocking that would quietly defeat the
// per-area isolation below (cold review, #372).
const NOMINATIM_REQUEST_TIMEOUT_MS = 8000;
const cazaAreaBadgeStyle = source => {
  const id = normalizeCatalogCaza(catalogCazaFromSource(source));
  if (!id) return null;
  return { id, ...(CAZA_AREA_BADGE[id] || CAZA_AREA_BADGE_NEUTRAL) };
};
const CazaAreaBadge = ({ source, isRTL, testId = 'caza-area-badge' }) => {
  const tokens = cazaAreaBadgeStyle(source);
  if (!tokens) return null;
  const { id, color, background } = tokens;
  return <span data-testid={testId} data-caza={id} style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 9999, background, color, fontSize: 10, fontWeight: 800, letterSpacing: 0, lineHeight: 1.2, flexShrink: 0 }}>{catalogCazaShortLabel(id, isRTL ? 'ar' : 'en')}</span>;
};
const geoJsonToLatLngPaths = geojson => {
  if (!geojson) return [];
  const ringToPath = ring => (ring || []).map(coord => ({ lat: coord[1], lng: coord[0] }));
  if (geojson.type === 'Polygon') return [ringToPath(geojson.coordinates?.[0])].filter(path => path.length);
  if (geojson.type === 'MultiPolygon') {
    return (geojson.coordinates || []).map(poly => ringToPath(poly?.[0])).filter(path => path.length);
  }
  return [];
};
const boundsFromPaths = paths => (paths || []).flat().reduce((acc, point) => ({
  minLat: Math.min(acc.minLat, point.lat),
  maxLat: Math.max(acc.maxLat, point.lat),
  minLng: Math.min(acc.minLng, point.lng),
  maxLng: Math.max(acc.maxLng, point.lng),
}), { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 });
const pointInCazaBounds = (lat, lng, bounds) => (
  bounds
  && lat >= bounds.minLat && lat <= bounds.maxLat
  && lng >= bounds.minLng && lng <= bounds.maxLng
);
const resolveReturnAreaId = (lat, lng, areas) => {
  const containing = CAZA_MAP_AREAS.filter(area => pointInCazaBounds(lat, lng, areas?.[area.id]?.bounds));
  const candidates = containing.length ? containing : CAZA_MAP_AREAS;
  return candidates.reduce((best, area) => {
    const dist = getDistanceKm({ lat, lng }, area.returnCenter);
    return dist < best.dist ? { id: area.id, dist } : best;
  }, { id: candidates[0].id, dist: Infinity }).id;
};
const createInitialCazaAreas = () => Object.fromEntries(CAZA_MAP_AREAS.map(area => [area.id, {
  bounds: { ...area.fallbackBounds },
  returnCenter: area.returnCenter,
}]));
// Returns RECORDS, not bare overlays: each one remembers which area it belongs to and whether it
// is a filled polygon or a dashed outline, so `applyCazaAreaTheme` can repaint it on a theme
// change without re-fetching a boundary from Nominatim (one request per area for the life of the
// map, and its usage policy allows at most one per second).
const drawCazaAreaOverlay = (area, paths, map, mode) => {
  const records = [];
  const { color, fillOpacity } = cazaAreaPaint(area.id, mode);
  paths.forEach(path => {
    if (area.strokeStyle === 'dashed') {
      records.push({
        areaId: area.id,
        kind: 'fill',
        overlay: new window.google.maps.Polygon({
          paths: path,
          strokeOpacity: 0,
          strokeWeight: 0,
          fillColor: color,
          fillOpacity,
          clickable: false,
          zIndex: 0,
          map,
        }),
      });
      records.push({
        areaId: area.id,
        kind: 'dash',
        overlay: new window.google.maps.Polyline({
          path,
          strokeColor: color,
          strokeOpacity: 0,
          strokeWeight: 0,
          icons: cazaAreaDashIcons(color),
          clickable: false,
          zIndex: 1,
          map,
        }),
      });
      return;
    }
    records.push({
      areaId: area.id,
      kind: 'outline',
      overlay: new window.google.maps.Polygon({
        paths: path,
        strokeColor: color,
        strokeOpacity: 0.75,
        strokeWeight: 4,
        fillColor: color,
        fillOpacity,
        clickable: false,
        zIndex: 0,
        map,
      }),
    });
  });
  return records;
};

// The dashed area's outline is a Polyline whose paint lives inside its `icons` array, so it is
// repainted by rebuilding that array rather than by setting `strokeColor` — the stroke itself is
// drawn at opacity 0 and only the repeated icon is visible.
const applyCazaAreaTheme = (records, mode) => {
  (records || []).forEach(({ overlay, areaId, kind }) => {
    if (!overlay || typeof overlay.setOptions !== 'function') return;
    const { color, fillOpacity } = cazaAreaPaint(areaId, mode);
    if (kind === 'dash') overlay.setOptions({ strokeColor: color, icons: cazaAreaDashIcons(color) });
    else overlay.setOptions({ strokeColor: color, fillColor: color, fillOpacity });
  });
};
const VillageFilterPanel = ({ groups, villageFilter, items, isRTL, t, onToggle, onClear, children }) => (
  <div data-testid="village-filter-panel" style={{ background: visitorTheme.panelSolid, borderRadius: 20, boxShadow: `0 4px 20px ${visitorTheme.scrim15}`, padding: 10, minWidth: 240, width: children ? 'min(296px, calc(100vw - 24px))' : undefined, maxHeight: children ? 'calc(100dvh - 180px)' : 300, overflowY: 'auto', touchAction: 'pan-y', direction: isRTL ? 'rtl' : 'ltr' }}>
    <div style={children ? { maxHeight: 220, overflowY: 'auto' } : undefined}>
    {groups.map(group => {
      const badge = CAZA_AREA_BADGE[group.caza] || CAZA_AREA_BADGE_NEUTRAL;
      return <div key={group.caza} data-testid="caza-village-group" data-caza={group.caza}>
        <div data-testid="caza-village-group-header" data-caza={group.caza} style={{ padding: '7px 10px', borderRadius: 6, background: badge.background, color: badge.color, fontSize: 11, fontWeight: 800, marginBottom: 2 }}>{displayCazaShort(group.caza, isRTL)}</div>
        {group.villages.map(entry => {
          const active = villageFilter.includes(entry.id);
          const label = displayVillageFilterLabel(entry.id, items, isRTL);
          return <button key={entry.id} type="button" data-testid="village-filter-option" data-village-id={entry.id} aria-label={t(`Toggle ${displayVillageFilterLabel(entry.id, items, false)} village filter`, `بدّل فلتر قرية ${displayVillageFilterLabel(entry.id, items, true)}`)} aria-pressed={active} onClick={() => onToggle(entry.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '10px 12px', background: active ? visitorTheme.neutralSurface : 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: isRTL ? 'right' : 'left' }}>
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${active ? visitorTheme.title : visitorTheme.checkboxLine}`, background: active ? visitorTheme.title : visitorTheme.panelSolid, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{active && <span style={{ color: visitorTheme.onInk, fontSize: 11, fontWeight: 700 }}>✓</span>}</div>
            <span style={{ flex: 1, fontSize: 14, color: visitorTheme.chipInk }}>{label}</span>
            <span style={{ fontSize: 11, color: visitorTheme.faintInk }}>{entry.count}</span>
          </button>;
        })}
      </div>;
    })}
    {villageFilter.length > 0 && <button type="button" data-testid="village-filter-clear" aria-label={t('Clear all village filters', 'مسح كل فلاتر القرى')} onClick={onClear} style={{ width: '100%', minHeight: 44, padding: '10px 12px', background: 'transparent', border: 'none', borderTop: `1px solid ${visitorTheme.neutralSurface}`, borderRadius: 0, cursor: 'pointer', fontSize: 12, color: visitorTheme.errorInk, fontWeight: 500, marginTop: 4 }}>{t('Clear All', 'مسح الكل')}</button>}
    </div>
    {children}
  </div>
);
const displayCazaStopFallback = (source, isRTL) => (
  isRTL ? `محطة في ${displayCazaName(source, true)}` : `${displayCazaName(source, false)} stop`
);
const displayCazaEventFallback = (source, isRTL) => (
  isRTL ? `فعالية في ${displayCazaShort(source, true)}` : `${displayCazaShort(source, false)} event`
);
const displayCazaRegion = (source, isRTL) => (
  isRTL ? `${displayCazaName(source, true)}، لبنان` : `${displayCazaName(source, false)}, Lebanon`
);
const donorDemoCandidateCopy = {
  description: {
    en: 'Directory listing. Confirm current hours and local details before you go.',
    ar: 'إدراج في الدليل. تحقق من الساعات والتفاصيل محليا قبل الزيارة.',
  },
  editorNote: {
    en: 'Confirm hours and access locally before you go.',
    ar: 'تحقق من الساعات والوصول محليا قبل الزيارة.',
  },
  photoCaption: {
    en: 'Photo unavailable',
    ar: 'الصورة قيد التجهيز',
  },
};

const donorDemoCandidatePhotoCaption = isRTL => isRTL ? donorDemoCandidateCopy.photoCaption.ar : donorDemoCandidateCopy.photoCaption.en;
const googleSourcedPhotoLabel = isRTL => isRTL ? 'صورة من غوغل' : 'Google photo';
const photoSourceDisclaimer = isRTL => isRTL
  ? 'قد تكون الصور من غوغل أو قيد مراجعة المصدر المحلي؛ تعرض للسياق فقط.'
  : 'Photos may be Google-sourced or pending local review; shown for context only.';
const APPROVED_COPY_LABELS = new Set([
  'approved first-party copy',
  'first-party approved',
]);
const hasApprovedCopyLabel = item => (Array.isArray(item?.editorialLabels) ? item.editorialLabels : [])
  .some(label => APPROVED_COPY_LABELS.has(String(label).trim().toLowerCase()));
const isDonorDemoApprovedCopyCandidateItem = item => hasApprovedCopyLabel(item);
const hasGoogleSourcedCatalogImage = item => Boolean(item?.image && item?.imageAttribution);
const hasPendingOrGoogleDisplayMedia = item => hasGoogleSourcedCatalogImage(item)
  || (isDonorDemoApprovedCopyCandidateItem(item) && !(item?.photos || [])
    .some(photo => photo?.attribution?.source === 'first_party_upload'));
const displayPhotoCaption = (item, isRTL) => {
  if (!isDonorDemoApprovedCopyCandidateItem(item)) return undefined;
  if (hasGoogleSourcedCatalogImage(item)) return googleSourcedPhotoLabel(isRTL);
  return donorDemoCandidatePhotoCaption(isRTL);
};
const normalizeDisplayPhoto = (photo, item) => {
  if (!photo) return null;
  if (typeof photo === 'string') return photo.trim() ? { src: photo.trim() } : null;
  const src = String(photo.src || photo.url || photo.image || photo.photoUri || '').trim();
  if (!src) return null;
  return {
    src,
    thumb: typeof photo.thumb === 'string' && photo.thumb.trim() ? photo.thumb.trim() : undefined,
    caption: photo.caption,
    attribution: photo.attribution || photo.imageAttribution || item?.imageAttribution || null,
  };
};
const getCatalogDisplayPhotos = item => {
  const candidates = [
    item?.displayPhotos,
    item?.displayMedia,
    item?.photos,
    item?.photoUrls,
    // A stored `photos` array is the authoritative set (ADR-0004 D1). `image`
    // holds that array's thumbnail derivative, so appending it here would add a
    // low-resolution duplicate of the hero to the gallery.
    (Array.isArray(item?.photos) && item.photos.length) || hasGoogleSourcedCatalogImage(item) ? null : item?.image,
  ].flatMap(value => Array.isArray(value) ? value : [value]);
  const seen = new Set();
  return candidates
    .map(photo => normalizeDisplayPhoto(photo, item))
    .filter(photo => {
      if (!photo?.src || seen.has(photo.src)) return false;
      seen.add(photo.src);
      return true;
    });
};
const getPrimaryDisplayPhoto = item => getCatalogDisplayPhotos(item)[0] || { src: item?.image || '' };
// Small surfaces (nearby carousel, planner stops) must not pull the 1200px
// master for a 56–104px box: a stored photo's `thumb` is the 400px derivative.
const displayPhotoThumbSrc = photo => photo?.thumb || photo?.src || '';
const buildCatalogPhotoAttribution = (item, isRTL, photo = null) => {
  if (photo?.attribution?.source === 'first_party_upload') return null;
  // ADR-0004 D5: a stored photo credits its own author, not the row.
  const stored = photo?.attribution?.source === 'stored_google_photo' ? photo.attribution : null;
  if (stored) {
    return {
      // No `label`: the on-image pill would duplicate the PhotoCredit line that
      // sits directly under the photo, and it cannot carry working links.
      authorName: stored.authorName,
      authorUri: stored.authorUri,
      googleMapsUri: stored.googleMapsUri,
    };
  }
  if (photo?.attribution) return { label: googleSourcedPhotoLabel(isRTL) };
  if (!hasGoogleSourcedCatalogImage(item)) return null;
  return { label: googleSourcedPhotoLabel(isRTL) };
};

// ADR-0004 D5: full credit lives on the detail page only — cards and thumbnails
// stay clean, which Google's visual requirements permit as long as a larger
// version carrying the attribution is reachable, which this is.
const PhotoCredit = ({ attribution, isRTL }) => {
  if (!attribution?.authorName && !attribution?.googleMapsUri) return null;
  const linkStyle = { color: premiumTheme.colors.accent, fontWeight: 700, textDecoration: 'none' };
  return <div data-testid="stored-photo-credit" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 2px 10px', fontSize: 11, color: premiumTheme.colors.mutedInk, direction: isRTL ? 'rtl' : 'ltr' }}>
    {attribution.authorName && <span>
      {isRTL ? 'صورة بعدسة ' : 'Photo by '}
      {attribution.authorUri
        ? <a href={attribution.authorUri} target="_blank" rel="noopener noreferrer" style={linkStyle}>{attribution.authorName}</a>
        : <strong>{attribution.authorName}</strong>}
    </span>}
    {attribution.googleMapsUri && <a href={attribution.googleMapsUri} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <ExternalLink style={{ width: 11, height: 11 }} />{isRTL ? 'عرض على خرائط غوغل' : 'View on Google Maps'}
    </a>}
  </div>;
};
const displayPhotoAttribution = (item, isRTL, photo = null) => buildCatalogPhotoAttribution(item, isRTL, photo);

// Script-strict localized text helpers live in src/domain/localizedText.js (shared with the
// admin visitor preview, #407).
const displayName = (item, isRTL) => strictLocalizedText({ en: item?.name, ar: item?.nameAr, isRTL, fallbackEn: displayCazaStopFallback(item, false), fallbackAr: displayCazaStopFallback(item, true) });
const displayVillage = (item, isRTL) => strictLocalizedText({ en: item?.village, ar: item?.villageAr, isRTL, fallbackEn: displayCazaName(item, false), fallbackAr: displayCazaName(item, true) });
const displayStandaloneVillage = (village, isRTL) => strictLocalizedText({ en: village, ar: village, isRTL, fallbackEn: 'Village', fallbackAr: 'قرية' });
const displayVillageFilterLabel = (villageId, items, isRTL) => {
  const match = (items || []).find(item => (
    catalogVillageFilterId(item) === villageId || item?.village === villageId || item?.villageAr === villageId
  ));
  if (match) return displayVillage(match, isRTL);
  return displayStandaloneVillage(parseCatalogVillageFilterId(villageId)?.village || villageId, isRTL);
};
const displayCategoryLabel = (categoryId, isRTL) => {
  const category = CATEGORIES.find(c => c.id === categoryId);
  if (category) return isRTL ? category.labelAr : category.labelEn;
  return isRTL ? 'فئة' : cleanLocalizedText(categoryId) || 'Category';
};
const displayEventName = (event, isRTL) => strictLocalizedText({ en: event?.name, ar: event?.nameAr, isRTL, fallbackEn: displayCazaEventFallback(event, false), fallbackAr: displayCazaEventFallback(event, true) });
const displayEventLocation = (event, isRTL) => strictLocalizedText({ en: event?.location, ar: event?.locationAr, isRTL, fallbackEn: displayCazaName(event, false), fallbackAr: displayCazaName(event, true) });
const displayEventDescription = (event, isRTL) => strictLocalizedText({ en: event?.description, ar: event?.descriptionAr, isRTL, fallbackEn: '', fallbackAr: '' });
const displayEventTime = (time, isRTL) => {
  const value = cleanLocalizedText(time);
  if (!value) return '';
  return isRTL ? value.replace(/\bAM\b/gi, 'ص').replace(/\bPM\b/gi, 'م') : value;
};
const displayDescription = (item, isRTL) => {
  if (hasApprovedCopyLabel(item)) {
    return strictLocalizedText({ en: item?.description, ar: item?.descriptionAr, isRTL, fallbackEn: donorDemoCandidateCopy.description.en, fallbackAr: donorDemoCandidateCopy.description.ar });
  }
  return strictLocalizedText({ en: item?.description, ar: item?.descriptionAr, isRTL, fallbackEn: displayCazaStopFallback(item, false), fallbackAr: displayCazaStopFallback(item, true) });
};
const displayEditorNote = (item, isRTL) => {
  if (isDonorDemoApprovedCopyCandidateItem(item)) {
    return isRTL ? donorDemoCandidateCopy.editorNote.ar : donorDemoCandidateCopy.editorNote.en;
  }
  return strictLocalizedText({
    en: item?.editorNote,
    ar: item?.editorNoteAr,
    isRTL,
    fallbackEn: 'Confirm hours and access locally before you go.',
    fallbackAr: 'تحقق من التفاصيل محليا قبل الزيارة.',
  });
};

const categoryEditorialLabels = {
  nature: ['Quiet morning spot', 'Best at golden hour'],
  religious: ['Quiet morning spot', 'Dress modestly'],
  heritage: ['Local history marker', 'Walkable from town center'],
  restaurant: ['Call ahead', 'Sunday lunch'],
  cafe: ['Evening stop', 'Cash may help'],
  shop: ['Local craft stop', 'Cash may help'],
  hotel: ['Call ahead', 'Check seasonal rates'],
  health: ['Call ahead', 'Confirm opening hours'],
};

const approvedCopyStatusLabels = ['First-party approved', 'Approved first-party copy', 'Photo pending', 'Online finding'];

const labelTranslations = {
  'Allow extra driving time': 'اترك وقتا إضافيا للطريق',
  'Best after rain': 'الأجمل بعد المطر',
  'Best at golden hour': 'الأجمل عند الغروب',
  'Best before sunset': 'الأجمل قبل الغروب',
  'Call ahead': 'اتصل مسبقا',
  'Cash may help': 'النقد قد يساعد',
  'Check seasonal rates': 'تحقق من الأسعار الموسمية',
  'Dress modestly': 'لباس محتشم',
  'Local review': 'مراجعة محلية',
  'Evening stop': 'محطة مسائية',
  'Family walk': 'مشي مناسب للعائلة',
  'Golden hour view': 'إطلالة وقت الغروب',
  'Good first stop': 'محطة بداية جيدة',
  'Local craft stop': 'حرف محلية',
  'Local history marker': 'علامة تاريخ محلي',
  'Local lunch': 'غداء محلي',
  'Location to confirm': 'الموقع قيد التأكيد',
  'First-party approved': 'معتمد من الجهة',
  'Approved first-party copy': 'نص أولي معتمد',
  'Old stone church': 'كنيسة حجرية قديمة',
  'Photo pending': 'صورة غوغل قيد التجهيز',
  'Pilgrimage stop': 'محطة حج',
  'Qadisha valley stop': 'محطة وادي قاديشا',
  'Quiet afternoon spot': 'مكان هادئ بعد الظهر',
  'Quiet morning spot': 'مكان هادئ صباحا',
  'Short stop': 'محطة قصيرة',
  'Start early': 'ابدأ باكرا',
  'Sunday lunch': 'غداء الأحد',
  'Trail route': 'مسار',
  'Village center': 'وسط البلدة',
  'Village shrine': 'مزار في القرية',
  'Walkable from Ehden': 'قريب مشيا من إهدن',
  'Walkable from town center': 'قريب مشيا من الوسط',
  'Windy ridge': 'حافة كثيرة الهواء',
};

const getEditorialLabels = (item, isRTL) => {
  const labels = item.editorialLabels?.length ? item.editorialLabels : categoryEditorialLabels[item.category] || ['Local review'];
  const publicLabels = labels.filter(label => !approvedCopyStatusLabels.includes(label));
  const uniqueLabels = [...new Set(publicLabels.length ? publicLabels : categoryEditorialLabels[item.category] || ['Local review'])];
  const localizedLabels = uniqueLabels
    .map(label => isRTL ? (labelTranslations[label] || label) : label)
    .filter(label => textMatchesActiveLanguage(label, isRTL));
  const fallbackLabels = (categoryEditorialLabels[item.category] || ['Local review'])
    .map(label => isRTL ? (labelTranslations[label] || label) : label);
  return (localizedLabels.length ? localizedLabels : fallbackLabels).slice(0, 2);
};

const placeImageIcons = { religious: StickCross, nature: TreePine, heritage: Landmark, restaurant: Utensils, hotel: BedDouble, shop: ShoppingBag, cafe: Coffee, health: Stethoscope, festival: Calendar, cultural: Calendar };

const emptyFavorites = () => ({ places: [], businesses: [] });

const normalizeFavoriteIds = value => (
  Array.isArray(value)
    ? value.filter(id => (typeof id === 'string' && id.trim().length > 0) || (typeof id === 'number' && Number.isFinite(id)))
    : []
);

const parseSavedFavorites = raw => {
  let parsed;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    return emptyFavorites();
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return emptyFavorites();
  return {
    places: normalizeFavoriteIds(parsed.places),
    businesses: normalizeFavoriteIds(parsed.businesses),
  };
};

// localStorage versioning + migration
const migrateLocalStorage = () => {
  try {
    const migrations = [['zgharta-data', 'zgharta-data:v1'], ['zgharta-favs', 'zgharta-favs:v1'], ['zgharta-lang', 'zgharta-lang:v1']];
    for (const [oldKey, newKey] of migrations) {
      if (localStorage.getItem(oldKey) !== null && localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, localStorage.getItem(oldKey));
        localStorage.removeItem(oldKey);
      }
    }
  } catch {}
};
migrateLocalStorage();

const PlaceImage = ({ src, category, name, caption, attribution, compact = false, isRTL = false, style = {}, onPhotoUnavailable }) => {
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const visitorPalette = useContext(VisitorPaletteContext);
  const imgRef = useRef(null);
  // An image the browser already has (CDN cache, back-navigation, a re-render
  // with the same src) fires `onLoad` BEFORE this effect runs, so resetting to
  // `false` unconditionally would leave it permanently at opacity 0 behind the
  // gradient. Seed from the element instead. Stored Supabase Storage photos hit
  // this on nearly every render; remote images were slow enough to hide it.
  useEffect(() => {
    setErr(false);
    // Seed loaded from complete/naturalWidth so a cached image is not left invisible.
    setLoaded(Boolean(imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0));
  }, [src]);
  const Icon = placeImageIcons[category] || MapPin;
  // The photo fallback is DECORATION, not text — a category-tinted gradient and one glyph at 0.42
  // opacity — so it keeps reading the raw hue register in both modes; #428 moves ink and badges,
  // never hues. Only the unrecognised-category fallback follows the theme, and it stays a VALUE
  // rather than a `var()` because it is concatenated with an alpha pair below (`${color}1f`),
  // which the browser would drop as a whole declaration (cold review, #422).
  const color = catColors[category] || visitorPalette.accent;
  const bg = catBgs[category] || premiumTheme.colors.paper;
  const attributionLabel = attribution?.label;
  const compactFallback = <div data-photo-fallback="true" style={{ position: 'relative', isolation: 'isolate', overflow: 'hidden', background: `linear-gradient(145deg, ${bg} 0%, ${visitorTheme.fallbackWash} 60%, ${color}1f 100%)`, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
    <Icon aria-hidden="true" style={{ width: 20, height: 20, color, opacity: 0.42 }} />
  </div>;
  const fallback = compact ? compactFallback : <div data-photo-fallback="true" role="img" aria-label={name} style={{ position: 'relative', isolation: 'isolate', overflow: 'hidden', background: `linear-gradient(145deg, ${bg} 0%, ${visitorTheme.fallbackWash} 60%, ${color}1f 100%)`, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
    <Icon aria-hidden="true" style={{ width: 32, height: 32, color, opacity: 0.42 }} />
  </div>;
  if (!src || err) return fallback;
  return <div style={{ position: 'relative', overflow: 'hidden', background: premiumTheme.colors.paper, ...style }}>{!loaded && <div style={{ position: 'absolute', inset: 0 }}>{fallback}</div>}<img ref={imgRef} src={src} alt={name} loading="lazy" onLoad={() => setLoaded(true)} onError={() => { setErr(true); onPhotoUnavailable?.(); }} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }} />{loaded && attributionLabel && !compact && <span style={{ position: 'absolute', bottom: 8, [isRTL ? 'right' : 'left']: 8, maxWidth: '88%', padding: '5px 8px', borderRadius: 9999, background: visitorTheme.photoWash, color: visitorTheme.onPhoto, fontSize: 10, fontWeight: 800, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{attributionLabel}</span>}</div>;
};

const MapCardImage = ({ src, category, name, caption, attribution, isRTL = false, style = {} }) => {
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const visitorPalette = useContext(VisitorPaletteContext);
  const imgRef = useRef(null);
  useEffect(() => {
    setErr(false);
    // Seed loaded from complete/naturalWidth so a cached image is not left invisible.
    setLoaded(Boolean(imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0));
  }, [src]);
  const Icon = placeImageIcons[category] || MapPin;
  // The photo fallback is DECORATION, not text — a category-tinted gradient and one glyph at 0.42
  // opacity — so it keeps reading the raw hue register in both modes; #428 moves ink and badges,
  // never hues. Only the unrecognised-category fallback follows the theme, and it stays a VALUE
  // rather than a `var()` because it is concatenated with an alpha pair below (`${color}1f`),
  // which the browser would drop as a whole declaration (cold review, #422).
  const color = catColors[category] || visitorPalette.accent;
  const bg = catBgs[category] || premiumTheme.colors.paper;
  const attributionLabel = attribution?.label;
  const fallback = <div data-photo-fallback="true" style={{ position: 'relative', isolation: 'isolate', overflow: 'hidden', background: `linear-gradient(145deg, ${bg} 0%, ${visitorTheme.fallbackWash} 60%, ${color}1f 100%)`, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
    <Icon aria-hidden="true" style={{ width: 32, height: 32, color, opacity: 0.42 }} />
  </div>;
  if (!src || err) return fallback;
  return <div style={{ position: 'relative', overflow: 'hidden', ...style }}>{!loaded && <div style={{ position: 'absolute', inset: 0 }}>{fallback}</div>}<img ref={imgRef} src={src} alt={name} loading="lazy" onLoad={() => setLoaded(true)} onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }} />{loaded && attributionLabel && <span style={{ position: 'absolute', bottom: 6, [isRTL ? 'right' : 'left']: 6, maxWidth: '86%', padding: '4px 6px', borderRadius: 9999, background: visitorTheme.photoWash, color: visitorTheme.onPhoto, fontSize: 8, fontWeight: 800, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{attributionLabel}</span>}</div>;
};

function getInitialSeoRoute() {
  if (typeof window === 'undefined') return parseSeoPath('/');
  return parseSeoPath(window.location.pathname);
}

function upsertMeta(selector, createAttrs, updateAttrs) {
  if (typeof document === 'undefined') return;
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('meta');
    Object.entries(createAttrs).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  Object.entries(updateAttrs).forEach(([key, value]) => node.setAttribute(key, value));
}

function upsertLink(rel, attrs) {
  if (typeof document === 'undefined') return;
  const hreflang = attrs.hreflang ? `[hreflang="${attrs.hreflang}"]` : '';
  let node = document.head.querySelector(`link[rel="${rel}"]${hreflang}`);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
}

function updateJsonLd(jsonLd) {
  if (typeof document === 'undefined') return;
  let node = document.head.querySelector('script[data-zgharta-jsonld="primary"]');
  if (!jsonLd) {
    if (node) node.remove();
    return;
  }
  if (!node) {
    node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute('data-zgharta-jsonld', 'primary');
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(jsonLd);
}

function applyDocumentSeo({ route, item, lang }) {
  if (typeof document === 'undefined') return;
  const title = routeTitle(route, item);
  const description = routeDescription(route, item);
  const canonicalUrl = getCanonicalUrl(route);
  const jsonLd = buildJsonLdForRoute({ route, item, lang });

  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.title = title;

  upsertMeta('meta[name="description"]', { name: 'description' }, { content: description });
  upsertMeta('meta[name="robots"]', { name: 'robots' }, { content: 'index, follow, max-image-preview:large' });
  upsertMeta('meta[property="og:title"]', { property: 'og:title' }, { content: title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description' }, { content: description });
  upsertMeta('meta[property="og:url"]', { property: 'og:url' }, { content: canonicalUrl });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, { content: lang === 'ar' ? 'ar_LB' : 'en_US' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, { content: title });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, { content: description });
  upsertLink('canonical', { href: canonicalUrl });
  getAlternateLinks(route).forEach(link => {
    upsertLink('alternate', { hreflang: link.lang, href: link.href });
  });
  updateJsonLd(jsonLd);

  const routeMarker = document.head.querySelector('meta[name="zgharta-seo-route"]') || document.createElement('meta');
  routeMarker.setAttribute('name', 'zgharta-seo-route');
  routeMarker.setAttribute('content', getCanonicalPath(route));
  if (!routeMarker.parentNode) document.head.appendChild(routeMarker);
}

function updateBrowserRoute(route, { replace = false } = {}) {
  if (typeof window === 'undefined') return;
  const path = getCanonicalPath(route);
  if (stripTrailingSlash(window.location.pathname) === path) return;
  window.history[replace ? 'replaceState' : 'pushState']({ zghartaRoute: route }, '', path);
}

const askStatusLabel = (state, t) => ({
  empty: t('Ask for a local plan', 'اطلب خطة محلية'),
  loading: t('Building a local plan...', 'يتم بناء خطة محلية...'),
  complete: t('Ready', 'جاهزة'),
  partial: t('Starting plan', 'خطة بداية'),
  no_results: t('No match yet', 'لا توجد نتيجة بعد'),
  validation_error: t('Plan needs review', 'الخطة تحتاج مراجعة'),
  provider_error: t('Planner unavailable', 'المخطط غير متاح'),
}[state] || t('Ready', 'جاهزة'));

const AskZghartaPanel = ({
  catalog,
  surface = 'guide',
  lang,
  isRTL,
  t,
  villageContext,
  savedAskPlans,
  onSavePlan,
  onRemovePlan,
  onSharePlan,
  onOpenItem,
  onViewItemOnMap,
}) => {
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState('empty');
  const [validationErrors, setValidationErrors] = useState([]);
  const [conversation, dispatchConversation] = useReducer(askZghartaConversationReducer, undefined, createAskZghartaConversationState);
  const transcriptRef = useRef(null);
  // "New plan" stays tappable mid-flight, so a reset can race an in-flight
  // request — a stale response must never land in the fresh conversation.
  const planRunSeqRef = useRef(0);
  const examples = ASK_ZGHARTA_EXAMPLES[lang] || ASK_ZGHARTA_EXAMPLES.en;
  const compact = surface === 'explore';
  const hasConversation = conversation.turns.length > 0;
  const loading = phase === 'loading';
  const panelTitle = t('Ask Zgharta', 'اسأل زغرتا');
  const panelSubtitle = compact
    ? t('Ask for places, villages, meals, walks, or a route; matching stops open from this directory.', 'اسأل عن أماكن أو قرى أو وجبات أو مسارات؛ تفتح النتائج من هذا الدليل.')
    : t('Ask for a local route, then save it or open stops on the map.', 'اطلب مسارا محليا، ثم احفظه أو افتح محطاته على الخريطة.');
  const visibleExamples = compact ? examples : [
    t('Nature plus lunch', 'طبيعة وغداء'),
    t('Churches and coffee', 'كنائس وقهوة'),
    t('Easy walk in Ehden', 'نزهة سهلة في إهدن'),
    t('Find a hotel', 'ابحث عن فندق'),
  ];
  const followUpSuggestions = [
    t('Make it shorter', 'اختصر الخطة'),
    t('Swap the cafe', 'بدّل المقهى'),
    t('Add something for kids', 'أضف شيئاً للأطفال'),
    t('Make it rainy-day friendly', 'مناسب ليوم ماطر'),
  ];
  const lastAssistantIndex = conversation.turns.reduce((last, turn, index) => (turn.role === 'assistant' ? index : last), -1);
  const chipStyle = chipCompact => ({ flexShrink: chipCompact ? 0 : 1, maxWidth: chipCompact ? 196 : 'none', minHeight: 44, padding: chipCompact ? '8px 11px' : '7px 9px', borderRadius: chipCompact ? 14 : 12, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: premiumTheme.colors.ink, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, fontSize: 13, lineHeight: 1.25, fontWeight: 600, textAlign: isRTL ? 'right' : 'left', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' });
  useEffect(() => {
    const node = transcriptRef.current;
    if (!node) return;
    // A new plan card is usually taller than the transcript viewport — land on
    // its header (status + title), not its tail. Contained: only node.scrollTop
    // moves, the page never scrolls.
    const lastTurn = conversation.turns[conversation.turns.length - 1];
    if (lastTurn?.role === 'assistant') {
      const cards = node.querySelectorAll('[data-ask-turn="assistant"]');
      const card = cards[cards.length - 1];
      if (card) {
        node.scrollTop += card.getBoundingClientRect().top - node.getBoundingClientRect().top;
        return;
      }
    }
    node.scrollTop = node.scrollHeight;
  }, [conversation.turns.length, phase]);

  const runPlan = async rawQuery => {
    const trimmed = rawQuery.trim();
    if (!trimmed || phase === 'loading') return;

    // Wire format: messages carry prior history only — the new query rides
    // request.query — so project them from the state BEFORE this turn lands.
    const messages = buildMessagesForRequest(conversation);
    const runSeq = planRunSeqRef.current + 1;
    planRunSeqRef.current = runSeq;
    dispatchConversation({ type: ASK_ZGHARTA_CONVERSATION_ACTIONS.userTurnAdded, content: trimmed });
    setQuery('');
    setPhase('loading');
    setValidationErrors([]);
    const now = new Date();
    const request = {
      query: trimmed,
      locale: lang,
      createdAt: now.toISOString(),
      context: {
        village: villageContext || undefined,
        visitDate: beirutDayKey(now),
      },
    };
    try {
      const result = await planAskZgharta({
        catalog,
        request,
        messages,
        remoteConfig: askZghartaPlannerConfig,
        now,
      });
      if (planRunSeqRef.current !== runSeq) return;
      const nextPlan = result.plan;
      const validation = validateAskZghartaPlan(nextPlan, buildAskZghartaReferenceIndex(catalog));
      if (!validation.ok) {
        // An invalid plan's selections must not enter conversation history —
        // no planReceived dispatch; the error turn is ephemeral, from phase.
        setValidationErrors(validation.errors);
        setPhase('validation_error');
        return;
      }
      dispatchConversation({ type: ASK_ZGHARTA_CONVERSATION_ACTIONS.planReceived, plan: nextPlan });
      setPhase('ready');
    } catch (err) {
      if (planRunSeqRef.current !== runSeq) return;
      setValidationErrors([err instanceof Error ? err.message : t('Planner unavailable', 'المخطط غير متاح')]);
      setPhase('provider_error');
    }
  };

  const resetConversation = () => {
    planRunSeqRef.current += 1;
    dispatchConversation({ type: ASK_ZGHARTA_CONVERSATION_ACTIONS.reset });
    setQuery('');
    setPhase('empty');
    setValidationErrors([]);
  };

  // Fallback plans carry numeric Supabase ids while refs may be strings —
  // coerce both sides (precedent: focusCatalogRefOnMap).
  const resolvePlanItemCatalogEntry = ref => {
    if (!ref) return null;
    const pool = ref.type === 'place' ? catalog?.places : ref.type === 'business' ? catalog?.businesses : ref.type === 'event' ? catalog?.events : null;
    if (!Array.isArray(pool)) return null;
    return pool.find(entry => String(entry.id) === String(ref.id)) || null;
  };

  const renderPlanCard = (turnPlan, key) => {
    const saved = isAskZghartaPlanSaved(savedAskPlans, turnPlan.id);
    const stopNumbers = {};
    let stopCount = 0;
    turnPlan.sections?.forEach(section => (section.items || []).forEach(item => {
      stopCount += 1;
      stopNumbers[`${section.id}:${item.id}`] = stopCount;
    }));
    const savedRecord = savedAskPlans.plans.find(record => record.id === turnPlan.id) || null;
    const firstMapItem = turnPlan.sections
      ?.flatMap(section => section.items || [])
      .find(item => item.ref?.type === 'place' || item.ref?.type === 'business') || null;
    const saveOrRemovePlan = () => {
      if (saved) onRemovePlan(turnPlan.id);
      else onSavePlan(turnPlan);
    };
    const sharePlan = () => {
      onSharePlan(savedRecord || createSavedAskZghartaPlanRecord(turnPlan, catalog));
    };
    return <div key={key} data-ask-turn="assistant" style={{ borderRadius: 14, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, padding: 12, textAlign: isRTL ? 'right' : 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row', marginBottom: 16 }}>
        <div style={{ minWidth: 0, flexBasis: '100%' }}>
          <p style={smallCapsStyle(isRTL, { color: turnPlan.status === 'no_results' ? visitorTheme.warnInk : premiumTheme.colors.accent, marginBottom: 5 })}>{askStatusLabel(turnPlan.status || 'complete', t)}</p>
          <h3 style={{ fontFamily: 'inherit', color: premiumTheme.colors.ink, fontSize: 24, lineHeight: 1.06, fontWeight: 700, marginBottom: 6 }}>{localizedAskZghartaText(turnPlan.title, lang)}</h3>
          <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 13, lineHeight: 1.5 }}>{localizedAskZghartaText(turnPlan.summary, lang)}</p>
        </div>
        {turnPlan.status !== 'no_results' && <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={saveOrRemovePlan} aria-label={saved ? t('Remove saved plan', 'إزالة الخطة المحفوظة') : t('Save plan', 'احفظ الخطة')} style={{ width: 44, height: 44, borderRadius: 9999, border: `1px solid ${saved ? premiumTheme.colors.accent : premiumTheme.colors.warmLine}`, background: saved ? premiumTheme.colors.accent : visitorTheme.fallbackWash, color: saved ? visitorTheme.onAccent : premiumTheme.colors.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{saved ? <Check style={{ width: 17, height: 17 }} /> : <Bookmark style={{ width: 17, height: 17 }} />}</button>
          <button onClick={sharePlan} aria-label={t('Share plan', 'شارك الخطة')} style={{ width: 44, height: 44, borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: visitorTheme.fallbackWash, color: premiumTheme.colors.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Share2 style={{ width: 17, height: 17 }} /></button>
          {firstMapItem && <button onClick={() => onViewItemOnMap(firstMapItem)} aria-label={t('View plan on map', 'اعرض الخطة على الخريطة')} style={{ width: 44, height: 44, borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: visitorTheme.fallbackWash, color: premiumTheme.colors.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MapIcon style={{ width: 17, height: 17 }} /></button>}
        </div>}
      </div>

      {turnPlan.warnings?.map(warning => <div key={warning.code} style={{ borderRadius: 12, background: visitorTheme.warnSoftBg, border: `1px solid ${visitorTheme.warnSoftLine}`, padding: 10, color: visitorTheme.warnSoftInk, fontSize: 12, lineHeight: 1.45, marginBottom: 10 }}>{localizedAskZghartaText(warning.message, lang)}</div>)}

      {turnPlan.sections?.map(section => <div key={section.id} style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent })}>{localizedAskZghartaText(section.title, lang)}</p>
        {section.items.map((item, itemIndex) => {
          const meta = item.meta || {};
          const stopIndex = stopNumbers[`${section.id}:${item.id}`];
          const resolvedEntry = resolvePlanItemCatalogEntry(item.ref);
          const stopPhoto = resolvedEntry ? getPrimaryDisplayPhoto(resolvedEntry) : { src: '' };
          const itemTitle = localizedAskZghartaText(item.title, lang);
          const CatIcon = meta.category ? catIcons[meta.category] : null;
          const stopInk = CatIcon ? categoryInkVars(meta.category) : null;
          const catColor = stopInk ? stopInk.ink : null;
          const catBg = stopInk ? stopInk.chipBg : null;
          const villageLabel = isRTL ? (meta.villageAr || meta.village) : meta.village;
          const specialties = (Array.isArray(meta.specialties) ? meta.specialties : []).filter(s => textMatchesActiveLanguage(s, isRTL)).slice(0, 3);
          const eventDateLocale = lang === 'ar' ? 'ar-LB' : 'en-US';
          const eventDateLabel = formatBeirutEventDate(meta.date, eventDateLocale, { month: 'short', day: 'numeric' });
          const eventDateValid = eventDateLabel != null;
          const eventIsToday = eventDateValid && (() => {
            const eventKey = beirutDayKey(meta.date);
            const todayKey = beirutDayKey(new Date());
            return eventKey != null && eventKey === todayKey;
          })();
          const eventDateText = eventDateValid
            ? `${eventIsToday ? t('Today', 'اليوم') : eventDateLabel}${meta.time ? ` · ${meta.time}` : ''}`
            : null;
          const hasMetaRow = CatIcon || typeof meta.rating === 'number' || villageLabel || eventDateText;
          return <div key={item.id} data-testid="ask-zgharta-stop" style={{ position: 'relative', [isRTL ? 'paddingRight' : 'paddingLeft']: 34 }}>
            <div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 0, top: 12, width: 24, height: 24, borderRadius: 9999, background: premiumTheme.colors.accent, color: visitorTheme.onAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>{stopIndex}</div>
            {itemIndex < section.items.length - 1 && <div aria-hidden="true" style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 11, top: 40, bottom: -9, width: 2, background: premiumTheme.colors.warmLine }} />}
            <article style={{ borderRadius: 0, border: 'none', borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, background: 'transparent', padding: '12px 0 20px', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <PlaceImage src={displayPhotoThumbSrc(stopPhoto)} category={meta.category} name={itemTitle} compact isRTL={isRTL} style={{ width: 56, height: 56, borderRadius: 12, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ fontSize: 15, lineHeight: 1.25, color: premiumTheme.colors.ink, fontWeight: 800, marginBottom: 6 }}>{itemTitle}</h4>
                  {hasMetaRow && <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                    {eventDateText && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 9999, background: visitorTheme.accentBright08, border: `1px solid ${premiumTheme.colors.warmLine}`, color: premiumTheme.colors.accent, fontSize: 11.5, fontWeight: 700 }}><Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />{eventDateText}</span>}
                    {CatIcon && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 9999, background: catBg, color: catColor, fontSize: 11.5, fontWeight: 700 }}><CatIcon style={{ width: 12, height: 12 }} />{getCategoryLabel(meta.category, lang)}</span>}
                    {typeof meta.rating === 'number' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, fontWeight: 700, color: premiumTheme.colors.ink }}><Star style={{ width: 12, height: 12, color: visitorTheme.star, fill: visitorTheme.star }} />{meta.rating.toFixed(1)}</span>}
                    {villageLabel && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', padding: '4px 9px', borderRadius: 9999, background: visitorTheme.glass92, border: `1px solid ${premiumTheme.colors.warmLine}`, color: premiumTheme.colors.mutedInk, fontSize: 11.5, fontWeight: 700 }}><MapPin style={{ width: 12, height: 12, flexShrink: 0 }} /><span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{villageLabel}</span></span>}
                  </div>}
                  <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 14, lineHeight: 1.6, marginBottom: specialties.length ? 8 : 0 }}>{localizedAskZghartaText(item.reason, lang)}</p>
                  {specialties.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                    {specialties.map((s, i) => <span key={i} style={{ background: catBg || premiumTheme.colors.paper, color: catColor || premiumTheme.colors.accent, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 9999 }}>{s}</span>)}
                  </div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => onOpenItem(item)} style={{ flex: 1, minHeight: 44, padding: '8px 11px', borderRadius: 9999, border: 'none', background: premiumTheme.colors.accent, color: visitorTheme.onAccent, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{t('Open', 'افتح')}</button>
                {item.ref?.type !== 'event' && <button onClick={() => onViewItemOnMap(item)} style={{ flex: 1, minHeight: 44, padding: '8px 11px', borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: premiumTheme.colors.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><MapIcon style={{ width: 13, height: 13 }} />{t('Map', 'خريطة')}</button>}
              </div>
            </article>
          </div>;
        })}
      </div>)}
    </div>;
  };

  return <section style={{ position: 'relative', overflow: 'hidden', borderRadius: 0, border: 'none', background: 'transparent', padding: compact ? 16 : 0, boxShadow: 'none', direction: isRTL ? 'rtl' : 'ltr' }}>
    <div style={{ display: 'flex', gap: compact ? 12 : 10, alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row', marginBottom: compact ? 14 : 10 }}>
      <div style={{ width: compact ? 42 : 36, height: compact ? 42 : 36, borderRadius: compact ? 14 : 12, background: visitorTheme.accentTintStrong, display: 'flex', alignItems: 'center', justifyContent: 'center', color: premiumTheme.colors.accent, flexShrink: 0 }}>
        <Sparkles style={{ width: compact ? 21 : 18, height: compact ? 21 : 18 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, textAlign: isRTL ? 'right' : 'left' }}>
        <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent, marginBottom: compact ? 4 : 3 })}>{t('Ask Zgharta', 'اسأل زغرتا')}</p>
        <h2 style={{ fontFamily: 'inherit', fontSize: compact ? 24 : 27, lineHeight: compact ? 1.02 : 1.08, fontWeight: 700, color: premiumTheme.colors.ink, marginBottom: compact ? 5 : 3 }}>{panelTitle}</h2>
        <p style={{ margin: 0, color: premiumTheme.colors.mutedInk, fontSize: 14, lineHeight: compact ? 1.5 : 1.38 }}>{panelSubtitle}</p>
      </div>
      {hasConversation && <button onClick={resetConversation} style={{ flexShrink: 0, minHeight: 44, padding: '6px 11px', borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: visitorTheme.fallbackWash, color: premiumTheme.colors.accent, cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}>{t('New plan', 'خطة جديدة')}</button>}
    </div>

    {hasConversation && <div ref={transcriptRef} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', marginBottom: 10 }}>
      {conversation.turns.map((turn, index) => {
        if (turn.role === 'user') return <div key={index} style={{ alignSelf: 'flex-end', maxWidth: '85%', padding: '8px 13px', borderRadius: 16, background: premiumTheme.colors.accentSoft, color: premiumTheme.colors.ink, fontSize: 13.5, lineHeight: 1.4, fontWeight: 600, fontFamily: fontFor(isRTL, 'body'), textAlign: isRTL ? 'right' : 'left' }}>{turn.content}</div>;
        return <React.Fragment key={index}>
          {renderPlanCard(turn.plan, index)}
          {index === lastAssistantIndex && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 }}>
            {followUpSuggestions.map(suggestion => <button key={suggestion} onClick={() => runPlan(suggestion)} disabled={loading} style={chipStyle(false)}>{suggestion}</button>)}
          </div>}
        </React.Fragment>;
      })}
      {loading && <div style={{ borderRadius: 14, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, padding: 12, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <Loader2 style={{ width: 16, height: 16, color: premiumTheme.colors.accent, animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        <span style={{ color: premiumTheme.colors.accent, fontSize: 12.5, fontWeight: 700 }}>{askStatusLabel('loading', t)}</span>
      </div>}
      {(phase === 'provider_error' || phase === 'validation_error') && <div style={{ borderRadius: 12, padding: 10, fontSize: 12, lineHeight: 1.45, textAlign: isRTL ? 'right' : 'left', ...(phase === 'provider_error'
        ? { background: visitorTheme.errorSoftBg, border: `1px solid ${visitorTheme.errorSoftLine}`, color: visitorTheme.errorSoftInk }
        : { background: visitorTheme.noticeSoftBg, border: `1px solid ${visitorTheme.noticeSoftLine}`, color: visitorTheme.noticeSoftInk }) }}>
        <strong>{askStatusLabel(phase, t)}</strong>
        {validationErrors[0] ? <span> · {validationErrors[0]}</span> : null}
      </div>}
    </div>}

    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
      <textarea value={query} onChange={event => setQuery(event.target.value)} aria-label={t('Ask Zgharta for a plan', 'اسأل زغرتا عن خطة')} placeholder={t('Churches, coffee, an easy walk...', 'كنائس وقهوة ومشي سهل...')} rows={2} style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', resize: 'vertical', minHeight: compact ? 52 : 44, borderRadius: compact ? 16 : 12, border: `1px solid ${premiumTheme.colors.warmLine}`, background: visitorTheme.fallbackWash, padding: compact ? '12px 13px' : '10px 12px', outline: 'none', color: premiumTheme.colors.ink, fontSize: 16, lineHeight: 1.35, fontFamily: 'inherit', textAlign: isRTL ? 'right' : 'left' }} />
      <button onClick={() => runPlan(query)} disabled={!query.trim() || phase === 'loading'} aria-label={t('Plan', 'خطط')} style={{ width: compact ? 52 : 46, borderRadius: compact ? 16 : 12, border: 'none', background: query.trim() ? premiumTheme.colors.accent : visitorTheme.faintInk, color: visitorTheme.onAccent, cursor: query.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {phase === 'loading' ? <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 20, height: 20 }} />}
      </button>
    </div>

    {!hasConversation && <div style={{ display: compact ? 'flex' : 'grid', gridTemplateColumns: compact ? undefined : 'repeat(2, minmax(0, 1fr))', gap: compact ? 8 : 7, overflowX: compact ? 'auto' : 'visible', paddingTop: compact ? 10 : 9, paddingBottom: compact ? 2 : 0, WebkitOverflowScrolling: 'touch', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
      {visibleExamples.map(example => <button key={example} onClick={() => runPlan(example)} disabled={loading} style={chipStyle(compact)}>{example}</button>)}
    </div>}

    {phase === 'empty' && compact && <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, color: premiumTheme.colors.accent, fontSize: 13, fontWeight: 700, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
      <Sparkles style={{ width: 16, height: 16 }} />{askStatusLabel('empty', t)}
    </div>}
  </section>;
};

// The System | Light | Dark control (#423). Three WORDS, never an icon alone, and the selected
// segment is marked three ways — the accent fill, a heavier weight and a border — because the
// owner is colourblind and a tint on its own is not a signal (#421 D7). "System" is the absence
// of a stored choice rather than a third stored value, so picking it CLEARS `zgharta-theme`
// (#421 D2) and the app follows the device again.
const THEME_CHOICES = [
  { id: 'system', value: null, en: 'System', ar: 'تلقائي' },
  { id: 'light', value: 'light', en: 'Light', ar: 'فاتح' },
  { id: 'dark', value: 'dark', en: 'Dark', ar: 'داكن' },
];

const ThemeChoiceControl = ({ choice, onChoose, isRTL, t }) => <div role="group" data-testid="theme-choice" aria-label={t('Theme', 'المظهر')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
  <span style={smallCapsStyle(isRTL, { fontSize: 9 })}>{t('Theme', 'المظهر')}</span>
  <div style={{ display: 'inline-flex', gap: 3, padding: 3, borderRadius: 9999, background: premiumTheme.colors.surface, border: `1px solid ${premiumTheme.colors.warmLine}`, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
    {THEME_CHOICES.map(option => {
      const active = choice === option.value;
      return <button key={option.id} type="button" data-testid={`theme-choice-${option.id}`} aria-pressed={active} onClick={() => onChoose(option.value)} style={{ minHeight: 28, padding: '5px 9px', borderRadius: 9999, border: active ? `1px solid ${premiumTheme.colors.accent}` : '1px solid transparent', background: active ? premiumTheme.colors.accent : 'transparent', color: active ? visitorTheme.onAccent : premiumTheme.colors.mutedInk, cursor: 'pointer', fontSize: 11.5, fontWeight: active ? 800 : 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{t(option.en, option.ar)}</button>;
    })}
  </div>
</div>;

const GuideScreen = ({ places, businesses, events, lang, isRTL, t, setTab, setLang, askPanel, themeChoice, onChooseTheme, onOpenItem }) => {
    const guidePhotoItem = [...places, ...businesses].find(item => item.image && item.featured) || [...places, ...businesses].find(item => item.image);
    const guidePhoto = guidePhotoItem ? getPrimaryDisplayPhoto(guidePhotoItem) : null;
    const todayKey = beirutDayKey(new Date());
    const nextEvent = events.find(e => {
      // `endDate || date` — a festival that started yesterday and runs all week is still ahead.
      const eventKey = beirutDayKey(e.endDate || e.date);
      return eventKey != null && (todayKey == null || eventKey >= todayKey);
    });
    const eventDateLocale = lang === 'ar' ? 'ar-LB' : 'en-US';
    const evDateDay = nextEvent ? formatBeirutEventDate(nextEvent.date, eventDateLocale, { day: 'numeric' }) : null;
    const evDateMonth = nextEvent ? formatBeirutEventDate(nextEvent.date, eventDateLocale, { month: 'short' }) : null;
    const eventCatColor = nextEvent ? (eventCatStyles[nextEvent.category]?.color || premiumTheme.colors.accent) : premiumTheme.colors.accent;
    const todayContext = t('Ask for a route, a stop, or a simple day plan from the local guide.', 'اسأل عن مسار أو محطة أو خطة يوم بسيطة من الدليل المحلي.');

    return <div style={{ minHeight: '100vh', background: premiumTheme.colors.paper, paddingBottom: 96, direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? fontFor(isRTL, 'body') : cleanSans }}>
      <header style={{ position: 'relative', zIndex: 1, background: premiumTheme.colors.paper, borderBottom: `1px solid ${premiumTheme.colors.warmLine}` }}>
        <div style={{ padding: '24px 20px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <div style={{ textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 0', color: premiumTheme.colors.accent, marginBottom: 4 }}>
                <Compass style={{ width: 12, height: 12, flexShrink: 0 }} />
                <span style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent, fontSize: 10 })}>{t('Simple guide', 'دليل مبسط')}</span>
              </div>
              <h1 style={{ fontFamily: 'inherit', fontSize: 34, lineHeight: 1.1, fontWeight: 700, color: premiumTheme.colors.ink, letterSpacing: 0, marginBottom: 2, overflowWrap: 'anywhere' }}>{t('Your local guide', 'دليلك المحلي')}</h1>
              <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 14, lineHeight: 1.6, margin: '10px 0 0' }}>{todayContext}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: isRTL ? 'flex-start' : 'flex-end', flexShrink: 0 }}>
              <button onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')} style={{ minHeight: 44, padding: '6px 10px', background: premiumTheme.colors.surface, borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: premiumTheme.colors.ink }}>{lang === 'en' ? 'Arabic' : 'إنكليزي'}</button>

            </div>
          </div>
          {onChooseTheme && <div style={{ display: 'flex', marginTop: 10, justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
            <ThemeChoiceControl choice={themeChoice} onChoose={onChooseTheme} isRTL={isRTL} t={t} />
          </div>}
        </div>
      </header>

      {guidePhotoItem && <figure style={{ margin: '0 20px 28px', position: 'relative', overflow: 'hidden', borderRadius: 12 }}>
        <button type="button" data-testid="guide-featured-place" onClick={() => onOpenItem(guidePhotoItem)} aria-label={t(`Open ${displayName(guidePhotoItem, isRTL)}`, `افتح ${displayName(guidePhotoItem, isRTL)}`)} style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'inherit', fontFamily: 'inherit' }}>
          <PlaceImage src={displayPhotoThumbSrc(guidePhoto)} category={guidePhotoItem.category} name={displayName(guidePhotoItem, isRTL)} caption={guidePhoto.caption || displayPhotoCaption(guidePhotoItem, isRTL)} attribution={displayPhotoAttribution(guidePhotoItem, isRTL, guidePhoto)} isRTL={isRTL} style={{ width: '100%', height: 192 }} />
          <span style={{ display: 'block', padding: '10px 0', fontSize: 13, lineHeight: 1.5, color: premiumTheme.colors.mutedInk, borderBottom: `1px solid ${premiumTheme.colors.warmLine}` }}>{displayName(guidePhotoItem, isRTL)} · {displayVillage(guidePhotoItem, isRTL)} · {displayCazaName(guidePhotoItem, isRTL)} <ChevronRight aria-hidden="true" size={13} style={{ verticalAlign: 'middle', transform: isRTL ? 'rotate(180deg)' : 'none' }} /></span>
        </button>
      </figure>}

      {askPanel && <section data-testid="guide-ask-zgharta-primary" style={{ padding: '0 20px' }}>{askPanel}</section>}

      {nextEvent && evDateDay && evDateMonth && <section style={{ padding: '32px 20px 0' }}>
        <h2 style={{ fontFamily: 'inherit', fontSize: 21, fontWeight: 700, color: premiumTheme.colors.ink, marginBottom: 10, textAlign: isRTL ? 'right' : 'left' }}>{t('Coming Up', 'قادم قريبا')}</h2>
        <button type="button" onClick={() => setTab('events')} style={{ width: '100%', background: premiumTheme.colors.surface, borderRadius: 8, padding: '13px 15px', cursor: 'pointer', display: 'flex', gap: 13, alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row', border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none', color: 'inherit', fontFamily: 'inherit' }}>
          <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 48 }}>
            <div data-testid="guide-coming-up-day" style={{ fontFamily: 'inherit', fontSize: 32, fontWeight: 700, color: premiumTheme.colors.ink, lineHeight: 1 }}>{evDateDay}</div>
            <div data-testid="guide-coming-up-month" style={smallCapsStyle(isRTL, { fontSize: 10, marginTop: 2 })}>{evDateMonth}</div>
          </div>
          <div style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
            <p style={smallCapsStyle(isRTL, { color: eventCatColor, marginBottom: 5 })}>{getEditorialLabels({ category: nextEvent.category }, isRTL)[0]}</p>
            <h3 style={{ fontWeight: 700, color: premiumTheme.colors.ink, fontSize: 15, marginBottom: 4 }}>{displayEventName(nextEvent, isRTL)}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: premiumTheme.colors.mutedInk, fontSize: 12, flexWrap: 'wrap' }}>
              {nextEvent.time && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Clock style={{ width: 11, height: 11 }} />{displayEventTime(nextEvent.time, isRTL)}</span>}
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin style={{ width: 11, height: 11 }} />{displayEventLocation(nextEvent, isRTL)}</span>
            </div>
          </div>
          {isRTL ? <ChevronLeft style={{ width: 16, height: 16, color: visitorTheme.chevronInk, flexShrink: 0 }} /> : <ChevronRight style={{ width: 16, height: 16, color: visitorTheme.chevronInk, flexShrink: 0 }} />}
        </button>
      </section>}

      <section style={{ padding: '32px 20px 0' }}>
        <h2 style={{ fontFamily: 'inherit', fontSize: 21, fontWeight: 700, color: premiumTheme.colors.ink, marginBottom: 10, textAlign: isRTL ? 'right' : 'left' }}>{t('Getting There', 'كيف تصل')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { from: t('From Beirut via Kfar Hazir', 'من بيروت عبر كفر حزير'), time: t('Kfar Hazir approach before Zgharta/Ehden', 'عبر كفر حزير قبل زغرتا/إهدن'), link: 'https://www.google.com/maps/dir/Beirut,+Lebanon/34.310327,35.77188/Zgharta,+Lebanon/Ehden,+Lebanon/' },
            { from: t('From Tripoli (local access)', 'من طرابلس (وصول محلي)'), time: t('~30 min drive to Zgharta', '~30 دقيقة بالسيارة إلى زغرتا'), link: 'https://www.google.com/maps/dir/Tripoli,+Lebanon/Zgharta,+Lebanon/' },
            { from: t('Ehden from Zgharta', 'من زغرتا إلى إهدن'), time: t('~20 min drive (15 km)', '~20 دقيقة بالسيارة (15 كم)'), link: 'https://www.google.com/maps/dir/Zgharta,+Lebanon/Ehden,+Lebanon/' }
          ].map((r, i, arr) => <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" style={{ padding: '14px 0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, borderBottom: i < arr.length - 1 ? `1px solid ${premiumTheme.colors.warmLine}` : 'none', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: premiumTheme.colors.surface, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Navigation style={{ width: 16, height: 16, color: premiumTheme.colors.accent }} />
            </div>
            <div style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
              <p style={{ fontWeight: 500, color: premiumTheme.colors.ink, fontSize: 14 }}>{r.from}</p>
              <p style={{ fontSize: 12, color: premiumTheme.colors.accent, fontWeight: 500, marginTop: 1 }}>{r.time}</p>
            </div>
            <ExternalLink style={{ width: 13, height: 13, color: visitorTheme.chevronInk, flexShrink: 0 }} />
          </a>)}
        </div>
      </section>
    </div>;
};

function OpenHoursStack({ openHours, fallbackLabel, isRTL, compact = false }) {
  const rows = splitOpenHoursForDisplay(openHours, fallbackLabel);
  const dayFontSize = compact ? 11 : 12;
  const hoursFontSize = compact ? 12 : 14;
  const rowGap = compact ? 7 : 10;

  return <div data-testid="open-hours-stack" role="list" style={{ display: 'flex', flexDirection: 'column', gap: rowGap, minWidth: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'visible', boxSizing: 'border-box', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
    {rows.map((row, index) => (
      <div data-testid="open-hours-row" role="listitem" key={`${row.day || 'hours'}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: compact ? 0 : '1px 0', overflowX: 'hidden' }}>
        {row.day && <span style={{ display: 'block', minWidth: 0, maxWidth: '100%', color: premiumTheme.colors.accent, fontSize: dayFontSize, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0, lineHeight: 1.2, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{row.day}</span>}
        <span style={{ display: 'block', minWidth: 0, maxWidth: '100%', color: premiumTheme.colors.mutedInk, fontSize: hoursFontSize, lineHeight: compact ? 1.35 : 1.42, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{row.hours}</span>
      </div>
    ))}
  </div>;
}

const ExploreScreen = ({ places, businesses, lang, isRTL, t, catFilter, setCatFilter, villageFilter = [], setVillageFilter, zoneId, setZoneId, setSelPlace, setSelBiz }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showVillageDrop, setShowVillageDrop] = useState(false);

    const allItems = React.useMemo(
      () => getExploreCatalogItems({ places, businesses }),
      [places, businesses],
    );

    const filteredItems = React.useMemo(() => (
      filterExploreCatalogItems(allItems, {
        searchQuery,
        categoryIds: catFilter,
        villageIds: villageFilter,
        zoneId,
        minRating: 0,
      })
    ), [allItems, searchQuery, catFilter, villageFilter, zoneId]);
    const villageGroups = React.useMemo(
      () => getCatalogVillageGroups(allItems, { categoryIds: catFilter }),
      [allItems, catFilter]
    );

    const toggleExploreCat = (id) => {
      setCatFilter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const categoryById = React.useMemo(() => {
      const lookup = {};
      CATEGORIES.forEach(c => { lookup[c.id] = c; });
      return lookup;
    }, []);

    const searchPlaceholder = t('Search places', 'ابحث عن مكان');

    return <div data-testid="explore-directory-screen" style={{ minHeight: '100vh', background: premiumTheme.colors.paper, paddingBottom: 96, direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? fontFor(isRTL, 'body') : cleanSans, maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ padding: '24px 18px 16px' }}>
        <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.15, fontWeight: 750, letterSpacing: isRTL ? 0 : '-0.8px', color: premiumTheme.colors.ink }}>{t('Directory', 'دليل الأماكن')}</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: premiumTheme.colors.mutedInk }}>{t('Find your next stop.', 'اعثر على محطتك التالية.')}</p>
      </div>
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: premiumTheme.colors.paper, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none' }}>
        <div style={{ padding: '12px 16px 10px', display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
          <label data-testid="directory-search-only" style={{ flex: 1, minHeight: 44, borderRadius: 12, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, display: 'flex', alignItems: 'center', gap: 9, padding: isRTL ? '0 12px 0 8px' : '0 8px 0 12px', boxShadow: 'none' }}>
            <Search style={{ width: 16, height: 16, color: premiumTheme.colors.accent, flexShrink: 0 }} />
            <input
              type="search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={t('Search directory', 'ابحث في الدليل')}
              style={{ width: '100%', minWidth: 0, height: 42, border: 'none', outline: 'none', background: 'transparent', color: premiumTheme.colors.ink, fontFamily: 'inherit', fontSize: 16, fontWeight: 500, textAlign: isRTL ? 'right' : 'left' }}
            />
            {searchQuery && <button type="button" aria-label={t('Clear search', 'مسح البحث')} onClick={() => setSearchQuery('')} style={{ width: 44, height: 44, borderRadius: 9999, border: 'none', background: visitorTheme.warm10, color: premiumTheme.colors.mutedInk, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X style={{ width: 14, height: 14 }} /></button>}
          </label>
          <button type="button" data-testid="directory-village-filter-button" aria-controls="directory-village-panel" aria-label={t('Filter directory by village', 'فلترة الدليل حسب القرية')} aria-expanded={showVillageDrop} onClick={() => setShowVillageDrop(open => !open)} style={{ minHeight: 44, padding: '0 10px', borderRadius: 12, border: `1px solid ${villageFilter.length > 0 ? premiumTheme.colors.accent : premiumTheme.colors.warmLine}`, background: villageFilter.length > 0 ? premiumTheme.colors.accentSoft : premiumTheme.colors.surface, color: villageFilter.length > 0 ? premiumTheme.colors.accent : visitorTheme.controlInk, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0, fontSize: 13, fontWeight: 600 }}>
            <MapPin style={{ width: 13, height: 13 }} />
            {villageFilter.length === 0 ? t('Village', 'قرية') : villageFilter.length === 1 ? displayVillageFilterLabel(villageFilter[0], allItems, isRTL) : villageFilter.length}
            <ChevronDown style={{ width: 12, height: 12 }} />
          </button>
          {showVillageDrop && <div id="directory-village-panel" role="dialog" aria-label={t('Villages', 'القرى')} onKeyDown={event => { if (event.key === 'Escape') { setShowVillageDrop(false); event.currentTarget.parentElement.querySelector('[data-testid="directory-village-filter-button"]')?.focus(); } }} style={{ position: 'absolute', top: 58, [isRTL ? 'left' : 'right']: 12, zIndex: 30 }}>
            <VillageFilterPanel
              groups={villageGroups}
              villageFilter={villageFilter}
              items={allItems}
              isRTL={isRTL}
              t={t}
              onToggle={id => setVillageFilter(prev => prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id])}
              onClear={() => setVillageFilter([])}
            >
              <details data-directory-area-options style={{ borderTop: `1px solid ${visitorTheme.hairline}`, marginTop: 6, padding: '0 10px', color: visitorTheme.ink }}>
                <summary style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, listStyle: 'none', cursor: 'pointer', fontSize: 12, color: visitorTheme.controlInk }}>
                  <span style={{ flex: 1 }}>{t('Area options', 'خيارات المنطقة')}</span>
                  {zoneId && <span>{t('1 filter active', 'فلتر نشط')}</span>}
                  <ChevronDown size={14} aria-hidden="true" />
                </summary>
                <div style={{ padding: '6px 0 12px' }}><AreaFilter surface="directory" value={zoneId} onChange={setZoneId} isRTL={isRTL} /></div>
                <AreaSource isRTL={isRTL} compact />
              </details>
            </VillageFilterPanel>
          </div>}
        </div>
        {showVillageDrop && <div data-testid="directory-village-filter-dismiss" onClick={() => setShowVillageDrop(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />}
        <div style={{ padding: '0 16px 12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          <div style={{ display: 'flex', gap: 7 }}>
            {CATEGORIES.map(c => {
              const active = catFilter.includes(c.id);
              const CIcon = c.icon;
              const pillInk = categoryInkVars(c.id);
              return <button
                key={c.id}
                type="button"
                data-testid="directory-category-pill"
                data-category={c.id}
                aria-label={t(`Toggle ${c.labelEn} directory category`, `بدّل فئة ${c.labelAr} في الدليل`)}
                aria-pressed={active}
                onClick={() => toggleExploreCat(c.id)}
                style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 10, border: active ? `1.5px solid ${c.color}` : `1px solid ${visitorTheme.warm14}`, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, background: active ? c.color : premiumTheme.colors.surface, color: active ? visitorTheme.categoryOnFill : visitorTheme.controlInk, fontSize: 12, fontWeight: 500, boxShadow: 'none', transition: 'all 0.15s ease' }}
              >
                <span style={{ width: 19, height: 19, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? visitorTheme.glass18 : pillInk.chipBg, color: active ? visitorTheme.categoryOnFill : pillInk.ink, flexShrink: 0 }}><CIcon style={{ width: 12, height: 12 }} /></span>
                <span>{t(c.labelEn, c.labelAr)}</span>
              </button>;
            })}
          </div>
        </div>
      </div>

      {(catFilter.length > 0 || villageFilter.length > 0 || zoneId) && <div data-testid="directory-active-filters" style={{ padding: '10px 14px 0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {zoneId && <button type="button" data-testid="directory-active-area-filter" aria-label={t('Remove area filter', 'إزالة فلتر المنطقة')} onClick={() => setZoneId('')} style={{ minHeight: 44, fontSize: 12, padding: '6px 10px', borderRadius: 10, background: premiumTheme.colors.accentSoft, color: premiumTheme.colors.accent, border: 'none', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>{t('Area filter active', 'فلتر المنطقة نشط')}<X size={13} /></button>}
        {catFilter.map(c => {
          const category = categoryById[c];
          const chipInk = categoryInkVars(c);
          return <button
            key={c}
            type="button"
            data-testid="directory-active-filter"
            data-category={c}
            aria-label={t(`Remove ${category ? category.labelEn : c} filter`, `إزالة فلتر ${category ? category.labelAr : c}`)}
            onClick={() => setCatFilter(prev => prev.filter(x => x !== c))}
            style={{ minHeight: 44, fontSize: 11, padding: '6px 9px', borderRadius: 9999, background: chipInk.chipBg, color: chipInk.ink, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${chipInk.chipLine}` }}
          >{category ? t(category.labelEn, category.labelAr) : displayCategoryLabel(c, isRTL)} <X style={{ width: 11, height: 11 }} /></button>;
        })}
        {villageFilter.map(villageId => {
          const label = displayVillageFilterLabel(villageId, allItems, isRTL);
          return <button
            key={villageId}
            type="button"
            data-testid="directory-active-village-filter"
            data-village-id={villageId}
            aria-label={t(`Remove ${displayVillageFilterLabel(villageId, allItems, false)} village filter`, `إزالة فلتر قرية ${displayVillageFilterLabel(villageId, allItems, true)}`)}
            onClick={() => setVillageFilter(prev => prev.filter(value => value !== villageId))}
            style={{ minHeight: 44, fontSize: 11, padding: '6px 9px', borderRadius: 9999, background: premiumTheme.colors.accentSoft, color: premiumTheme.colors.accent, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${visitorTheme.accentHairline}` }}
          >{label} <X style={{ width: 11, height: 11 }} /></button>;
        })}
      </div>}

      <div style={{ padding: '18px 16px' }}>
        <div role="status" style={{ fontSize: 13, color: premiumTheme.colors.mutedInk, marginBottom: 12 }}>{t(`${filteredItems.length} places to explore`, `${filteredItems.length} أماكن للاستكشاف`)}</div>
        <div data-testid="directory-list" style={{ display: 'flex', flexDirection: 'column', background: 'transparent', overflow: 'hidden' }}>
          {filteredItems.map((item, itemIndex) => {
            const category = categoryById[item.category];
            const itemName = displayName(item, isRTL);
            const itemVillage = displayVillage(item, isRTL);
            const rowInk = categoryInkVars(item.category);
            const categoryColor = rowInk.ink;
                        const CategoryIcon = catIcons[item.category] || MapPin;
            const photoAttribution = displayPhotoAttribution(item, isRTL);
            return <button
              key={`${item.type}-${item.id}`}
              data-testid="directory-list-row"
              type="button"
              onClick={() => item.type === 'place' ? setSelPlace(item) : setSelBiz(item)}
              style={{ width: '100%', minHeight: 110, padding: '14px 0', border: 'none', borderBottom: itemIndex < filteredItems.length - 1 ? `1px solid ${premiumTheme.colors.warmLine}` : 'none', background: 'transparent', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', fontFamily: 'inherit', display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr)', gap: 14, alignItems: 'center' }}
            >
              <div style={{ position: 'relative', width: 84, height: 88, borderRadius: 12, overflow: 'hidden', border: `1px solid ${premiumTheme.colors.warmLine}` }}>
                <PlaceImage src={item.image} category={item.category} name={itemName} caption={displayPhotoCaption(item, isRTL)} attribution={photoAttribution} compact isRTL={isRTL} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
              </div>
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap', justifyContent: 'flex-start', minWidth: 0 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', padding: 0, background: 'transparent', color: categoryColor, fontSize: 12, lineHeight: 1.35, fontWeight: 600, letterSpacing: 0, flexShrink: 0 }}>
                    <CategoryIcon style={{ width: 10, height: 10, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{category ? t(category.labelEn, category.labelAr) : displayCategoryLabel(item.category, isRTL)}</span>
                  </span>
                </div>
                <h3 style={{ fontFamily: 'inherit', fontSize: 17, lineHeight: 1.3, fontWeight: 650, letterSpacing: 0, color: premiumTheme.colors.ink, margin: 0, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{itemName}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: premiumTheme.colors.mutedInk, fontSize: 13, fontWeight: 400, minWidth: 0, flexWrap: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}><MapPin style={{ width: 11, height: 11, color: categoryColor, flexShrink: 0 }} /><span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemVillage}</span></span>
                </div>
                <AreaSummary item={item} isRTL={isRTL} />
              </div>
            </button>;
          })}
	          {filteredItems.length === 0 && <div style={{ textAlign: 'center', padding: '46px 22px', background: premiumTheme.colors.surface, borderRadius: 8, border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none' }}>
            <div style={{ width: 74, height: 74, borderRadius: 8, background: premiumTheme.colors.paper, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Compass style={{ width: 34, height: 34, color: premiumTheme.colors.accent }} />
            </div>
            <p style={{ color: premiumTheme.colors.ink, fontSize: 18, fontWeight: 700, marginBottom: 6, fontFamily: 'inherit' }}>{t('No directory listings match', 'لا توجد نتائج مطابقة في دليل الأماكن')}</p>
            <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 13, lineHeight: 1.55, maxWidth: 270, margin: '0 auto' }}>{t('Try another search term to return to matching places.', 'جرّب عبارة بحث أخرى للعودة إلى الأماكن المطابقة.')}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 18 }}>
              {searchQuery && <button onClick={() => setSearchQuery('')} style={{ padding: '10px 16px', background: premiumTheme.colors.accent, color: visitorTheme.onAccent, border: 'none', borderRadius: 9999, cursor: 'pointer', fontSize: 13, fontWeight: 500, boxShadow: 'none' }}>{t('Clear search', 'مسح البحث')}</button>}
              {zoneId && <button type="button" onClick={() => setZoneId('')} style={{ padding: '10px 16px', background: premiumTheme.colors.surface, color: premiumTheme.colors.accent, border: `1px solid ${premiumTheme.colors.warmLine}`, borderRadius: 8, cursor: 'pointer' }}>{t('Clear area filter', 'مسح فلتر المنطقة')}</button>}
              {villageFilter.length > 0 && <button onClick={() => setVillageFilter([])} style={{ padding: '10px 16px', background: premiumTheme.colors.surface, color: premiumTheme.colors.accent, border: `1px solid ${premiumTheme.colors.warmLine}`, borderRadius: 9999, cursor: 'pointer', fontSize: 13, fontWeight: 500, boxShadow: 'none' }}>{t('Clear village filters', 'مسح فلاتر القرى')}</button>}
            </div>
          </div>}
        </div>
      </div>
    </div>;
};

// The Events tab is a WEEK BOARD (#450): a 7-day strip of counts across the top, and below it the
// same upcoming events as a diary — the date sits once in the margin, the events of that day hang
// off it under a thin spine. It reads composed with three events and no photographs, because the
// margin and the board carry the structure rather than pictures.
//
// The date rules live in src/events/eventBoard.js, keyed off `beirutDayKey` — the same helper the
// Guide's "Coming up" card and the catalog filters use, so the two surfaces can never disagree
// about which day "today" is. `filterEvents` is untouched and still owns the category filter; the
// board is additive.

// Localised weekday / day / month parts for one day key. Event dates are Beirut calendar values,
// so every read goes through `formatBeirutEventDate` rather than the browser's own timezone.
const eventDayParts = (dayKey, dateLocale) => ({
  weekday: formatBeirutEventDate(dayKey, dateLocale, { weekday: 'short' }),
  day: formatBeirutEventDate(dayKey, dateLocale, { day: 'numeric' }),
  month: formatBeirutEventDate(dayKey, dateLocale, { month: 'short' }),
  year: formatBeirutEventDate(dayKey, dateLocale, { year: 'numeric' }),
  full: formatBeirutEventDate(dayKey, dateLocale, { weekday: 'long', day: 'numeric', month: 'long' }),
});

// A card action (Calendar / Directions). It sits INSIDE a card that is itself a button, so every
// one of them stops the click from also opening the detail view.
const EventCardAction = ({ href, icon: Icon, label, font }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={event => event.stopPropagation()}
    style={{ ...font, flex: 1, minWidth: 0, minHeight: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent', color: visitorTheme.accent, fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '0 6px' }}
  >
    <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
  </a>
);

const EventCard = ({ event: e, isRTL, t, lang, dateLocale, isNext, isPast, onOpen, categoryMeta, font }) => {
  const s = eventCatStyles[e.category] || eventCatStyles.cultural;
  const meta = categoryMeta[e.category] || { label: displayCategoryLabel(e.category, isRTL), icon: Calendar, ...s };
  const CategoryIcon = meta.icon;
  const name = displayEventName(e, isRTL);
  const location = displayEventLocation(e, isRTL);
  // A row whose locality is under boundary review carries the placeholder AS its village
  // (`PUBLIC_LOCALITY_UNDER_REVIEW_LABELS`), so appending it would read "Ehden Country Club ·
  // Location to confirm". The venue alone is the honest line there.
  const rawVillage = displayVillage(e, isRTL);
  const village = rawVillage === PUBLIC_LOCALITY_UNDER_REVIEW_LABELS[isRTL ? 'ar' : 'en'] ? '' : rawVillage;
  const nights = eventNights(e);
  const runNights = nights.isRun ? nights.total : 0;
  const filledNight = nights.isRun ? Math.max(0, nights.dayKeys.indexOf(firstRemainingNight(e, beirutDayKey(new Date()) ?? ''))) : 0;
  const timeText = displayEventTime(e.time, isRTL);
  // `item.image` is the 400px derivative; the 1200px master is only ever the detail hero
  // (CLAUDE.md, "photo size per surface"). A 56px thumbnail must never reach for the master.
  const thumbSrc = typeof e.image === 'string' ? e.image.trim() : '';
  const calRange = googleCalendarAllDayRange(e.date, e.endDate);
  const calUrl = calRange
    ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(name)}&dates=${calRange.start}/${calRange.end}&location=${encodeURIComponent(location || '')}`
    : null;
  const directionsUrl = eventDirectionsUrl(e, { cazaLabel: catalogCazaLabelOrNeutral(normalizeCatalogCaza(e.caza), lang) });
  // Past the bar cap the run is described in DAYS, not nights, and the chip agrees with the
  // caption below it — "12 nights" over "12 days" on one card is two answers to one question.
  const nightsLabel = nights.capped
    ? t(`${runNights} days`, `${runNights} أيام`)
    : t(`${runNights} nights`, `${runNights} ليالٍ`);
  // A run in another year, or one crossing New Year, states its year — otherwise "Wed 1 – Sat 4
  // Jul" on a 2099 festival is a date a visitor cannot place, and a December-to-January run reads
  // as if it ends before it starts.
  const runYears = eventRangeYearParts(e.date, e.endDate, dateLocale);
  const runFrom = eventShortDayLabel(e.date, dateLocale, { weekday: !nights.capped, month: nights.capped, year: runYears.start });
  const runTo = eventShortDayLabel(e.endDate, dateLocale, { weekday: !nights.capped, month: true, year: runYears.end });
  const runCaption = nights.isRun
    ? (nights.capped
      // Past the bar cap the run states its true length in days instead of drawing segments that
      // would have to lie about how many nights there are.
      ? t(`Runs ${runFrom} – ${runTo} · ${runNights} days`, `من ${runFrom} إلى ${runTo} · ${runNights} أيام`)
      : t(`Runs ${runFrom} – ${runTo} · same time each night`, `من ${runFrom} إلى ${runTo} · الوقت نفسه كل ليلة`))
    : null;
  const openDetail = () => onOpen(e);

  // The card carries THREE actions — open the detail, add to calendar, get directions — so the
  // article itself is not the button (cold review). A `role="button"` announces its descendants as
  // non-semantic content, which loses the two links for a screen-reader user, and a keydown from a
  // focused link bubbled to the card's own handler besides. The TITLE is the real, focusable
  // control that opens the detail; the article keeps a plain `onClick` so a pointer user can still
  // tap anywhere on it, which is what the design asks for.
  return <article
    data-testid="event-card"
    data-event-id={String(e.id)}
    data-event-next={isNext ? 'true' : undefined}
    onClick={openDetail}
    style={{ ...font, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', background: premiumTheme.colors.surface, border: isNext ? `1.5px solid ${visitorTheme.accent}` : `1px solid ${visitorTheme.warm12}`, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', opacity: 1, filter: isPast ? 'saturate(0.7)' : 'none' }}
  >
    <div style={{ display: 'flex', gap: 10, padding: '11px 12px 9px', alignItems: 'flex-start', minWidth: 0 }}>
      <div style={{ width: 52, minWidth: 52, textAlign: 'center', paddingTop: 2 }}>
        <div data-testid="event-card-time" style={{ ...font, fontSize: timeText ? 15 : 12, fontWeight: 700, color: isPast ? visitorTheme.pastInk : visitorTheme.ink, lineHeight: timeText ? 1 : 1.1, direction: 'ltr', unicodeBidi: 'embed' }}>{timeText || t('All day', 'طوال اليوم')}</div>
        {runNights > 1 && <div style={smallCapsStyle(isRTL, { fontSize: 9.5, fontWeight: 600, color: visitorTheme.warmMeta, marginTop: 3 })}>{nightsLabel}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: 0 }}>
          <button
            type="button"
            data-testid="event-card-open"
            onClick={event => { event.stopPropagation(); openDetail(); }}
            style={{ ...font, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', width: '100%', padding: 0, margin: 0, border: 'none', background: 'transparent', textAlign: isRTL ? 'right' : 'left', fontSize: 15.5, fontWeight: 700, lineHeight: 1.2, color: isPast ? visitorTheme.pastInk : visitorTheme.ink, cursor: 'pointer' }}
          >{name}</button>
        </h3>
        <div style={{ marginTop: 4, fontSize: 12, color: isPast ? visitorTheme.pastMeta : visitorTheme.meta, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <MapPin style={{ width: 13, height: 13, flexShrink: 0, color: isPast ? visitorTheme.pastMeta : visitorTheme.accent }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{village && village !== location ? `${location} · ${village}` : location}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
          {isNext && <span data-testid="event-card-next-up" style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 9999, background: visitorTheme.accent, color: visitorTheme.onAccent }}>{t('Next up', 'التالي')}</span>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, padding: '3px 7px', borderRadius: 9999, background: s.bg, color: s.color }}>
            <CategoryIcon style={{ width: 11, height: 11, flexShrink: 0 }} />
            {meta.label}
          </span>
          {runNights > 1 && <span data-testid="event-card-nights" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 9999, background: visitorTheme.accentSoft, color: visitorTheme.accent, border: `1px dashed ${visitorTheme.accent24}` }}>
            <Calendar style={{ width: 10, height: 10, flexShrink: 0 }} />{nightsLabel}
          </span>}
        </div>
      </div>
      {thumbSrc && <div data-testid="event-card-thumb" style={{ flexShrink: 0 }}>
        <PlaceImage src={thumbSrc} category={e.category} name={name} compact isRTL={isRTL} style={{ width: 56, height: 56, borderRadius: 10 }} />
      </div>}
    </div>
    {nights.isRun && <div style={{ padding: '0 12px 10px' }}>
      {!nights.capped && <div data-testid="event-card-run-bar" style={{ display: 'flex', gap: 4 }}>
        {nights.dayKeys.map((key, index) => <i key={key} style={{ flex: 1, height: 5, borderRadius: 3, background: index === filledNight ? visitorTheme.accent : visitorTheme.eventRunTrack }} />)}
      </div>}
      <div data-testid="event-card-run-caption" style={{ ...font, fontSize: 10.5, color: visitorTheme.warmMeta, marginTop: nights.capped ? 0 : 5 }}>{runCaption}</div>
    </div>}
    <div style={{ display: 'flex', borderTop: `1px solid ${visitorTheme.warm12}` }}>
      {calUrl && <EventCardAction href={calUrl} icon={CalendarPlus} label={t('Calendar', 'التقويم')} font={font} />}
      <div style={{ width: calUrl ? 1 : 0, background: visitorTheme.warm12 }} />
      <EventCardAction href={directionsUrl} icon={Navigation} label={t('Directions', 'الاتجاهات')} font={font} />
    </div>
  </article>;
};

// One day of the diary: the date in the 52px margin, the cards hanging off a 2px spine.
const EventDayGroup = ({ dayKey, events, label, isRTL, t, lang, dateLocale, nowKey, nextEventId, onOpen, categoryMeta, font, past }) => {
  const parts = dayKey ? eventDayParts(dayKey, dateLocale) : null;
  const isToday = Boolean(dayKey) && dayKey === nowKey;
  const isTomorrow = Boolean(dayKey) && dayKey === addDays(nowKey, 1);
  const currentYear = formatBeirutEventDate(new Date(), dateLocale, { year: 'numeric' });
  // The year only appears when it is not this one — a "2099" on every card is noise, and its
  // absence on a distant event is a real loss.
  const showYear = Boolean(parts?.year) && parts.year !== currentYear;
  const tag = isToday ? t('Today', 'اليوم') : (isTomorrow ? t('Tomorrow', 'غداً') : null);
  // `minmax(0, 1fr)`, not `1fr`: a grid track's automatic minimum is its content's min-content
  // width, so a card holding a nowrap venue line grows the track past the viewport and scrolls
  // the whole page sideways at 390px. Every flex child down that column carries `minWidth: 0`
  // for the same reason.
  return <section aria-label={label || parts?.full || undefined} style={{ display: 'grid', gridTemplateColumns: '52px minmax(0, 1fr)', columnGap: 10, minWidth: 0 }}>
    <div style={{ alignSelf: 'start', textAlign: 'center', paddingTop: 6 }}>
      <div style={smallCapsStyle(isRTL, { fontSize: 10, fontWeight: 700, color: isToday ? visitorTheme.accent : visitorTheme.pastInk })}>{parts?.weekday || t('Soon', 'قريباً')}</div>
      <div data-testid="event-card-day" style={{ ...font, fontSize: 26, fontWeight: 800, lineHeight: 1, marginTop: 1, color: past ? visitorTheme.pastInk : visitorTheme.ink, ...(isToday ? { background: visitorTheme.eventTodayFill, color: visitorTheme.eventTodayInk, borderRadius: 10, width: 40, margin: '1px auto 0', padding: '5px 0 3px' } : {}) }}>{parts?.day || '•'}</div>
      <div data-testid="event-card-month" style={{ ...font, fontSize: 10, fontWeight: 600, color: visitorTheme.pastInk, marginTop: 2 }}>{parts?.month || t('Soon', 'قريباً')}</div>
      {showYear && <div data-testid="event-card-year" style={{ ...font, fontSize: 9, fontWeight: 600, color: visitorTheme.pastInk, marginTop: 1 }}>{parts.year}</div>}
      {tag && <div style={smallCapsStyle(isRTL, { display: 'block', fontSize: 8, fontWeight: 800, color: visitorTheme.accent, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' })}>{tag}</div>}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', minWidth: 0 }}>
      <div aria-hidden="true" style={{ position: 'absolute', top: 10, bottom: 10, insetInlineStart: -6, width: 2, borderRadius: 2, background: visitorTheme.eventDaySpine }} />
      {events.map(e => <EventCard
        key={e.id}
        event={e}
        isRTL={isRTL}
        t={t}
        lang={lang}
        dateLocale={dateLocale}
        isNext={e.id === nextEventId}
        isPast={Boolean(past)}
        onOpen={onOpen}
        categoryMeta={categoryMeta}
        font={font}
      />)}
    </div>
  </section>;
};

const EventsScreen = ({ events, lang, isRTL, t, setSelEvent, setTab, onShareWeek, screenContainer }) => {
    const [evFilter, setEvFilter] = useState('all');
    // The board IS the date filter — the old Upcoming/Past/All and today/weekend/month segmented
    // controls are gone, and `Past` is the disclosure at the bottom of the screen.
    const [pickedDay, setPickedDay] = useState(null);
    const [pastOpen, setPastOpen] = useState(false);
    const now = new Date();
    const dateLocale = lang === 'ar' ? 'ar-LB' : 'en-US';
    const eventsFont = fontFor(isRTL, 'body');
    const inheritFont = { fontFamily: eventsFont, letterSpacing: 0, fontStyle: 'normal' };
    const catStyle = c => eventCatStyles[c] || eventCatStyles.cultural;
    const categoryMeta = {
      all: { label: t('All', 'الكل'), ariaLabel: t('Show all event categories', 'عرض كل فئات الفعاليات'), icon: ListChecks, bg: visitorTheme.accent10, color: visitorTheme.accent },
      festival: { label: t('Festival', 'مهرجان'), ariaLabel: t('Filter events by festivals', 'فلترة الفعاليات حسب المهرجانات'), icon: Calendar, ...catStyle('festival') },
      religious: { label: t('Religious', 'ديني'), ariaLabel: t('Filter events by religious events', 'فلترة الفعاليات حسب المناسبات الدينية'), icon: StickCross, ...catStyle('religious') },
      cultural: { label: t('Cultural', 'ثقافي'), ariaLabel: t('Filter events by cultural events', 'فلترة الفعاليات حسب الفعاليات الثقافية'), icon: Landmark, ...catStyle('cultural') },
      nature: { label: t('Nature', 'طبيعة'), ariaLabel: t('Filter events by nature events', 'فلترة الفعاليات حسب فعاليات الطبيعة'), icon: TreePine, ...catStyle('nature') },
    };
    // `filterEvents` still owns the category filter, unchanged — the Guide card and the events SEO
    // route read it with the old date filters, so it stays intact and the board is additive.
    const categoryEvents = React.useMemo(
      () => filterEvents(events, { categoryId: evFilter, timeFilter: 'all', now }),
      [events, evFilter, now]
    );
    const model = React.useMemo(() => bucketUpcomingEvents(categoryEvents, now), [categoryEvents, now]);
    const board = React.useMemo(() => weekBoardDays(categoryEvents, now), [categoryEvents, now]);
    const boardDayKeys = new Set(board.map(day => day.dayKey));
    const weekEventCount = model.upcoming.filter(event => (
      eventRunDayKeys(event).some(dayKey => boardDayKeys.has(dayKey))
    )).length;
    const unfilteredUpcoming = React.useMemo(
      () => filterEvents(events, { timeFilter: 'upcoming', now }).length,
      [events, now]
    );
    const split = weekBoardSplit(board);
    const boardHasEvents = board.some(day => day.count > 0);
    const categoryOptions = ['all', 'festival', 'religious', 'cultural', 'nature'].map(id => ({
      id,
      ...categoryMeta[id],
      count: filterEvents(events, { categoryId: id, timeFilter: 'upcoming', now }).length,
    }));
    const bucketTitles = {
      today: t('Today', 'اليوم'),
      tomorrow: t('Tomorrow', 'غداً'),
      weekend: t('This weekend', 'نهاية الأسبوع'),
      next: t('Next week', 'الأسبوع القادم'),
      later: t('Later', 'لاحقاً'),
    };
    const bucketRange = bucket => {
      const days = bucket.days;
      const first = days[0]?.dayKey;
      const last = days[days.length - 1]?.dayKey;
      if (!first) return '';
      if (bucket.key === 'weekend' && last && last !== first) {
        return `${formatBeirutEventDate(first, dateLocale, { day: 'numeric' })}–${formatBeirutEventDate(last, dateLocale, { day: 'numeric', month: 'short' })}`;
      }
      if (bucket.key === 'next' || bucket.key === 'later') return formatBeirutEventDate(first, dateLocale, { month: 'long' });
      return formatBeirutEventDate(first, dateLocale, { day: 'numeric', month: 'short' });
    };
    const todayLine = formatBeirutEventDate(now, dateLocale, { weekday: 'long', day: 'numeric', month: 'long' });
    const pickCategory = id => { setEvFilter(id); setPickedDay(null); };
    const openEvent = e => setSelEvent(e);
    const dayGroupProps = {
      isRTL, t, lang, dateLocale, nowKey: model.nowKey, onOpen: openEvent, categoryMeta, font: inheritFont,
    };
    // The board counts a run on EVERY night it runs, so picking Saturday has to reach a
    // Friday-to-Sunday festival whose card is filed under Friday. Selecting the day group instead
    // would have shown the visitor a smaller list than the number on the button they just tapped.
    const pickedDayEvents = pickedDay
      ? model.upcoming.filter(e => eventRunDayKeys(e).includes(pickedDay))
      : [];
    const shareSummary = t(
      `${weekEventCount} events in the next seven days in Zgharta and Bsharri`,
      `${weekEventCount} فعالية في الأيام السبعة القادمة في زغرتا وبشري`
    );

    const quietCard = (variant) => <div data-testid="events-quiet-card" style={{ ...inheritFont, border: `1px solid ${visitorTheme.warm14}`, background: premiumTheme.colors.surface, borderRadius: 12, padding: '28px 20px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, margin: '0 auto 12px', borderRadius: 14, background: premiumTheme.colors.paper, border: `1px solid ${visitorTheme.accent14}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: visitorTheme.accent }}>
        <Calendar style={{ width: 30, height: 30 }} />
      </div>
      <h3 style={{ ...inheritFont, fontSize: 18, fontWeight: 700, color: visitorTheme.ink, margin: 0 }}>
        {variant === 'filtered' ? t('Nothing in this category', 'لا شيء في هذه الفئة') : t('A quiet week', 'أسبوع هادئ')}
      </h3>
      <p style={{ ...inheritFont, fontSize: 14, color: visitorTheme.warmMeta, lineHeight: 1.6, margin: '6px 0 0' }}>
        {variant === 'filtered'
          ? t('Nothing is listed in this category for the next seven days.', 'لا شيء مدرج في هذه الفئة للأيام السبعة القادمة.')
          : t('Nothing is listed for the next seven days. Festivals here cluster in July to September.', 'لا شيء مدرج للأيام السبعة القادمة. المهرجانات هنا تتركز بين تموز وأيلول.')}
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {variant === 'filtered'
          ? <button type="button" data-testid="events-show-all" onClick={() => pickCategory('all')} style={{ ...inheritFont, flex: 1, minHeight: 44, borderRadius: 12, border: '1px solid transparent', background: visitorTheme.accent, color: visitorTheme.onAccent, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{t('Show all', 'عرض الكل')}</button>
          : <button type="button" data-testid="events-open-guide" onClick={() => setTab('guide')} style={{ ...inheritFont, flex: 1, minHeight: 44, borderRadius: 12, border: '1px solid transparent', background: visitorTheme.accent, color: visitorTheme.onAccent, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Compass style={{ width: 16, height: 16 }} />{t('Open the Guide', 'افتح الدليل')}</button>}
        {model.past.length > 0 && <button type="button" data-testid="events-see-past" onClick={() => setPastOpen(true)} style={{ ...inheritFont, flex: 1, minHeight: 44, borderRadius: 12, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: visitorTheme.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('See past events', 'الفعاليات السابقة')}</button>}
      </div>
    </div>;

    const renderBuckets = buckets => buckets.map(bucket => <div key={bucket.key} data-testid={`events-bucket-${bucket.key}`} style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px', gap: 8 }}>
        <h2 style={{ ...inheritFont, fontSize: 19, fontWeight: 700, color: visitorTheme.ink, margin: 0 }}>{bucketTitles[bucket.key]}</h2>
        <span style={{ ...inheritFont, fontSize: 11, color: visitorTheme.warmMeta, fontWeight: 500, whiteSpace: 'nowrap' }}>{bucketRange(bucket)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bucket.days.map(day => <EventDayGroup key={day.dayKey} dayKey={day.dayKey} events={day.events} nextEventId={model.nextEventId} {...dayGroupProps} />)}
      </div>
    </div>);

    let body;
    if (pickedDay && pickedDayEvents.length) {
      const dayEvents = pickedDayEvents;
      body = <div data-testid="events-picked-day" style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px', gap: 8 }}>
          <h2 style={{ ...inheritFont, fontSize: 19, fontWeight: 700, color: visitorTheme.ink, margin: 0 }}>{formatBeirutEventDate(pickedDay, dateLocale, { weekday: 'long' })}</h2>
          <button type="button" data-testid="events-clear-day" onClick={() => setPickedDay(null)} style={{ ...inheritFont, minHeight: 44, padding: '6px 10px', borderRadius: 9999, border: `1px solid ${visitorTheme.warm14}`, background: premiumTheme.colors.surface, color: visitorTheme.accent, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{t('Show the whole week', 'عرض الأسبوع كله')}</button>
        </div>
        <EventDayGroup dayKey={pickedDay} events={dayEvents} nextEventId={model.nextEventId} {...dayGroupProps} />
      </div>;
    } else if (!boardHasEvents) {
      body = <>
        {quietCard(evFilter !== 'all' && unfilteredUpcoming > 0 ? 'filtered' : 'quiet')}
        {model.buckets.length > 0 && <div data-testid="events-looking-ahead" style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px', gap: 8 }}>
            <h2 style={{ ...inheritFont, fontSize: 19, fontWeight: 700, color: visitorTheme.ink, margin: 0 }}>{t('Looking further ahead', 'أبعد في الأفق')}</h2>
            <span style={{ ...inheritFont, fontSize: 11, color: visitorTheme.warmMeta, fontWeight: 500, whiteSpace: 'nowrap' }}>{bucketRange(model.buckets[0])}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {model.buckets.flatMap(bucket => bucket.days).map(day => <EventDayGroup key={day.dayKey} dayKey={day.dayKey} events={day.events} nextEventId={model.nextEventId} {...dayGroupProps} />)}
          </div>
        </div>}
      </>;
    } else {
      body = <>
        {renderBuckets(model.buckets)}
        {model.upcomingCount <= 4 && <p data-testid="events-sparse-note" style={{ ...inheritFont, margin: '10px 0 0', fontSize: 12, color: visitorTheme.warmMeta, textAlign: 'center', lineHeight: 1.5 }}>
          {t('Nothing more listed yet. The calendar refreshes every Monday from three listing sources.', 'لا شيء آخر مدرج بعد. تُحدَّث الرزنامة كل اثنين من ثلاثة مصادر.')}
        </p>}
      </>;
    }

    return <div style={{ ...screenContainer, background: premiumTheme.colors.paper, fontFamily: eventsFont, maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: premiumTheme.colors.paper, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, padding: '24px 20px 0', fontFamily: eventsFont }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ minWidth: 0, textAlign: isRTL ? 'right' : 'left' }}>
            <div style={{ ...inheritFont, display: 'inline-flex', alignItems: 'center', gap: 6, color: visitorTheme.accent }}>
              <Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />
              <span style={smallCapsStyle(isRTL, { color: visitorTheme.accent, fontSize: 10, fontWeight: 600 })}>{t("What's on", 'ماذا يجري')}</span>
            </div>
            <h1 style={{ ...inheritFont, fontSize: 34, lineHeight: 1.1, fontWeight: 700, color: visitorTheme.ink, margin: '4px 0 0', overflowWrap: 'anywhere' }}>{t('This week', 'هذا الأسبوع')}</h1>
            <p style={{ ...inheritFont, fontSize: 12.5, color: visitorTheme.warmMeta, margin: '4px 0 0', lineHeight: 1.4 }}>
              <b style={{ color: visitorTheme.ink, fontWeight: 600 }}>{model.upcomingCount}</b> {t('upcoming', 'قادمة')} · {t('Zgharta & Bsharri', 'زغرتا وبشري')} · {todayLine}
            </p>
          </div>
          <button type="button" aria-label={t('Share this week', 'شارك هذا الأسبوع')} onClick={() => onShareWeek(shareSummary)} style={{ ...inheritFont, width: 44, height: 44, minWidth: 44, borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: visitorTheme.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <Share2 style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div role="group" aria-label={t('The next seven days', 'الأيام السبعة القادمة')} data-testid="events-week-board" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
          {board.map(day => {
            const parts = eventDayParts(day.dayKey, dateLocale);
            const picked = pickedDay === day.dayKey;
            const filled = day.isToday || picked;
            return <button
              key={day.dayKey}
              type="button"
              data-testid="events-week-board-day"
              data-day={day.dayKey}
              aria-label={`${parts.full}, ${t(`${day.count} events`, `${day.count} فعاليات`)}`}
              aria-pressed={picked}
              {...(day.isToday ? { 'aria-current': 'date' } : {})}
              disabled={day.count === 0}
              onClick={() => setPickedDay(picked ? null : day.dayKey)}
              style={{ ...inheritFont, minWidth: 0, border: `1px solid ${filled ? visitorTheme.eventTodayFill : (day.isWeekend ? visitorTheme.eventWeekendPlateLine : 'transparent')}`, background: filled ? visitorTheme.eventTodayFill : (day.isWeekend ? visitorTheme.eventWeekendPlate : 'transparent'), borderRadius: 10, padding: '6px 0 5px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: day.count === 0 ? 'default' : 'pointer' }}
            >
              <span style={smallCapsStyle(isRTL, { fontSize: 10, fontWeight: 600, color: filled ? visitorTheme.eventTodayInk : visitorTheme.pastInk })}>{parts.weekday}</span>
              <span style={{ ...inheritFont, fontSize: 17, fontWeight: 700, lineHeight: 1, color: filled ? visitorTheme.eventTodayInk : visitorTheme.ink }}>{parts.day}</span>
              <span style={{ ...inheritFont, height: 16, minWidth: 16, padding: '0 4px', borderRadius: 9999, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', background: day.count === 0 ? 'transparent' : (filled ? premiumTheme.colors.paper : visitorTheme.mutedSurface), color: day.count === 0 ? visitorTheme.pastInk : visitorTheme.ink }}>{day.count === 0 ? '·' : day.count}</span>
            </button>;
          })}
        </div>
        <div style={{ ...inheritFont, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: visitorTheme.warmMeta, margin: '6px 0 0', padding: '0 2px 10px' }}>
          <span>{t('Fri–Sun', 'الجمعة–الأحد')}: <b style={{ fontWeight: 600, color: visitorTheme.ink }}>{split.weekend}</b> · {t('rest of week', 'باقي الأسبوع')}: <b style={{ fontWeight: 600, color: visitorTheme.ink }}>{split.rest}</b></span>
        </div>

        <div style={{ padding: '0 12px 10px', margin: '0 -12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          <div role="group" aria-label={t('Event category', 'فئة الفعالية')} style={{ display: 'flex', gap: 6 }}>
            {categoryOptions.map(c => {
              const active = evFilter === c.id;
              const Icon = c.icon;
              return <button key={c.id} type="button" aria-label={c.ariaLabel} aria-pressed={active} onClick={() => pickCategory(c.id)} style={{ ...inheritFont, flex: 'none', minHeight: 34, display: 'inline-flex', alignItems: 'center', gap: 6, padding: active ? '5px 10px' : '6px 11px', borderRadius: 9999, border: active ? `2px solid ${c.color}` : `1px solid ${visitorTheme.warm14}`, background: active ? c.bg : premiumTheme.colors.surface, color: active ? visitorTheme.ink : visitorTheme.title, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {active && <Check style={{ width: 11, height: 11, flexShrink: 0, color: c.color }} />}
                <span style={{ width: 18, height: 18, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.bg, color: c.color, flexShrink: 0 }}><Icon style={{ width: 11, height: 11 }} /></span>
                <span>{c.label}</span>
                <span style={{ fontSize: 10.5, color: visitorTheme.meta, fontWeight: 600 }}>{c.count}</span>
              </button>;
            })}
          </div>
        </div>
      </div>

      <div style={{ padding: '14px 12px 18px' }}>
        {body}
        {model.undated.length > 0 && <div data-testid="events-undated" style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px' }}>
            <h2 style={{ ...inheritFont, fontSize: 19, fontWeight: 700, color: visitorTheme.ink, margin: 0 }}>{t('Date to be announced', 'سيعلن الموعد لاحقاً')}</h2>
          </div>
          <EventDayGroup dayKey={null} events={model.undated} label={t('Date to be announced', 'سيعلن الموعد لاحقاً')} nextEventId={null} {...dayGroupProps} />
        </div>}
        {model.past.length > 0 && <details data-testid="events-past" open={pastOpen} style={{ margin: '6px 2px 0' }}>
          <summary
            onClick={event => { event.preventDefault(); setPastOpen(open => !open); }}
            style={{ ...inheritFont, listStyle: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, border: `1px dashed ${visitorTheme.warm14}`, color: visitorTheme.pastInk, fontSize: 13, fontWeight: 600 }}
          >
            {t(`Past events (${model.past.reduce((n, day) => n + day.events.length, 0)})`, `فعاليات سابقة (${model.past.reduce((n, day) => n + day.events.length, 0)})`)}
            <ChevronDown style={{ width: 14, height: 14, flexShrink: 0 }} />
          </summary>
          <div style={{ paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {model.past.map(day => <EventDayGroup key={day.dayKey} dayKey={day.dayKey} events={day.events} nextEventId={null} past {...dayGroupProps} />)}
          </div>
        </details>}
      </div>
    </div>;
};

export default function ZghartaTourismApp() {
  const initialRoute = getInitialSeoRoute();
  // Mounted in #423. `themeRootStyle` carries the flattened `--visitor-*` palette plus
  // `colorScheme`, and goes on every root wrapper below; `themeChoice` is null for System and
  // 'light'/'dark' for an explicit pick (#421 D2). Called before the loading/error early returns,
  // because the wrappers those return also need the variables.
  const { mode: themeMode, stored: themeChoice, setMode: setThemeMode, rootStyle: themeRootStyle } = useVisitorTheme();
  // #423's Map-tab light pin is GONE (#424): the map now has its own dark `styles` array, dark
  // marker tiers and dark chrome, so the pane and the nav over it read `themeMode` like every
  // other wrapper.
  //
  // Four things on this tab read palette VALUES rather than `var()`, because CSS is not what
  // consumes them: the Maps polygon options, the marker SVG presentation attributes, the geo-dot
  // `innerHTML`, and the global keyframes. Three of the four are built by imperative code inside
  // effects and class methods whose closures are NOT rebuilt on a theme change, so they read the
  // mode through this ref at call time instead of capturing it.
  //
  // Published from a LAYOUT effect rather than during render (cold review, #424). React does not
  // define what a render-time ref write means when a render is interrupted or discarded, so an
  // uncommitted mode could be left in the ref with nothing standing behind it. A layout effect
  // runs on commit and before every passive effect, so anything that reads this ref — the map
  // constructor, a marker builder, a late Nominatim polygon — sees committed state only. The
  // initial value comes from `useRef(themeMode)`, so the first render is right without it.
  const themeModeRef = useRef(themeMode);
  useLayoutEffect(() => { themeModeRef.current = themeMode; }, [themeMode]);
  const mapPalette = paletteFor(themeMode);
  const [seoRoute, setSeoRoute] = useState(initialRoute);
  const [tab, setTabState] = useState(initialRoute.tab);
  const [lang, setLangState] = useState(() => {
    if (initialRoute.lang) return initialRoute.lang;
    try { return localStorage.getItem('zgharta-lang:v1') || 'en'; } catch { return 'en'; }
  });
  const [selPlace, setSelPlace] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selBiz, setSelBiz] = useState(null);
  const [favs, setFavs] = useState(() => {
    try { return parseSavedFavorites(localStorage.getItem('zgharta-favs:v1')); }
    catch { return emptyFavorites(); }
  });
  const [savedAskPlans, setSavedAskPlans] = useState(() => {
    try {
      const current = localStorage.getItem(ASK_ZGHARTA_STORAGE_KEY);
      if (current) return parseSavedAskZghartaPlans(current);
      return parseSavedAskZghartaPlans(localStorage.getItem(LEGACY_ASK_ZGHARTA_STORAGE_KEY));
    } catch { return EMPTY_SAVED_ASK_ZGHARTA_PLANS; }
  });
  const [catFilter, setCatFilter] = useState([]);
  const [mapVillageFilter, setMapVillageFilter] = useState([]);
  const [zoneId, setZoneId] = useState('');
  const [selEvent, setSelEvent] = useState(null);
  const [places, setPlaces] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const detailReturnRouteRef = React.useRef(routeForTab(initialRoute.tab, initialRoute.lang || 'en'));
  const detailCloseButtonRef = React.useRef(null);

  const isRTL = lang === 'ar';
  const t = (en, ar) => lang === 'en' ? en : ar;

  const setTab = React.useCallback((nextTab) => {
    setTabState(prevTab => {
      const resolvedTab = typeof nextTab === 'function' ? nextTab(prevTab) : nextTab;
      const nextRoute = routeForTab(resolvedTab, lang);
      setSeoRoute(nextRoute);
      updateBrowserRoute(nextRoute);
      return resolvedTab;
    });
  }, [lang]);

  const setLang = React.useCallback((nextLang) => {
    setLangState(prevLang => {
      const resolvedLang = typeof nextLang === 'function' ? nextLang(prevLang) : nextLang;
      const nextRoute = { ...seoRoute, lang: resolvedLang };
      setSeoRoute(nextRoute);
      updateBrowserRoute(nextRoute);
      return resolvedLang;
    });
  }, [seoRoute]);

  // RTL-dependent styles (computed per render)
  const modalBackBtn = { position: 'absolute', top: 16, [isRTL ? 'right' : 'left']: 16, ...modalStyles.heroButton };
  const modalContainer = { position: 'fixed', inset: 0, background: visitorTheme.modalBg, zIndex: 50, overflowY: 'auto', direction: isRTL ? 'rtl' : 'ltr', fontFamily: fontFor(isRTL, 'body') };
  const screenContainer = { minHeight: '100vh', background: premiumTheme.colors.appBg, paddingBottom: premiumTheme.sizes.bottomNavClearance, direction: isRTL ? 'rtl' : 'ltr', fontFamily: fontFor(isRTL, 'body') };

  const fetchData = async () => {
    const applyCatalog = catalog => {
      setPlaces(catalog.places);
      setBusinesses(catalog.businesses);
      setEvents(catalog.events);
    };

    setLoading(true);
    setError(null);

    const result = await loadCatalog({
      storage: visitorThemeStorage(),
      now: Date.now,
      readRows: () => readCatalogRowsFromSupabase(supabase),
      onCacheHit: catalog => {
        applyCatalog(catalog);
        setLoading(false);
      },
    });

    if (result.catalog) {
      applyCatalog(result.catalog);
    } else if (!result.hadValidCache) {
      setError(result.error?.message || 'Failed to load');
    }

    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { try { localStorage.setItem('zgharta-favs:v1', JSON.stringify(favs)); } catch {} }, [favs]);
  useEffect(() => { try { localStorage.setItem(ASK_ZGHARTA_STORAGE_KEY, JSON.stringify(savedAskPlans)); } catch {} }, [savedAskPlans]);
  useEffect(() => { try { localStorage.setItem('zgharta-lang:v1', lang); } catch {} }, [lang]);

  const toggleFav = (id, type) => setFavs(p => { const k = type === 'place' ? 'places' : 'businesses'; return { ...p, [k]: p[k].includes(id) ? p[k].filter(i => i !== id) : [...p[k], id] }; });
  const isFav = (id, type) => favs[type === 'place' ? 'places' : 'businesses'].includes(id);
  const displayPlaces = places;
  const displayBusinesses = businesses;
  const publicCatalogPlaces = React.useMemo(
    () => getPublicEnglishCatalogItems(displayPlaces),
    [displayPlaces],
  );
  const publicCatalogBusinesses = React.useMemo(
    () => getPublicEnglishCatalogItems(displayBusinesses),
    [displayBusinesses],
  );
  const publicDisplayPlaces = React.useMemo(
    () => withGeozones(withDistinctPublicEnglishNames(publicCatalogPlaces)),
    [publicCatalogPlaces],
  );
  const publicDisplayBusinesses = React.useMemo(
    () => withGeozones(withDistinctPublicEnglishNames(publicCatalogBusinesses)),
    [publicCatalogBusinesses],
  );
  const catalog = React.useMemo(
    () => ({ places: publicDisplayPlaces, businesses: publicDisplayBusinesses, events }),
    [publicDisplayPlaces, publicDisplayBusinesses, events],
  );
  // Route slugs are assigned over the same collections the SEO generator uses — the
  // public-English catalog filtered by SEO attribution — so a name that collides for the
  // generator collides here too, and the URL the app pushes matches the sitemap. An item
  // that is not SEO-attributable has no generated page and keeps its plain name slug.
  const seoRoutePlaces = React.useMemo(
    () => publicCatalogPlaces.filter(isSeoZghartaAttributable),
    [publicCatalogPlaces],
  );
  const seoRouteBusinesses = React.useMemo(
    () => publicCatalogBusinesses.filter(isSeoZghartaAttributable),
    [publicCatalogBusinesses],
  );
  const seoRouteEvents = React.useMemo(
    () => (events || []).filter(isSeoZghartaAttributable),
    [events],
  );
  const selectedPlace = React.useMemo(
    () => selectedPlaceId ? (publicDisplayPlaces.find(place => String(place.id) === String(selectedPlaceId)) || selPlace) : null,
    [publicDisplayPlaces, selectedPlaceId, selPlace]
  );

  const openPlaceDetail = React.useCallback((place) => {
    if (!place) return;
    detailReturnRouteRef.current = routeForTab(tab, lang);
    setSelPlace(place);
    setSelectedPlaceId(place.id);
    setSelBiz(null);
    setSelEvent(null);
  }, [tab, lang]);

  const clearPlaceDetail = React.useCallback(() => {
    setSelPlace(null);
    setSelectedPlaceId(null);
  }, []);

  const saveAskPlan = React.useCallback((plan) => {
    const timestamp = new Date().toISOString();
    setSavedAskPlans(current => saveAskZghartaPlanState(current, plan, catalog, timestamp));
  }, [catalog]);

  const removeAskPlan = React.useCallback((planId) => {
    setSavedAskPlans(current => removeAskZghartaPlanState(current, planId));
  }, []);

  const shareAskPlanRecord = React.useCallback(async (record) => {
    const text = buildSavedAskZghartaPlanShareMessage(record, lang);
    const title = localizedAskZghartaText(record.plan.title, lang)
      || t(`${displayCazaShort(record.stops, false)} plan`, `خطة ${displayCazaShort(record.stops, true)}`);
    if (navigator.share) {
      try { await navigator.share({ title, text }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(text); alert(t('Copied to clipboard!', 'تم النسخ!')); } catch {}
  }, [lang, t]);

  const openAskPlanItem = React.useCallback((planItem) => {
    const ref = planItem?.ref;
    if (!ref) return;
    if (ref.type === 'place') {
      const match = publicDisplayPlaces.find(place => String(place.id) === String(ref.id));
      if (match) openPlaceDetail(match);
    } else if (ref.type === 'business') {
      const match = publicDisplayBusinesses.find(business => String(business.id) === String(ref.id));
      if (match) setSelBiz(match);
    } else if (ref.type === 'event') {
      const match = events.find(event => String(event.id) === String(ref.id));
      if (match) setSelEvent(match);
    }
  }, [publicDisplayPlaces, publicDisplayBusinesses, events, openPlaceDetail]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__ZGHARTA_SEO_READY = false;
    const handlePopState = () => {
      const nextRoute = parseSeoPath(window.location.pathname);
      setSeoRoute(nextRoute);
      setTabState(nextRoute.tab);
      setLangState(nextRoute.lang);
      if (!nextRoute.itemType) {
        setSelPlace(null);
        setSelectedPlaceId(null);
        setSelBiz(null);
        setSelEvent(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!seoRoute.itemType) return;

    if (seoRoute.itemType === 'place') {
      const match = findItemBySlug(seoRoutePlaces, seoRoute.slug)
        || findItemBySlug(publicDisplayPlaces, seoRoute.slug);
      if (match && selPlace?.id !== match.id) {
        setTabState('explore');
        setSelPlace(match);
        setSelectedPlaceId(match.id);
        setSelBiz(null);
        setSelEvent(null);
      }
    } else if (seoRoute.itemType === 'business') {
      const match = findItemBySlug(seoRouteBusinesses, seoRoute.slug)
        || findItemBySlug(publicDisplayBusinesses, seoRoute.slug);
      if (match && selBiz?.id !== match.id) {
        setTabState('explore');
        setSelPlace(null);
        setSelectedPlaceId(null);
        setSelBiz(withGeozones([match])[0]);
        setSelEvent(null);
      }
    } else if (seoRoute.itemType === 'event') {
      const match = findItemBySlug(seoRouteEvents, seoRoute.slug)
        || findItemBySlug(events, seoRoute.slug);
      if (match && selEvent?.id !== match.id) {
        setTabState('events');
        setSelPlace(null);
        setSelectedPlaceId(null);
        setSelBiz(null);
        setSelEvent(match);
      }
    }
    // The SEO-eligible collection is tried first because it is the one the generator
    // assigned slugs over; the full display list stays as the fallback so a URL for an
    // item that has no generated page still opens it.
  }, [
    loading, seoRoute, publicDisplayPlaces, publicDisplayBusinesses, events,
    seoRoutePlaces, seoRouteBusinesses, seoRouteEvents,
    selPlace?.id, selBiz?.id, selEvent?.id,
  ]);

  useEffect(() => {
    let nextRoute = null;
    let routeList = null;
    let routeItem = null;
    if (selPlace) {
      nextRoute = routeForItem('place', selPlace, lang, seoRoutePlaces);
      routeList = seoRoutePlaces;
      routeItem = selPlace;
    } else if (selBiz) {
      nextRoute = routeForItem('business', selBiz, lang, seoRouteBusinesses);
      routeList = seoRouteBusinesses;
      routeItem = selBiz;
    } else if (selEvent) {
      nextRoute = routeForItem('event', selEvent, lang, seoRouteEvents);
      routeList = seoRouteEvents;
      routeItem = selEvent;
    }
    if (!nextRoute) return;
    setSeoRoute(nextRoute);
    // Canonicalizing an older slug for the item already on screen REPLACES the entry.
    // Pushing would let Back land on the legacy URL, which resolves to the same item and
    // pushes the canonical again — a trap the visitor cannot leave.
    const current = typeof window === 'undefined' ? null : parseSeoPath(window.location.pathname);
    const canonicalizingSameItem = Boolean(
      current
      && current.itemType === nextRoute.itemType
      && current.slug
      && routeItem
      && findItemBySlug(routeList, current.slug)?.id === routeItem.id
    );
    updateBrowserRoute(nextRoute, { replace: canonicalizingSameItem });
  }, [selPlace, selBiz, selEvent, lang, seoRoutePlaces, seoRouteBusinesses, seoRouteEvents]);

  const activeSeo = React.useMemo(() => {
    if (selPlace) return { route: routeForItem('place', selPlace, lang, seoRoutePlaces), item: selPlace };
    if (selBiz) return { route: routeForItem('business', selBiz, lang, seoRouteBusinesses), item: selBiz };
    if (selEvent) return { route: routeForItem('event', selEvent, lang, seoRouteEvents), item: selEvent };
    return { route: { ...routeForTab(tab, lang), ...seoRoute, lang, tab }, item: null };
  }, [selPlace, selBiz, selEvent, lang, tab, seoRoute, seoRoutePlaces, seoRouteBusinesses, seoRouteEvents]);

  useEffect(() => {
    if (typeof window !== 'undefined') window.__ZGHARTA_SEO_READY = false;
    if (loading || (activeSeo.route.itemType && !activeSeo.item)) return;
    applyDocumentSeo({ route: activeSeo.route, item: activeSeo.item, lang });
    if (typeof window !== 'undefined') window.__ZGHARTA_SEO_READY = true;
  }, [activeSeo, lang, loading]);

  const getNearby = React.useCallback((coords, excludeId, limit = 4) => (
    hasUsableCoordinates(coords)
      ? getNearbyCatalogItems(catalog, coords, excludeId, limit).filter(hasUsableCoordinates)
      : []
  ), [catalog]);

  // Share a place via Web Share API or clipboard — include Google Maps link
  const shareLoc = async (name, village, coords, item) => {
    const mapsLink = hasUsableCoordinates(coords) ? `https://maps.google.com/?q=${coords.lat},${coords.lng}` : '';
    const region = displayCazaRegion(item, isRTL);
    const text = `${name} — ${village || region}${village ? `, ${region}` : ''}${mapsLink ? '\n' + mapsLink : ''}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: name,
          text,
          ...(mapsLink ? { url: mapsLink } : {}),
        });
      } catch {}
    }
    else { try { await navigator.clipboard.writeText(text); alert(t('Copied to clipboard!', 'تم النسخ!')); } catch {} }
  };

  // The Events tab's header share (#450). Events have no coordinates, so there is no map link to
  // share — what a visitor wants to pass on is the week itself, which is this page.
  const shareEventsWeek = async summary => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = t('Events in Zgharta and Bsharri', 'فعاليات زغرتا وبشري');
    const text = `${title} — ${summary}${url ? `\n${url}` : ''}`;
    if (navigator.share) {
      try { await navigator.share({ title, text, ...(url ? { url } : {}) }); } catch {}
      return;
    }
    try { await navigator.clipboard.writeText(text); alert(t('Copied to clipboard!', 'تم النسخ!')); } catch {}
  };

  useEffect(() => {
    const onPopState = () => clearPlaceDetail();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [clearPlaceDetail]);

  const closePlaceDetail = React.useCallback(() => {
    clearPlaceDetail();
    setSelBiz(null);
    setSelEvent(null);
    const nextRoute = detailReturnRouteRef.current || routeForTab(tab, lang);
    setSeoRoute(nextRoute);
    setTabState(nextRoute.tab);
    updateBrowserRoute(nextRoute, { replace: true });
  }, [clearPlaceDetail, lang, tab]);

  // Show on map helper
  const closeDetail = React.useCallback(() => {
    clearPlaceDetail();
    setSelBiz(null);
    setSelEvent(null);
    const nextRoute = routeForTab(tab, lang);
    setSeoRoute(nextRoute);
    updateBrowserRoute(nextRoute);
  }, [clearPlaceDetail, lang, tab]);

  const detailId = selBiz?.id ?? selEvent?.id ?? null;
  useEffect(() => {
    if (detailId == null) return undefined;
    const opener = document.activeElement;
    detailCloseButtonRef.current?.focus({ preventScroll: true });
    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDetail();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    };
  }, [closeDetail, detailId]);

  const showOnMap = () => {
    clearPlaceDetail();
    setSelBiz(null);
    setSelEvent(null);
    const nextRoute = routeForTab('map', lang);
    setSeoRoute(nextRoute);
    updateBrowserRoute(nextRoute);
    setTab('map');
  };

  const formatDistance = dist => dist < 1
    ? (isRTL ? `${(dist * 1000).toFixed(0)} متر` : `${(dist * 1000).toFixed(0)}m`)
    : (isRTL ? `${dist.toFixed(1)} كم` : `${dist.toFixed(1)}km`);
  const getCategoryDisplay = category => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat ? (isRTL ? cat.labelAr : cat.labelEn) : displayCategoryLabel(category, isRTL);
  };
  const getEventCategoryDisplay = category => ({
    cultural: t('Cultural', 'ثقافية'),
    festival: t('Festival', 'مهرجان'),
    nature: t('Nature', 'طبيعة'),
    religious: t('Religious', 'دينية'),
  }[category] || (isRTL ? 'فعالية' : 'Event'));

  const renderNearbySection = nearby => nearby.length > 0 && <section style={{ marginTop: 2 }}>
    <h3 style={{ ...modalStyles.sectionTitle, textAlign: isRTL ? 'right' : 'left' }}>{t('Nearby', 'بالقرب')}</h3>
    <div style={{ display: 'flex', gap: 14, overflowX: 'auto', padding: '2px 2px 12px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', direction: isRTL ? 'rtl' : 'ltr' }}>
      {nearby.map(n => {
        const NearI = catIcons[n.category] || MapPin;
        // Two grounds, so two inks: the pill floats on `photoPlate92`, which is a white plate in
        // BOTH modes (#423), while the distance label sits on the card's own themed surface. One
        // value served both until #428, and in dark that was 3.20-3.45:1 (#423 browser QA).
        const nearInk = categoryInkVars(n.category);
        const nearLabel = getCategoryDisplay(n.category);
        const nearLabels = getEditorialLabels(n, isRTL);
        const primaryPhoto = getPrimaryDisplayPhoto(n);
        const openNearby = () => {
          if (n.type === 'place') openPlaceDetail(n);
          else {
            clearPlaceDetail();
            setSelBiz(n);
          }
        };
        return <button type="button" key={`${n.type}-${n.id}`} onClick={openNearby} style={{ ...modalStyles.nearbyCard, fontFamily: 'inherit', color: 'inherit' }}>
          <div style={{ position: 'relative' }}>
            <PlaceImage src={displayPhotoThumbSrc(primaryPhoto)} category={n.category} name={displayName(n, isRTL)} caption={primaryPhoto.caption || displayPhotoCaption(n, isRTL)} attribution={displayPhotoAttribution(n, isRTL, primaryPhoto)} isRTL={isRTL} style={{ width: '100%', height: 104 }} />
            <span style={{ position: 'absolute', top: 8, [isRTL ? 'right' : 'left']: 8, display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 132, padding: '5px 8px', borderRadius: 9999, background: visitorTheme.photoPlate92, color: nearInk.onPlate, boxShadow: `0 8px 16px ${visitorTheme.scrim12}`, fontSize: 10, fontWeight: 700, overflow: 'hidden' }}>
              <NearI style={{ width: 11, height: 11, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nearLabel}</span>
            </span>
          </div>
          <div style={{ padding: 12 }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: premiumTheme.colors.ink, lineHeight: 1.2, minHeight: 34, marginBottom: 8, textAlign: isRTL ? 'right' : 'left', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{displayName(n, isRTL)}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: nearInk.ink, fontSize: 11, fontWeight: 500 }}><MapPin style={{ width: 11, height: 11 }} />{formatDistance(n.dist)}</span>
              <span style={smallCapsStyle(isRTL, { fontSize: 9, maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{nearLabels[0]}</span>
            </div>
            <p style={{ marginTop: 7, color: premiumTheme.colors.mutedInk, fontSize: 11, fontWeight: 700, textAlign: isRTL ? 'right' : 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayVillage(n, isRTL)}</p>
          </div>
        </button>;
      })}
    </div>
  </section>;

  // Event Detail Modal
  // The event detail, rebuilt for the Week Board (#450): a hero that falls back to a rotated date
  // plate rather than an empty box, two labelled When/Where tiles, a Pick-a-night row for a
  // multi-night run, an action grid, an honest no-description box, and a source credit line.
  //
  // Events carry NO coordinates — the ingest stores a venue string and a village — so Directions
  // is a Google Maps search, not a route. And the description is either the guide team's own or
  // nothing at all: the ingest deliberately never stores the source's marketing copy, and this
  // screen never invents a substitute for it.
  const EventModal = ({ event: e }) => {
    const s = eventCatStyles[e.category] || eventCatStyles.cultural;
    const eventName = displayEventName(e, isRTL);
    const eventLocation = displayEventLocation(e, isRTL);
    const rawEventVillage = displayVillage(e, isRTL);
    const eventVillage = rawEventVillage === PUBLIC_LOCALITY_UNDER_REVIEW_LABELS[isRTL ? 'ar' : 'en'] ? '' : rawEventVillage;
    // "Zgharta caza", not "Zgharta Caza, Lebanon": the Where tile already names the venue and the
    // village, so the caza is the last widening step, not a postal address.
    const eventRegion = displayCazaName(e, isRTL);
    const eventDescription = displayEventDescription(e, isRTL);
    const eventDateLocale = lang === 'ar' ? 'ar-LB' : 'en-US';
    const nights = eventNights(e, { max: 400 });
    const isRun = nights.isRun;
    const nowKey = beirutDayKey(new Date()) ?? '';
    const defaultNight = isRun ? firstRemainingNight(e, nowKey) : null;
    // An event past its first night but before `endDate` is still upcoming, so the night that is
    // pre-picked is the first one REMAINING — offering a visitor a night that has already gone is
    // how a calendar entry lands in the past.
    const [pickedNight, setPickedNight] = useState(defaultNight);
    // A run that opened before today still lists the nights it has already played. Offering them
    // would let a visitor put yesterday in their calendar, which is the exact result
    // `firstRemainingNight` exists to avoid — so an elapsed night of an ONGOING run is disabled.
    // A run that is entirely over keeps every night selectable: there is nothing left to protect,
    // and a dead row of buttons would just be broken.
    const runIsOver = isRun && nights.dayKeys[nights.dayKeys.length - 1] < nowKey;
    const isElapsedNight = night => isRun && !runIsOver && night < nowKey;
    const activeNight = isRun
      ? (nights.dayKeys.includes(pickedNight) && !isElapsedNight(pickedNight) ? pickedNight : defaultNight)
      : null;
    // Google reads the END of an all-day range as EXCLUSIVE; `googleCalendarAllDayRange` owns that
    // shift. For a run the visitor is adding ONE night, not the whole span.
    const calRange = isRun
      ? googleCalendarAllDayRange(activeNight, activeNight)
      : googleCalendarAllDayRange(e.date, e.endDate);
    const calUrl = calRange
      ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventName)}&dates=${calRange.start}/${calRange.end}&details=${encodeURIComponent(eventDescription || '')}&location=${encodeURIComponent(eventLocation || '')}`
      : null;
    const directionsUrl = eventDirectionsUrl(e, { cazaLabel: catalogCazaLabelOrNeutral(normalizeCatalogCaza(e.caza), lang) });
    const rangeEnd = e.endDate && e.endDate !== e.date ? e.endDate : null;
    const whenYears = eventRangeYearParts(e.date, rangeEnd, eventDateLocale);
    const whenValue = isRun
      ? `${eventShortDayLabel(e.date, eventDateLocale, { weekday: true, month: true, year: whenYears.start })} – ${eventShortDayLabel(rangeEnd, eventDateLocale, { weekday: true, month: true, year: whenYears.end })}`
      : (formatBeirutEventDate(e.date, eventDateLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
        || t('Date to be announced', 'سيعلن الموعد لاحقاً'));
    const timeText = displayEventTime(e.time, isRTL);
    const whenSubline = isRun
      ? `${t(`${nights.total} nights`, `${nights.total} ليالٍ`)}${timeText ? ` · ${timeText}` : ''}`
      : (timeText || t('Time to be announced', 'سيعلن الوقت لاحقاً'));
    const eventDateDay = formatBeirutEventDate(e.date, eventDateLocale, { day: 'numeric' });
    const eventDateMonthShort = formatBeirutEventDate(e.date, eventDateLocale, { month: 'short' });
    const eventCategoryLabel = getEventCategoryDisplay(e.category);
    const kicker = [
      formatBeirutEventDate(e.date, eventDateLocale, { weekday: 'long' }),
      timeText,
    ].filter(Boolean).join(' · ') || eventCategoryLabel;
    const credit = eventSourceCredit(e);
    // The hero is the 1600px master, never the 400px card derivative (#409).
    const heroPhotoRaw = getPrimaryDisplayPhoto(e).src;
    const heroPhotoSrc = typeof heroPhotoRaw === 'string' ? heroPhotoRaw.trim() : '';
    const [heroPhotoFailed, setHeroPhotoFailed] = useState(false);
    const showHeroPhoto = Boolean(heroPhotoSrc) && !heroPhotoFailed;
    // "Kfarsghab · Zgharta caza". Either half may be missing — an under-review locality drops the
    // village, and a row with no caza is never given one (#379).
    const whereSubline = [eventVillage, eventRegion && eventRegion !== eventVillage ? eventRegion : null]
      .filter(Boolean).join(' · ');
    const tileLabel = smallCapsStyle(isRTL, { fontSize: 12, fontWeight: 500, textTransform: 'none', color: visitorTheme.warmMeta });
    const gridBtn = { ...premiumStyles.actionButton, minHeight: 48, borderRadius: 12, fontSize: 14, fontWeight: 600 };

    return <>
      <div style={{ position: 'relative', height: 288, overflow: 'hidden', background: premiumTheme.colors.surface, borderBottom: `1px solid ${premiumTheme.colors.warmLine}` }}>
        {showHeroPhoto ? <>
          <div data-testid="event-modal-hero-photo" style={{ position: 'absolute', inset: 0 }}>
            <PlaceImage src={heroPhotoSrc} category={e.category} name={eventName} isRTL={isRTL} style={{ width: '100%', height: '100%' }} onPhotoUnavailable={() => setHeroPhotoFailed(true)} />
          </div>
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${visitorTheme.heroScrim62} 0%, ${visitorTheme.heroScrim06} 46%, ${visitorTheme.heroScrim30} 100%)`, pointerEvents: 'none' }} />
        </> : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* The plate sits on `surface` rather than the prototype's `paper` for one reason: at
              52px the numeral is large text, and the light `religious` hue clears 3:1 on surface
              (3.05) but not on paper (2.98). A 1% lighter plate is invisible; a numeral under the
              bar is not. */}
          <div style={{ width: 120, height: 120, borderRadius: 12, background: premiumTheme.colors.surface, border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', transform: 'none' }}>
            <span data-testid="event-modal-day" style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, color: s.color }}>{eventDateDay || '•'}</span>
            <span data-testid="event-modal-month" style={smallCapsStyle(isRTL, { marginTop: 4, fontSize: 11, fontWeight: 700, color: visitorTheme.pastInk })}>{eventDateMonthShort || t('Soon', 'قريباً')}</span>
          </div>
        </div>}
        <button aria-label={t('Share', 'مشاركة')} onClick={() => shareLoc(eventName, eventLocation, undefined, e)} style={{ ...modalStyles.heroButton, position: 'absolute', top: 16, [isRTL ? 'left' : 'right']: 16 }}><Share2 style={{ width: 18, height: 18, color: visitorTheme.title }} /></button>
        <div style={{ position: 'absolute', bottom: 14, insetInlineStart: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span data-testid="event-modal-category-chip" style={{ ...modalStyles.heroChip, gap: 5, padding: '5px 10px', fontSize: 12, fontWeight: 600 }}><Calendar style={{ width: 13, height: 13, color: s.color }} />{eventCategoryLabel}</span>
          <CazaAreaBadge source={e} isRTL={isRTL} />
        </div>
      </div>
      <div style={{ padding: '24px 22px 38px', background: premiumTheme.colors.paper, textAlign: isRTL ? 'right' : 'left' }}>
        {/* The prototype paints this kicker in the category hue. In light, `religious` (2.98:1) and
            `nature` (3.08:1) fail AA at 11px, so it reads in the brand accent instead; the category
            is still named in words on the hero chip, never carried by colour alone. */}
        <div style={smallCapsStyle(isRTL, { fontSize: 13, fontWeight: 500, textTransform: 'none', color: visitorTheme.accent })}>{kicker}</div>
        <h1 id="event-detail-title" style={{ ...modalStyles.title, fontSize: 34, fontWeight: 700, marginTop: 6, marginBottom: 0, lineHeight: 1.12, fontFamily: fontFor(isRTL, 'body') }}>{eventName}</h1>

        <div style={{ marginTop: 24, display: 'grid', gap: 0, borderTop: `1px solid ${premiumTheme.colors.warmLine}` }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '18px 0', borderBottom: `1px solid ${premiumTheme.colors.warmLine}` }}>
            <Calendar style={{ width: 18, height: 18, flexShrink: 0, color: visitorTheme.accent }} />
            <div style={{ minWidth: 0 }}>
              <div style={tileLabel}>{t('When', 'متى')}</div>
              <div data-testid="event-modal-full-date" style={{ fontSize: 15, fontWeight: 600, color: visitorTheme.ink, lineHeight: 1.3 }}>{whenValue}</div>
              <div style={{ fontSize: 12, color: visitorTheme.meta, fontWeight: 500, marginTop: 1, direction: isRTL ? 'rtl' : 'ltr', unicodeBidi: 'embed' }}>{whenSubline}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '18px 0', borderBottom: `1px solid ${premiumTheme.colors.warmLine}` }}>
            <MapPin style={{ width: 18, height: 18, flexShrink: 0, color: visitorTheme.accent }} />
            <div style={{ minWidth: 0 }}>
              <div style={tileLabel}>{t('Where', 'أين')}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: visitorTheme.ink, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{eventLocation}</div>
              <div style={{ fontSize: 12, color: visitorTheme.meta, fontWeight: 500, marginTop: 1 }}>{whereSubline}</div>
            </div>
          </div>
        </div>

        {isRun && <div data-testid="event-modal-nights" style={{ marginTop: 14 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: visitorTheme.ink, margin: '0 0 6px' }}>{t('Pick a night', 'اختر الليلة')}</h4>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {nights.dayKeys.map(night => {
              const picked = night === activeNight;
              const elapsed = isElapsedNight(night);
              return <button
                key={night}
                type="button"
                data-testid="event-modal-night"
                data-night={night}
                data-elapsed={elapsed ? 'true' : undefined}
                aria-pressed={picked}
                disabled={elapsed}
                aria-label={`${formatBeirutEventDate(night, eventDateLocale, { weekday: 'long', day: 'numeric', month: 'long' })}${elapsed ? ` — ${t('already played', 'انتهت')}` : ''}`}
                onClick={() => setPickedNight(night)}
                style={{ flex: '1 0 62px', minWidth: 62, fontFamily: 'inherit', background: picked ? visitorTheme.accentTint : premiumTheme.colors.surface, border: picked ? `2px solid ${visitorTheme.accent}` : `1px solid ${premiumTheme.colors.warmLine}`, borderRadius: 12, padding: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: elapsed ? 'default' : 'pointer', color: elapsed ? visitorTheme.pastMeta : visitorTheme.ink, opacity: elapsed ? 0.55 : 1 }}
              >
                <b style={{ fontSize: 16, fontWeight: 800 }}>{formatBeirutEventDate(night, eventDateLocale, { day: 'numeric' })}</b>
                <span style={smallCapsStyle(isRTL, { fontSize: 10, fontWeight: 600, color: visitorTheme.warmMeta })}>{formatBeirutEventDate(night, eventDateLocale, { weekday: 'short' })}</span>
                {picked && <span style={{ fontSize: 9, fontWeight: 800, color: visitorTheme.accent }}>{t('✓ picked', '✓ مختارة')}</span>}
                {!picked && elapsed && <span style={{ fontSize: 9, fontWeight: 700, color: visitorTheme.pastMeta }}>{t('played', 'انتهت')}</span>}
              </button>;
            })}
          </div>
        </div>}

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {calUrl
            ? <a href={calUrl} target="_blank" rel="noopener noreferrer" style={{ ...gridBtn, gridColumn: '1 / -1', background: visitorTheme.accent, color: visitorTheme.onAccent, boxShadow: 'none' }}>
              <CalendarPlus style={{ width: 16, height: 16 }} />
              {t('Add to calendar', 'أضف للتقويم')}{isRun && activeNight ? ` · ${eventShortDayLabel(activeNight, eventDateLocale, { month: true })}` : ''}
            </a>
            : null}
          <a href={directionsUrl} target="_blank" rel="noopener noreferrer" style={{ ...gridBtn, ...(calUrl ? {} : { gridColumn: '1 / -1' }), background: premiumTheme.colors.surface, color: visitorTheme.ink, border: `1px solid ${premiumTheme.colors.warmLine}` }}>
            <Navigation style={{ width: 16, height: 16 }} />{t('Directions', 'الاتجاهات')}
          </a>
          <button onClick={() => shareLoc(eventName, eventLocation, undefined, e)} style={{ ...gridBtn, background: premiumTheme.colors.surface, color: visitorTheme.ink, border: `1px solid ${premiumTheme.colors.warmLine}`, cursor: 'pointer' }}>
            <Share2 style={{ width: 16, height: 16 }} />{t('Share', 'مشاركة')}
          </button>
        </div>

        {eventDescription
          ? <p style={{ ...modalStyles.bodyText, marginTop: 18, marginBottom: 0, fontSize: 15, lineHeight: 1.65 }}>{eventDescription}</p>
          : <p data-testid="event-modal-no-description" style={{ marginTop: 18, marginBottom: 0, padding: '14px 0', borderTop: `1px solid ${visitorTheme.warm12}`, fontSize: 13, color: visitorTheme.warmMeta, lineHeight: 1.55 }}>
            {/* A hand-added row has no listing to have confirmed anything, and saying it did
                would fabricate provenance next to a credit line that says the opposite. */}
            {credit.kind === 'listed'
              ? t('No description yet. Name, date and venue are confirmed from the listing; the guide team adds a note when they have one.', 'لا يوجد وصف بعد. الاسم والموعد والمكان مؤكدة من المصدر؛ يضيف فريق الدليل ملاحظة عند توفرها.')
              : t('No description yet. The guide team adds a note when they have one.', 'لا يوجد وصف بعد. يضيف فريق الدليل ملاحظة عند توفرها.')}
          </p>}

        <div data-testid="event-modal-source" style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${premiumTheme.colors.warmLine}`, fontSize: 12, color: visitorTheme.warmMeta, lineHeight: 1.5 }}>
          {credit.kind === 'listed' ? <>
            {t('Listed by', 'مدرج من')} <b style={{ fontWeight: 700, color: visitorTheme.ink }}>{credit.domain}</b>
            {credit.url && <> · <a href={credit.url} target="_blank" rel="noopener noreferrer" style={{ color: visitorTheme.accent, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>{t('Tickets & original listing', 'الصفحة الأصلية')}<ExternalLink style={{ width: 11, height: 11 }} /></a></>}
            <br />{t('We show the name, date and venue only — never their marketing text.', 'نعرض الاسم والموعد والمكان فقط — لا نص تسويقي.')}
          </> : t('Added by the guide team.', 'أضافه فريق الدليل.')}
        </div>
      </div>
    </>;
  };
  // MapScreen state — lifted to parent so it survives MapScreen remounts
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const markerLayerRef = React.useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const selectedMarkerRef = React.useRef(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const carouselRef = React.useRef(null);
  const visibleCardsRef = React.useRef([]);
  // The 'idle' listener is registered once at map creation, so it would otherwise close over
  // the very first render's updateCards (and its filter snapshot) forever (#250).
  const updateCardsRef = React.useRef(() => {});
  const [mapLoaded, setMapLoaded] = useState(false);
  // `mapLoaded` says the Maps API SCRIPT is ready; it flips before the Map instance exists and
  // does not flip again when it is created. The #424 theme effect has to run after the INSTANCE
  // exists, so it keys on this instead — see the comment on that effect.
  const [mapInstanceReady, setMapInstanceReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [mapFilter, setMapFilter] = useState([]);
  const [geoActive, setGeoActive] = useState(false);
  // Locate-me feedback (#438). `value` is the state the visitor is shown; `seq` exists so that
  // reporting the SAME status twice still counts as a new event — otherwise a second failure of
  // the same kind inside the notice window would inherit the first one's dismissal timer and the
  // notice would vanish early, which reads as the silence this ticket removed.
  const [geoStatus, setGeoStatus] = useState({ value: GEO_IDLE, seq: 0 });
  const reportGeoStatus = status => setGeoStatus(prev => ({ value: status, seq: prev.seq + 1 }));
  const geoMarkerRef = React.useRef(null);
  const [cardsVisible, setCardsVisible] = useState(true);
  const [showVillageDrop, setShowVillageDrop] = useState(false);
  const mapVillageButtonRef = React.useRef(null);
  const mapVillagePanelRef = React.useRef(null);
  const closeMapVillagePicker = () => {
    setShowVillageDrop(false);
    mapVillageButtonRef.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!showVillageDrop || tab !== 'map') return undefined;
    mapVillagePanelRef.current?.focus({ preventScroll: true });
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setShowVillageDrop(false);
        mapVillageButtonRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showVillageDrop, tab]);
  const geolocDone = React.useRef(false);
  const prevZoomTierRef = React.useRef(null);
  const [outsideBounds, setOutsideBounds] = useState(false);
  const [returnAreaId, setReturnAreaId] = useState('zgharta');
  const cazaOverlaysRef = React.useRef({ drawn: false, overlays: [] });
  const geozoneLayerRef = React.useRef(null);
  const [showAreas, setShowAreas] = useState(false);
  const cazaAreasRef = React.useRef(createInitialCazaAreas());
  const [mapViewportHeight, setMapViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return 844;
    return Math.round(window.visualViewport?.height || window.innerHeight || 844);
  });

  const favsRef = React.useRef(favs);

  const villageFilter = mapVillageFilter;
  const setVillageFilter = setMapVillageFilter;

  const allLocations = React.useMemo(() => getMapCatalogLocations(catalog), [catalog]);
  const filteredLocations = React.useMemo(() => filterMapCatalogLocations(allLocations, { categoryIds: mapFilter, villageIds: villageFilter, zoneId }), [allLocations, mapFilter, villageFilter, zoneId]);
  const filtersActive = mapFilter.length > 0 || villageFilter.length > 0 || Boolean(zoneId);
  const villageGroups = React.useMemo(() => getCatalogVillageGroups(allLocations, { categoryIds: mapFilter }), [allLocations, mapFilter]);
  const focusCatalogRefOnMap = React.useCallback((ref) => {
    if (!ref || (ref.type !== 'place' && ref.type !== 'business')) return;
    const loc = allLocations.find(location => location.type === ref.type && String(location.id) === String(ref.id));
    if (!loc || !loc.coordinates?.lat || !loc.coordinates?.lng) return;
    setMapFilter([]);
    setVillageFilter([]);
    setZoneId('');
    selectedMarkerRef.current = loc;
    setSelectedMarker(loc);
    setCardsVisible(true);
    setVisibleCards(prev => {
      const next = [loc, ...prev.filter(item => !(item.type === loc.type && item.id === loc.id))].slice(0, 8);
      visibleCardsRef.current = next;
      return next;
    });
    setTab('map');
    window.setTimeout(() => {
      mapInstanceRef.current?.panTo({ lat: loc.coordinates.lat, lng: loc.coordinates.lng });
      mapInstanceRef.current?.setZoom(16);
    }, 0);
  }, [allLocations, setTab, setVillageFilter]);
  const viewAskPlanItemOnMap = React.useCallback((planItem) => {
    focusCatalogRefOnMap(planItem?.ref);
  }, [focusCatalogRefOnMap]);
  const viewAskPlanRecordOnMap = React.useCallback((record) => {
    const item = record?.plan?.sections?.flatMap(section => section.items || []).find(candidate => candidate.ref?.type === 'place' || candidate.ref?.type === 'business');
    focusCatalogRefOnMap(item?.ref);
  }, [focusCatalogRefOnMap]);
  const askPanelProps = {
    catalog,
    lang,
    isRTL,
    t,
    savedAskPlans,
    onSavePlan: saveAskPlan,
    onRemovePlan: removeAskPlan,
    onSharePlan: shareAskPlanRecord,
    onOpenItem: openAskPlanItem,
    onViewItemOnMap: viewAskPlanItemOnMap,
  };
  // Filter changes must clear the ref as well as the state: updateCards reads the ref, so
  // clearing state alone left a filtered-out marker pinned in the carousel (#250).
  const clearSelectedMarker = React.useCallback(() => { selectedMarkerRef.current = null; setSelectedMarker(null); }, []);

  // #442. The search box searches the WHOLE catalog; the carousel's pool is the FILTERED set, so
  // a match the active filters exclude was panned to and then dropped by the next map idle.
  // Owner decision 2026-08-27: clear the filters, the way Ask Zgharta's "view on map" already
  // does — but this handler runs on EVERY KEYSTROKE, so it clears only when there really is a
  // match AND that match is really excluded. Empty input, a query under the 3-character floor and
  // a no-match query all leave a visitor's deliberate filter choice exactly where they put it.
  //
  // Clearing a filter silently would be the worse half of the trade, so the clear is ANNOUNCED in
  // words (the owner is colourblind — a chip losing its tint is not a signal): `searchClearedFor`
  // names the row the filters were dropped for, and the map's existing filter affordances (the
  // village button's label, the clear-filters X) go back to their no-filter state beside it.
  const [searchClearedFor, setSearchClearedFor] = useState(null);
  // ONE rule retires the notice, rather than a rule per path: it stands only while it still
  // describes the map — the filters are still empty AND the row it names is still the selected
  // one. Re-applying a filter, a later search that lands on a different row, a marker tap and a
  // tap on the blank map all move one of those two, and none of them has to know about this
  // notice (cold review, #442). The commit that sets it also sets `selectedMarker` to the same
  // row and empties the filters, so it never wipes itself.
  useEffect(() => {
    setSearchClearedFor(prev => {
      if (!prev) return prev;
      if (filtersActive) return null;
      return selectedMarker && selectedMarker.type === prev.type && String(selectedMarker.id) === String(prev.id) ? prev : null;
    });
  }, [filtersActive, selectedMarker]);
  const runMapSearch = React.useCallback((rawQuery) => {
    const plan = planMapSearchSelection(allLocations, filteredLocations, rawQuery);
    if (!plan.match) {
      // No hidden state: an emptied box does NOT put the filters back, it only stops describing
      // a clear that has already happened.
      if (!String(rawQuery || '').trim()) setSearchClearedFor(null);
      return;
    }
    if (plan.clearsFilters) {
      setMapFilter([]);
      setVillageFilter([]);
      setZoneId('');
      setSearchClearedFor(plan.match);
    }
    selectedMarkerRef.current = plan.match;
    setSelectedMarker(plan.match);
    setCardsVisible(true);
    mapInstanceRef.current?.panTo({ lat: plan.match.coordinates.lat, lng: plan.match.coordinates.lng });
    mapInstanceRef.current?.setZoom(16);
  }, [allLocations, filteredLocations, setVillageFilter]);
  const updateCards = React.useCallback(() => {
    const bounds = mapInstanceRef.current?.getBounds();
    if (!bounds) return;
    const visible = filterLocationsByViewport(filteredLocations, latLng => bounds.contains(latLng));
    const cards = computeVisibleMapCards({
      locationsInView: sortCatalogItemsForDisplay(visible),
      selectionPool: filteredLocations,
      selected: selectedMarkerRef.current,
      previousCards: visibleCardsRef.current,
      filtersActive,
    });
    if (cards === visibleCardsRef.current) return;
    setVisibleCards(cards);
    visibleCardsRef.current = cards;
  }, [filteredLocations, filtersActive]);
  // Assigned by an effect with NO guard: the map effect below returns early when the map host
  // is absent, and the 'idle' listener it registered lives on regardless, so assigning the ref
  // in there would reintroduce the stale callback this fixes (cold review finding, #250).
  useEffect(() => { updateCardsRef.current = updateCards; }, [updateCards]);

  const toggleCat = (c) => setMapFilter(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleVillage = (v) => setVillageFilter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const mapTightHeight = mapViewportHeight < 520;
  const mapCompactHeight = mapViewportHeight < 640;
  const mapCardHeight = mapTightHeight ? 144 : mapCompactHeight ? 164 : 184;
  const mapCarouselBottom = mapTightHeight ? 64 : mapCompactHeight ? 66 : 72;
  const mapControlBottomVisible = mapCarouselBottom + mapCardHeight + 12;
  const mapControlBottomHidden = mapTightHeight ? 68 : 80;
  const mapCarouselPadding = mapTightHeight ? '0 18px' : '0 24px';
  const mapBottomWithSafeArea = value => `calc(${value}px + env(safe-area-inset-bottom))`;
  const mapCarouselBottomCss = mapBottomWithSafeArea(mapCarouselBottom);
  const mapControlBottomVisibleCss = mapBottomWithSafeArea(mapControlBottomVisible);
  const mapControlBottomHiddenCss = mapBottomWithSafeArea(mapControlBottomHidden);
  // Locate-me feedback (#438). The strip sits one control-height above the control row and
  // tracks the same folded/open offset, so it never overlaps the buttons or the card drawer.
  const geoPending = geoStatus.value === GEO_PENDING;
  const geoStatusMessage = geolocationMessage(geoStatus.value);
  const geoStatusText = geoStatusMessage ? t(geoStatusMessage.en, geoStatusMessage.ar) : '';
  const mapControlRowBottom = cardsVisible && visibleCards.length > 0 ? mapControlBottomVisible : mapControlBottomHidden;
  const mapGeoStatusBottomCss = mapBottomWithSafeArea(mapControlRowBottom + 50);


  const catIconPaths = {
    religious: { vb: '0 0 16 16', d: '<line x1="8" y1="1" x2="8" y2="15"/><line x1="3" y1="5" x2="13" y2="5"/>' },
    nature: { vb: '0 0 24 24', d: '<path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"/><path d="M12 22v-3"/>' },
    heritage: { vb: '0 0 24 24', d: '<path d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z"/><path d="M6 7v11"/><path d="M10 7v11"/><path d="M14 7v11"/><path d="M18 7v11"/><line x1="3" y1="22" x2="21" y2="22"/>' },
    restaurant: { vb: '0 0 24 24', d: '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><line x1="7" y1="2" x2="7" y2="22"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>' },
    hotel: { vb: '0 0 24 24', d: '<path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><path d="M12 4v6"/><line x1="2" y1="18" x2="22" y2="18"/>' },
    cafe: { vb: '0 0 24 24', d: '<path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/><line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/>' },
    shop: { vb: '0 0 24 24', d: '<path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"/><path d="M16 10a4 4 0 0 1-8 0"/>' },
    health: { vb: '0 0 24 24', d: '<path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/>' },
  };

  const makeCatSVG = (cat, color, size) => {
    const icon = catIconPaths[cat];
    if (!icon) return '';
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="${icon.vb}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icon.d}</svg>`;
  };


  // `markerHalo` / `markerIconOnFav` are read as VALUES here, not `var()`: both are written into
  // an SVG `stroke=` / `fill=` presentation attribute, which cannot resolve a custom property.
  // They come off the CURRENT palette rather than the frozen light one (#422's placeholder), and
  // through the ref rather than the render closure, because these three builders are handed to
  // every MarkerRecord at construction and outlive the render that made them.
  const makeDotEl = (color, cat, fav) => {
    const el = document.createElement('div');
    const halo = paletteFor(themeModeRef.current).markerHalo;
    if (fav) {
      const muted = mutedCatColors[cat] || color;
      el.style.cssText = `width:22px;height:22px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px ${visitorTheme.scrim30});`;
      el.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="${muted}" stroke="${halo}" stroke-width="1.5"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>`;
    } else {
      el.style.cssText = `width:10px;height:10px;background:${color};border:2px solid ${visitorTheme.markerHalo};border-radius:50%;box-shadow:0 1px 3px ${visitorTheme.scrim30};`;
    }
    return el;
  };

  const makeIconEl = (cat, color, fav) => {
    const el = document.createElement('div');
    if (fav) {
      const muted = mutedCatColors[cat] || color;
      el.style.cssText = `width:28px;height:28px;background:${muted};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px ${visitorTheme.scrim25};`;
      el.innerHTML = makeCatSVG(cat, paletteFor(themeModeRef.current).markerIconOnFav, 16);
    } else {
      el.style.cssText = `width:28px;height:28px;background:${visitorTheme.markerBg};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px ${visitorTheme.scrim25};`;
      el.innerHTML = makeCatSVG(cat, color, 16);
    }
    return el;
  };

  const makeLabeledEl = (cat, color, name, fav) => {
    const el = document.createElement('div');
    el.style.cssText = 'display:flex;align-items:center;gap:4px;';
    const iconWrap = document.createElement('div');
    if (fav) {
      const muted = mutedCatColors[cat] || color;
      iconWrap.style.cssText = `width:28px;height:28px;background:${muted};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px ${visitorTheme.scrim25};flex-shrink:0;`;
      iconWrap.innerHTML = makeCatSVG(cat, paletteFor(themeModeRef.current).markerIconOnFav, 16);
    } else {
      iconWrap.style.cssText = `width:28px;height:28px;background:${visitorTheme.markerBg};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px ${visitorTheme.scrim25};flex-shrink:0;`;
      iconWrap.innerHTML = makeCatSVG(cat, color, 16);
    }
    const label = document.createElement('div');
    const truncated = name.length > 20 ? name.slice(0, 20) + '…' : name;
    // The label halo IS a CSS `text-shadow`, so it may stay a `var()`.
    const halo = visitorTheme.markerHalo;
    label.style.cssText = `font-family:${mapFontFor(isRTL)};font-size:12px;font-weight:600;letter-spacing:0;font-style:normal;color:${color};white-space:nowrap;text-shadow:0 0 3px ${halo},-1px -1px 0 ${halo},1px -1px 0 ${halo},-1px 1px 0 ${halo},1px 1px 0 ${halo},0 -1px 0 ${halo},0 1px 0 ${halo},-1px 0 0 ${halo},1px 0 0 ${halo};`;
    label.textContent = truncated;
    el.appendChild(iconWrap);
    el.appendChild(label);
    return el;
  };

  useEffect(() => {
    const isMapsApiReady = () => Boolean(window.google?.maps?.Map);
    if (isMapsApiReady()) { setMapLoaded(true); return; }
    if (!GOOGLE_MAPS_KEY) return;
    let checkTimer;
    const markMapReady = () => {
      if (isMapsApiReady()) setMapLoaded(true);
      else checkTimer = setTimeout(markMapReady, 200);
    };
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) { markMapReady(); return () => clearTimeout(checkTimer); }
    const script = document.createElement('script');
    window.__zghartaGoogleMapsReady = markMapReady;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&loading=async&callback=__zghartaGoogleMapsReady`;
    script.async = true; script.defer = true;
    script.onerror = () => setMapError(true);
    document.head.appendChild(script);
    return () => clearTimeout(checkTimer);
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      setMapViewportHeight(Math.round(window.visualViewport?.height || window.innerHeight || 844));
    };
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (tab !== 'map' || selectedPlaceId) return;
    const orig = {
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.height,
      bodyPosition: document.body.style.position,
      bodyWidth: document.body.style.width,
      bodyTop: document.body.style.top,
      htmlOverflow: document.documentElement.style.overflow,
      htmlHeight: document.documentElement.style.height,
    };
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = '0';
    return () => {
      document.documentElement.style.overflow = orig.htmlOverflow;
      document.documentElement.style.height = orig.htmlHeight;
      document.body.style.overflow = orig.bodyOverflow;
      document.body.style.height = orig.bodyHeight;
      document.body.style.position = orig.bodyPosition;
      document.body.style.width = orig.bodyWidth;
      document.body.style.top = orig.bodyTop;
    };
  }, [tab, selectedPlaceId]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google?.maps?.Map) return;
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 34.3955, lng: 35.8945 },
        zoom: 15,
        isFractionalZoomEnabled: true,
        gestureHandling: 'greedy',
        disableDefaultUI: true,
        zoomControl: false,
        // Through the ref, not `themeMode`: this effect's deps are [mapLoaded, filteredLocations],
        // so its closure can be older than the current mode. Runtime changes are handled by the
        // dedicated effect below.
        styles: visitorMapStyles(themeModeRef.current),
      });
      setMapInstanceReady(true);
      mapInstanceRef.current.addListener('click', () => { selectedMarkerRef.current = null; setSelectedMarker(null); });
      mapInstanceRef.current.addListener('dragstart', () => setGeoActive(false));
      mapInstanceRef.current.addListener('zoom_changed', () => {
        const zoom = mapInstanceRef.current.getZoom();
        const oldTier = prevZoomTierRef.current;
        const newTier = overlayZoomTier(zoom, oldTier);
        // #431: BOTH directions animate. This used to be `newTier > oldTier`, so zooming out was
        // a hard cut. The transition is now a crossfade (src/map/markerTierTransition.js), which
        // reads the same either way and never leaves a marker without content.
        const shouldAnimate = oldTier !== null && newTier !== oldTier;
        prevZoomTierRef.current = newTier;
        // Always resync, even when the global tier didn't move: a record's own _tier can diverge
        // from it (setRecords() rebuilds records, and the favs effect resets _tier to null), and
        // skipping this call on an unchanged global tier left such a record stuck on a stale icon
        // (cold review finding, #341). Each record's own updateContent no-ops when its tier is
        // already correct, so this stays cheap — comparisons only, no DOM writes, when nothing
        // actually needs to change.
        markerLayerRef.current?.updateActiveTiers(zoom, shouldAnimate);
      });
      mapInstanceRef.current.addListener('idle', () => {
        updateCardsRef.current();
        const c = mapInstanceRef.current.getCenter();
        if (c) {
          const lat = c.lat();
          const lng = c.lng();
          const nextReturnId = resolveReturnAreaId(lat, lng, cazaAreasRef.current);
          const inAnyCaza = CAZA_MAP_AREAS.some(area => pointInCazaBounds(lat, lng, cazaAreasRef.current[area.id]?.bounds));
          // One constant, not two (#437): the pill's floor IS the marker-hide floor, so the
          // affordance is on screen for exactly the band where every pin is culled.
          const zoomedOut = isBelowMarkerHideFloor(mapInstanceRef.current.getZoom());
          setReturnAreaId(nextReturnId);
          setOutsideBounds(!inAnyCaza || zoomedOut);
        }
      });
      if (!cazaOverlaysRef.current.drawn) {
        cazaOverlaysRef.current.drawn = true;
        (async () => {
          // Nominatim's usage policy is an absolute maximum of one request per second, so the two
          // caza polygons are fetched ONE AT A TIME with a gap between them — never in parallel.
          // The request count is fixed at CAZA_MAP_AREAS.length (2) for the life of the map.
          // Each area is caught on its own: a `Promise.all` loses BOTH boundaries when either
          // request fails, which is not "degrade quietly" — it is a blank map for a fault in one
          // area. An area that fails simply keeps the hard-coded fallback bounds already seeded
          // into cazaAreasRef by createInitialCazaAreas().
          for (let i = 0; i < CAZA_MAP_AREAS.length; i += 1) {
            const area = CAZA_MAP_AREAS[i];
            if (i > 0) await new Promise(resolve => setTimeout(resolve, NOMINATIM_MIN_REQUEST_GAP_MS));
            if (!mapInstanceRef.current) return;
            const controller = typeof AbortController === 'function' ? new AbortController() : null;
            const timeoutId = controller ? setTimeout(() => controller.abort(), NOMINATIM_REQUEST_TIMEOUT_MS) : null;
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${area.query.replace(/ /g, '+')}&format=json&polygon_geojson=1&limit=1`,
                {
                  // Browsers refuse to let fetch set User-Agent (it is a forbidden header name), so
                  // this is dropped in the app and honoured only by non-browser callers. Nominatim
                  // still receives the Origin/Referer the browser sets, which is what identifies us.
                  headers: { 'User-Agent': 'ZghartaTourismApp/1.0' },
                  ...(controller ? { signal: controller.signal } : {}),
                }
              );
              if (!res.ok) continue;
              const data = await res.json();
              const paths = geoJsonToLatLngPaths(data?.[0]?.geojson || null);
              if (!paths.length || !mapInstanceRef.current) continue;
              cazaAreasRef.current[area.id] = {
                bounds: boundsFromPaths(paths),
                returnCenter: area.returnCenter,
              };
              cazaOverlaysRef.current.overlays.push(...drawCazaAreaOverlay(area, paths, mapInstanceRef.current, themeModeRef.current));
            } catch (err) {
              // Fallback bounds for THIS area are already set in cazaAreasRef; try the next one.
              // An abort lands here too, which is the point: a stalled area is skipped, not waited on.
            } finally {
              if (timeoutId) clearTimeout(timeoutId);
            }
          }
        })();
      }
    }

    // Round 2 (#339): a single MarkerRecord per location (lazy per-tier DOM element, no eager
    // creation of all three tiers), owned by one MarkerLayer OverlayView instead of N independent
    // ones. MarkerLayer projects and viewport-culls all records in one draw() pass — replacing N
    // per-marker `fromLatLngToDivPixel` + style writes per frame with one bounds check per marker
    // (cheap arithmetic) plus a `transform: translate3d(...)` write only for markers actually
    // in (or near) the viewport. Markers outside the padded viewport are fully detached from the
    // DOM rather than kept around with `display:none`.
    // Ceiling on how many on-screen markers may run a tier crossfade in one crossing (#431).
    // 120 covers a normal screenful; a denser one keeps the extra markers correct and visible,
    // just without the fade. See updateActiveTiers for the measurement behind the number.
    const MAX_ANIMATED_TIER_SWAPS = 120;
    // How far outside the viewport a marker may sit and still be treated as on screen for the
    // crossfade (fraction of viewport size, per axis). See viewportAnimationBox.
    const ANIMATE_VIEWPORT_PAD = 0.25;
    const CULL_ATTACH_PAD = 0.5; // fraction of viewport size to pre-attach just outside view
    const CULL_DETACH_PAD = 0.85; // wider margin before detaching — avoids edge-of-viewport thrash

    class MarkerRecord {
      constructor(loc, position, onClick, isFav, meta) {
        this.loc = loc;
        this.position = position;
        this.latLng = new window.google.maps.LatLng(position.lat, position.lng);
        this.onClick = onClick;
        this.isFav = isFav;
        this.category = meta.category;
        this.color = meta.color;
        this.locName = meta.locName;
        this.makeDotEl = meta.makeDotEl;
        this.makeIconEl = meta.makeIconEl;
        this.makeLabeledEl = meta.makeLabeledEl;
        this.elements = {};
        this.div = null;
        this._active = false;
        this._tier = null;
        // Handle for an in-flight tier crossfade (#431): { ghost, timer }, or null.
        this._tierSwap = null;
        this._px = null;
        this._py = null;
      }
      ensureEl(tier) {
        if (tier === 2) {
          if (!this.elements.elLabeled) this.elements.elLabeled = this.makeLabeledEl(this.category, this.color, this.locName, this.isFav);
          return this.elements.elLabeled;
        }
        if (tier === 1) {
          if (!this.elements.elIcon) this.elements.elIcon = this.makeIconEl(this.category, this.color, this.isFav);
          return this.elements.elIcon;
        }
        if (!this.elements.elDot) this.elements.elDot = this.makeDotEl(this.color, this.category, this.isFav);
        return this.elements.elDot;
      }
      ensureDiv() {
        if (this.div) return;
        this.div = document.createElement('div');
        this.div.setAttribute('role', 'button');
        this.div.setAttribute('tabindex', '0');
        this.div.setAttribute('aria-label', this.locName);
        this.div.style.cssText = `position:absolute;cursor:pointer;z-index:${this.isFav ? 10 : 1};will-change:transform;`;
        this.div.addEventListener('click', (e) => { e.stopPropagation(); this.onClick(); });
        this.div.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          this.onClick();
        });
      }
      activate(container) {
        this.ensureDiv();
        if (this.div.parentNode !== container) container.appendChild(this.div);
        this._active = true;
      }
      deactivate() {
        // A crossfade left running past a cull would strand the outgoing element inside the
        // reused wrapper and fire its removal timer against a detached div (#431).
        clearMarkerTierGhost(this._tierSwap);
        this._tierSwap = null;
        if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
        this._active = false;
        this._px = null;
        this._py = null;
      }
      updateContent(zoom, animate) {
        if (!this.div) return;
        const tier = markerContentTier(zoom, this._tier);
        if (this._tier === tier) return;
        this._tier = tier;
        const el = this.ensureEl(tier);
        // #431: the swap itself lives in src/map/markerTierTransition.js — it keeps the outgoing
        // tier on screen, centred on the same anchor, until the incoming one has faded in, so
        // this marker is never blank. It settles any crossfade still running first, which is
        // what keeps updateContent idempotent under repeated zoom_changed and palette repaints.
        this._tierSwap = swapMarkerTierContent(this.div, el, {
          handle: this._tierSwap,
          animate,
          reducedMotion: prefersReducedMotion(),
        });
      }
    }

    class MarkerLayer extends window.google.maps.OverlayView {
      constructor() {
        super();
        this.records = [];
        this.container = null;
        this._hideTier = null;
      }
      onAdd() {
        this.container = document.createElement('div');
        this.container.style.cssText = 'position:absolute;left:0;top:0;width:0;height:0;';
        this.getPanes().overlayMouseTarget.appendChild(this.container);
      }
      onRemove() {
        this.records.forEach((r) => r.deactivate());
        if (this.container) { this.container.remove(); this.container = null; }
      }
      setRecords(records) {
        this.records.forEach((r) => r.deactivate());
        this.records = records;
        this.updateActiveSet();
      }
      updateActiveTiers(zoom, animate) {
        // Two bounds on how many markers may crossfade at once (#431), because starting an
        // animation on every attached record is what turned a tier crossing into a 50ms long
        // task under 4x CPU throttle — a measurable regression in the exact zoom-out gesture
        // #337/#339 spent a ticket making cheap (measured: baseline 0 long tasks, unbounded
        // crossfade 1 x 50ms, bounded 0).
        //
        // 1. OFF-SCREEN records swap instantly. Culling attaches records up to CULL_ATTACH_PAD
        //    outside the viewport, so a crossing at low zoom touches far more records than are
        //    on screen, and animating those buys nothing anyone can see. The test is the
        //    viewport widened by ANIMATE_VIEWPORT_PAD, not the bare viewport: a marker's
        //    ANCHOR can sit outside the bounds while the marker itself still paints inside
        //    them (a label-tier marker is ~120px wide), and `getBounds()` during a zoom already
        //    reports the destination, so an unpadded test drops markers the visitor is looking
        //    at right now.
        // 2. Past MAX_ANIMATED_TIER_SWAPS on-screen records, the rest swap instantly too. An
        //    instant swap still never blanks — that is the whole point of the crossfade module,
        //    which mounts the incoming element before it animates anything — so the cap costs
        //    smoothness on the densest screens, never visibility. Records are visited in catalog
        //    order, which is spatially unsorted, so the animated subset is scattered across the
        //    viewport rather than being one region that moves while its neighbour does not.
        const map = animate ? this.getMap() : null;
        const box = viewportAnimationBox(map ? map.getBounds() : null, ANIMATE_VIEWPORT_PAD);
        let budget = MAX_ANIMATED_TIER_SWAPS;
        this.records.forEach((r) => {
          if (!r._active) return;
          let animateThis = animate && isInAnimationBox(r.position, box);
          if (animateThis) {
            if (budget <= 0) animateThis = false;
            // Only a record that ACTUALLY changes tier spends budget; updateContent no-ops for
            // the rest, and a no-op that ate budget would starve the markers that do move.
            else if (markerContentTier(zoom, r._tier) !== r._tier) budget -= 1;
          }
          r.updateContent(zoom, animateThis);
        });
      }
      draw() {
        this.updateActiveSet();
      }
      updateActiveSet() {
        const map = this.getMap();
        const proj = this.getProjection();
        if (!map || !proj || !this.container) return;
        const zoom = map.getZoom();
        const bounds = map.getBounds();
        // A transiently-null bounds (projection not settled yet) must not overwrite _hideTier —
        // that would "poison" the hysteresis state and force an extra 0.15 zoom of climb to
        // un-hide once bounds return (cold review finding, #341). Missing bounds hides this frame
        // for its own reason, independent of the persisted tier.
        if (bounds) this._hideTier = overlayZoomTier(zoom, this._hideTier);
        const globallyHidden = !bounds || this._hideTier === -1;
        let attach = null;
        let detach = null;
        if (!globallyHidden) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latSpan = ne.lat() - sw.lat();
          const lngSpan = ne.lng() - sw.lng();
          attach = {
            minLat: sw.lat() - latSpan * CULL_ATTACH_PAD, maxLat: ne.lat() + latSpan * CULL_ATTACH_PAD,
            minLng: sw.lng() - lngSpan * CULL_ATTACH_PAD, maxLng: ne.lng() + lngSpan * CULL_ATTACH_PAD,
          };
          detach = {
            minLat: sw.lat() - latSpan * CULL_DETACH_PAD, maxLat: ne.lat() + latSpan * CULL_DETACH_PAD,
            minLng: sw.lng() - lngSpan * CULL_DETACH_PAD, maxLng: ne.lng() + lngSpan * CULL_DETACH_PAD,
          };
        }
        for (const r of this.records) {
          const { lat, lng } = r.position;
          let shouldBeActive;
          if (globallyHidden) {
            shouldBeActive = false;
          } else if (r._active) {
            // Hysteresis: an already-active marker only drops out past the wider DETACH box.
            shouldBeActive = lat >= detach.minLat && lat <= detach.maxLat && lng >= detach.minLng && lng <= detach.maxLng;
          } else {
            shouldBeActive = lat >= attach.minLat && lat <= attach.maxLat && lng >= attach.minLng && lng <= attach.maxLng;
          }
          if (shouldBeActive && !r._active) {
            r.activate(this.container);
            r.updateContent(zoom, false);
          } else if (!shouldBeActive && r._active) {
            r.deactivate();
            continue;
          }
          if (!shouldBeActive) continue;
          const pos = proj.fromLatLngToDivPixel(r.latLng);
          if (!pos) continue;
          if (pos.x === r._px && pos.y === r._py) continue;
          r._px = pos.x;
          r._py = pos.y;
          r.div.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
        }
      }
    }

    if (!markerLayerRef.current) {
      markerLayerRef.current = new MarkerLayer();
      markerLayerRef.current.setMap(mapInstanceRef.current);
    }

    // Debug hook for scripts/measure-map-zoom.mjs: total catalog markers this filter pass built,
    // independent of how many are currently attached to the DOM (culling makes DOM count
    // viewport-dependent, so it's no longer a valid "is the catalog loaded" readiness signal).
    window.__zghartaTestTotalMarkers = filteredLocations.length;

    const newRecords = filteredLocations.map((loc) => {
      // A VALUE, not `var()`: this colour is written into an SVG `stroke=`/`fill=` presentation
      // attribute by makeCatSVG / makeDotEl, which cannot resolve a CSS variable (cold review, #422).
      const color = markerColors[loc.category] || paletteFor(themeModeRef.current).accent;
      const locName = displayName(loc, isRTL);
      const locFav = loc.type === 'place' ? favsRef.current.places.includes(loc.id) : favsRef.current.businesses.includes(loc.id);

      const record = new MarkerRecord(
        loc,
        { lat: loc.coordinates.lat, lng: loc.coordinates.lng },
        () => { selectedMarkerRef.current = loc; setSelectedMarker(loc); setCardsVisible(true); mapInstanceRef.current.panTo({ lat: loc.coordinates.lat, lng: loc.coordinates.lng }); },
        locFav,
        { category: loc.category, color, locName, makeDotEl, makeIconEl, makeLabeledEl }
      );

      markersRef.current.push({ overlay: record, elements: record.elements, loc });
      return record;
    });

    markerLayerRef.current.setRecords(newRecords);

    // Update visible cards for carousel
    updateCards();

    // Geolocate once: if user is in Zgharta area, pan to their location
    if (!geolocDone.current && mapInstanceRef.current && navigator.geolocation) {
      geolocDone.current = true;
      navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        const inAnyCaza = CAZA_MAP_AREAS.some(area => pointInCazaBounds(latitude, longitude, cazaAreasRef.current[area.id]?.bounds));
        if (inAnyCaza) {
          mapInstanceRef.current.panTo({ lat: latitude, lng: longitude });
          mapInstanceRef.current.setZoom(15);
        }
      }, () => {});
    }
    return () => {
      markerLayerRef.current?.setRecords([]);
      markersRef.current = [];
      if (geoMarkerRef.current) { geoMarkerRef.current.setMap(null); geoMarkerRef.current = null; }
    };
  }, [mapLoaded, filteredLocations]);

  useEffect(() => {
    if (!mapInstanceReady) return undefined;
    const layer = createGeozoneLayer(window.google.maps, mapInstanceRef.current);
    geozoneLayerRef.current = layer;
    return () => {
      layer.setMap(null);
      geozoneLayerRef.current = null;
    };
  }, [mapInstanceReady]);

  useEffect(() => {
    if (!geozoneLayerRef.current) return;
    styleGeozoneLayer(geozoneLayerRef.current, { zoneId, visible: showAreas, color: paletteFor(themeMode).accent });
  }, [mapInstanceReady, zoneId, showAreas, themeMode]);

  // Lightweight effect: update marker visuals when favs change (no marker recreation, no zoom change)
  useEffect(() => {
    favsRef.current = favs;
    if (!mapInstanceRef.current || markersRef.current.length === 0) return;
    const zoom = mapInstanceRef.current.getZoom();
    markersRef.current.forEach(({ overlay, loc }) => {
      const locFav = loc.type === 'place' ? favs.places.includes(loc.id) : favs.businesses.includes(loc.id);
      if (overlay.isFav === locFav) return;
      overlay.isFav = locFav;
      overlay.elements.elDot = null;
      overlay.elements.elIcon = null;
      overlay.elements.elLabeled = null;
      overlay._tier = null;
      if (overlay.div) overlay.div.style.zIndex = locFav ? 10 : 1;
      overlay.updateContent(zoom, false);
    });
  }, [favs]);

  // Everything on this tab that reads a palette VALUE rather than a `var()` is repainted here.
  // A `var()` surface needs nothing: the map pane is a DOM descendant of the themed root wrapper,
  // so the frosted chrome, the carousel and the village panel all re-resolve on their own.
  //
  // Keyed on `mapInstanceReady` and NOT on `mapLoaded` (cold review, #424). `mapLoaded` means the
  // Maps SCRIPT is ready, which happens before the instance exists and never happens again — so
  // on a first load this effect ran once against a null map and then never fired, leaving the
  // constructor's own read of `themeModeRef` as the only thing painting the map. Browser QA
  // measured exactly 0 `setOptions` calls on load, which is that gap. Keying on the instance
  // makes the effect self-correcting: it always runs after creation, whatever the constructor got.
  //
  // Placed AFTER the effect that creates the map and the markers, so on a render where both the
  // map and a new mode arrive together this runs second and wins.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    // Wholesale replacement, which is why visitorMapStyles carries the POI-hiding rules in BOTH
    // arrays — `setOptions({ styles })` does not merge with what was there (#421 D6).
    map.setOptions({ styles: visitorMapStyles(themeMode) });
    applyCazaAreaTheme(cazaOverlaysRef.current.overlays, themeMode);
    geoMarkerRef.current?.paint?.();
    if (markersRef.current.length > 0) {
      const zoom = map.getZoom();
      markersRef.current.forEach(({ overlay }) => {
        // The category hue is a literal and does not move between modes, but a row whose category
        // is missing or unrecognised falls back to the ACCENT, which does — and the record captured
        // whichever mode it was built in. `filteredLocations` does not change on a theme switch, so
        // nothing else would ever rebuild it (cold review, #428).
        overlay.color = markerColors[overlay.category] || paletteFor(themeMode).accent;
        // Same shape as the favs effect: drop the cached per-tier elements and clear the tier so
        // updateContent rebuilds this record's CURRENT tier from the new palette. A record that
        // is culled out of the viewport has no div and is skipped — it is rebuilt from scratch
        // when it next activates, which is already how a marker enters the DOM.
        overlay.elements.elDot = null;
        overlay.elements.elIcon = null;
        overlay.elements.elLabeled = null;
        overlay._tier = null;
        overlay.updateContent(zoom, false);
      });
    }
  }, [themeMode, mapInstanceReady]);

  useEffect(() => {
    // `cardsVisible` is part of the guard, not just the deps: a fold that lands inside the 150ms
    // window below used to leave this pointed at a carousel that is translated off the screen,
    // and `scrollIntoView` then scrolled the MAP HOST instead of the carousel (#433).
    if (!selectedMarker || !cardsVisible || !carouselRef.current) return;
    // Delay to let updateCards/render settle after panTo triggers idle
    const timer = setTimeout(() => {
      if (!carouselRef.current) return;
      const idx = visibleCardsRef.current.findIndex(v => v.id === selectedMarker.id && v.type === selectedMarker.type);
      if (idx >= 0 && carouselRef.current.children[idx]) {
        carouselRef.current.children[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedMarker, visibleCards, cardsVisible]);

  // A failure notice clears itself after GEO_NOTICE_MS (#438). It is advice, not an error the
  // visitor has to acknowledge, and a notice that needed a tap to remove would be a second dead
  // control on the same screen. Keyed on the whole status object so a repeat of the same failure
  // restarts the timer rather than inheriting the previous one's.
  useEffect(() => {
    if (!isGeolocationFailure(geoStatus.value)) return undefined;
    const timer = setTimeout(() => setGeoStatus(prev => (isGeolocationFailure(prev.value) ? { value: GEO_IDLE, seq: prev.seq + 1 } : prev)), GEO_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [geoStatus]);

  const handleLocateMe = () => {
    // Tapping again while the browser's own permission prompt is still up would queue a second
    // request behind the first. The spinner and "Finding you…" are what say to wait.
    if (geoStatus.value === GEO_PENDING) return;
    // The old code returned silently when the map host was absent — the Maps script failed, or
    // the pane has not painted yet. Nothing can be shown in that state, so say so; a silent
    // return is the same dead control this ticket is about.
    if (!mapInstanceRef.current) { reportGeoStatus(GEO_UNAVAILABLE); return; }
    requestVisitorLocation({
      geolocation: navigator.geolocation,
      onStatus: reportGeoStatus,
      onPosition: (pos) => {
      if (!mapInstanceRef.current) return;
      const { latitude, longitude } = pos.coords;
      const position = { lat: latitude, lng: longitude };
      mapInstanceRef.current.panTo(position);
      mapInstanceRef.current.setZoom(16);
      setGeoActive(true);
      if (geoMarkerRef.current) { geoMarkerRef.current.setMap(null); geoMarkerRef.current = null; }
      // Blue dot overlay
      const GeoOverlay = class extends window.google.maps.OverlayView {
        onAdd() {
          this.div = document.createElement('div');
          this.div.style.cssText = 'position:absolute;';
          this.paint();
          this.getPanes().overlayMouseTarget.appendChild(this.div);
        }
        // Values, not var(): this string is assembled before the overlay is in the document, and
        // the keyframes it names are a global stylesheet rather than a themed subtree. Split out
        // of onAdd so a theme change repaints a dot that is ALREADY on the map — the overlay is
        // created once per locate-me tap and would otherwise keep the mode it was born in.
        paint() {
          if (!this.div) return;
          const palette = paletteFor(themeModeRef.current);
          this.div.innerHTML = `<div style="width:16px;height:16px;background:${palette.geoDot};border:3px solid ${palette.markerHalo};border-radius:50%;box-shadow:0 0 8px ${palette.geoHalo};animation:geoPulse 2s infinite"></div>`;
        }
        draw() {
          const proj = this.getProjection();
          if (!proj || !this.div) return;
          const p = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(position.lat, position.lng));
          if (p) { this.div.style.left = (p.x - 11) + 'px'; this.div.style.top = (p.y - 11) + 'px'; }
        }
        onRemove() { if (this.div) { this.div.remove(); this.div = null; } }
      };
      geoMarkerRef.current = new GeoOverlay();
      geoMarkerRef.current.setMap(mapInstanceRef.current);
      },
    });
  };

  // `overflow` is owned by the stylesheet below, NOT declared here: it needs the two-declaration
  // `hidden` -> `clip` fallback pair, which an inline style object cannot express (#433).
  const MapScreen = () => <div className="map-screen" style={{ position: 'fixed', inset: 0, maxWidth: 448, margin: '0 auto', touchAction: 'none', zIndex: 1, fontFamily: mapFontFor(isRTL) }}>
    {(GOOGLE_MAPS_KEY || window.google?.maps?.Map) ? (
      mapError ? <div style={{ width: '100%', height: '100%', background: visitorTheme.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ textAlign: 'center' }}><MapIcon style={{ width: 48, height: 48, color: visitorTheme.emptyIcon, margin: '0 auto 12px' }} /><p style={{ color: visitorTheme.meta, marginBottom: 12 }}>{t('Map failed to load', 'فشل تحميل الخريطة')}</p><button onClick={() => window.location.reload()} style={{ padding: '8px 20px', background: visitorTheme.mapAccent, color: visitorTheme.onAccent, border: 'none', borderRadius: 9999, cursor: 'pointer', fontSize: 14 }}>{t('Reload', 'إعادة تحميل')}</button></div></div>
      : <>
        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />
        {!mapLoaded && <div style={{ position: 'absolute', inset: 0, background: visitorTheme.mapLoadingBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 style={{ width: 32, height: 32, color: visitorTheme.mapAccent, animation: 'spin 1s linear infinite' }} /></div>}
      </>
    ) : (
      <div style={{ width: '100%', height: '100%', background: visitorTheme.mapPlaceholderBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ textAlign: 'center' }}><MapIcon style={{ width: 64, height: 64, color: visitorTheme.mapPlaceholderInk, margin: '0 auto 16px' }} /><p style={{ color: visitorTheme.controlInk }}>{t('Add Google Maps API key', 'أضف مفتاح خرائط غوغل')}</p></div></div>
    )}

    {/* Top bar: search + village filter + category chips */}
    <div style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Row 1: Search bar + village + lang */}
      <div style={{ background: visitorTheme.mapGlass24, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: `1px solid ${visitorTheme.mapGlassLine}`, borderRadius: 16, padding: '4px 6px 4px 14px', boxShadow: `0 2px 7px ${visitorTheme.scrim08}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Search style={{ width: 18, height: 18, color: visitorTheme.controlInk, flexShrink: 0 }} />
        {/* This box searches `allLocations` on purpose — it is a whole-catalog lookup, not a
            filter of the visible set. A match the active filters exclude now CLEARS them and says
            so (#442, `runMapSearch`), so the drawer always opens onto the row that was found. */}
        <input type="text" aria-label={t('Search map places', 'ابحث في أماكن الخريطة')} placeholder={t('Find a place', 'ابحث عن مكان')} style={{ flex: 1, minWidth: 0, height: 44, border: 'none', outline: 'none', fontSize: 16, background: 'transparent', color: visitorTheme.ink, textAlign: isRTL ? 'right' : 'left' }} onChange={e => runMapSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button ref={mapVillageButtonRef} type="button" data-testid="map-village-filter-button" aria-controls="map-village-panel" aria-describedby={zoneId ? 'map-area-filter-status' : undefined} aria-label={t('Filter map by village', 'فلترة الخريطة حسب القرية')} aria-expanded={showVillageDrop} onClick={() => setShowVillageDrop(v => !v)} style={{ minHeight: 44, padding: '0 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', background: villageFilter.length > 0 ? visitorTheme.glassActive : visitorTheme.mapGlass28, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', color: villageFilter.length > 0 ? visitorTheme.onInk : visitorTheme.controlInk, display: 'flex', alignItems: 'center', gap: 3 }}>
            <MapPin style={{ width: 14, height: 14 }} />
            {villageFilter.length === 0 ? t('Villages', 'القرى') : villageFilter.length === 1 ? displayVillageFilterLabel(villageFilter[0], allLocations, isRTL) : villageFilter.length}
            {zoneId && <><span aria-hidden="true">· 1</span><span id="map-area-filter-status" hidden>{t('1 area filter active', 'فلتر منطقة نشط')}</span></>}
            <ChevronDown style={{ width: 14, height: 14 }} />
          </button>
          {filtersActive && <button type="button" aria-label={t('Clear map filters', 'مسح فلاتر الخريطة')} onClick={() => { setMapFilter([]); setVillageFilter([]); setZoneId(''); clearSelectedMarker(); }} style={{ width: 44, minHeight: 44, padding: 0, borderRadius: 12, fontSize: 11, border: 'none', cursor: 'pointer', background: visitorTheme.clearFilterBg, color: visitorTheme.clearFilterInk }}><X style={{ width: 14, height: 14 }} /></button>}
          <button type="button" aria-label={t('Switch language to Arabic', 'بدّل اللغة إلى الإنكليزية')} onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')} style={{ minHeight: 44, padding: '0 10px', background: visitorTheme.mapGlass28, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: visitorTheme.controlInk }}>{lang === 'en' ? 'ع' : 'EN'}</button>
        </div>
      </div>
      {/* Row 1b: what just happened to the filters (#442). The clear is announced in WORDS, on
          its own line, because a category chip losing its tint is a colour-only signal and the
          owner is colourblind. It is a statement, not a control: the visitor's way back is the
          same chips and village button, which are already back in their no-filter state. */}
      {searchClearedFor && <div data-testid="map-filters-cleared-notice" role="status" style={{ background: visitorTheme.mapGlass24, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: `1px solid ${visitorTheme.mapGlassLine}`, borderRadius: 8, padding: '6px 10px', boxShadow: `0 2px 7px ${visitorTheme.scrim08}`, fontSize: 11, lineHeight: 1.4, color: visitorTheme.controlInk, textAlign: isRTL ? 'right' : 'left' }}>
        {t(`Filters cleared to show ${displayName(searchClearedFor, false)}`, `تم مسح الفلاتر لعرض ${displayName(searchClearedFor, true)}`)}
      </div>}
      {/* Row 2: Category chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', flex: 1, minWidth: 0, gap: 6, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', direction: isRTL ? 'rtl' : 'ltr' }}>
        {CATEGORIES.map(c => {
          const active = mapFilter.includes(c.id);
          const Icon = c.icon;
          // `data-testid` + `data-category-id` mirror the village filter's existing hook shape
          // (`village-filter-option` / `data-village-id`): they let a probe pick a chip without
          // depending on an English aria-label, which is what made the #435 probe time out under
          // `--lang ar` (cold review).
          return <button key={c.id} type="button" data-testid="map-category-chip" data-category-id={c.id} aria-label={t(`Toggle ${c.labelEn} map category`, `بدّل فئة ${c.labelAr} على الخريطة`)} aria-pressed={active} onClick={() => { toggleCat(c.id); clearSelectedMarker(); }} style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 44, padding: '8px 13px', borderRadius: 12, border: active ? `2px solid ${c.color}` : `1.5px solid ${visitorTheme.mapChipLine}`, background: active ? c.color : visitorTheme.mapGlass38, backdropFilter: active ? 'none' : 'blur(8px)', WebkitBackdropFilter: active ? 'none' : 'blur(8px)', color: active ? visitorTheme.categoryOnFill : visitorTheme.chipInk, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: `0 1px 3px ${visitorTheme.scrim08}`, flexShrink: 0, transition: 'all 0.15s ease' }}>
            <Icon style={{ width: 13, height: 13 }} />
            {t(c.labelEn, c.labelAr)}
          </button>;
        })}
      </div>

      </div>
    </div>

    {/* Village dropdown panel */}
    {showVillageDrop && <div ref={mapVillagePanelRef} id="map-village-panel" role="dialog" aria-label={t('Villages', 'القرى')} tabIndex={-1} style={{ position: 'absolute', top: 120, [isRTL ? 'left' : 'right']: 12, zIndex: 20, outline: 'none' }}>
      <VillageFilterPanel
        groups={villageGroups}
        villageFilter={villageFilter}
        items={allLocations}
        isRTL={isRTL}
        t={t}
        onToggle={id => { toggleVillage(id); clearSelectedMarker(); }}
        onClear={() => { setVillageFilter([]); clearSelectedMarker(); }}
      >
        <MapOptions value={zoneId} onChange={value => { setZoneId(value); clearSelectedMarker(); if (value && value !== '__review__') setShowAreas(true); }} visible={showAreas} onVisibleChange={setShowAreas} isRTL={isRTL} />
      </VillageFilterPanel>
    </div>}

    {/* Click outside to close dropdown */}
    {showVillageDrop && <div data-testid="map-village-filter-dismiss" onClick={closeMapVillagePicker} style={{ position: 'absolute', inset: 0, zIndex: 11 }} />}

    {/* Locate me button */}
    <button type="button" data-testid="map-locate-me" aria-label={t('Locate me on the map', 'حدد موقعي على الخريطة')} aria-pressed={geoActive} aria-busy={geoPending} data-geo-status={geoStatus.value} onClick={handleLocateMe} style={{
      position: 'absolute', bottom: cardsVisible && visibleCards.length > 0 ? mapControlBottomVisibleCss : mapControlBottomHiddenCss, [isRTL ? 'right' : 'left']: 12, zIndex: 8,
      width: 44, height: 44, borderRadius: 9999, border: `1.5px solid ${visitorTheme.mapControlLine}`,
      background: geoActive ? visitorTheme.geoActiveBg : visitorTheme.mapGlass52,
      backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
      boxShadow: `0 2px 8px ${visitorTheme.scrim11}`, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'bottom 0.3s ease, transform 0.1s ease, background 0.2s ease',
    }} {...pressScaleHandlers}>
      {geoPending
        ? <Loader2 data-testid="map-locate-me-spinner" style={{ width: 20, height: 20, color: geoActive ? visitorTheme.geoActiveInk : visitorTheme.meta, animation: 'spin 1s linear infinite' }} />
        : <Navigation style={{ width: 20, height: 20, color: geoActive ? visitorTheme.geoActiveInk : visitorTheme.meta, fill: geoActive ? visitorTheme.geoActiveInk : 'none', transition: 'all 0.2s ease' }} />}
    </button>

    {/* Locate-me status strip (#438). Always mounted so `role="status"` really announces a
        change; empty and invisible when there is nothing to say. The state is carried by a
        SENTENCE and by a GLYPH (spinner while working, crossed-out arrow on a failure) — never
        by a hue, because the owner is colourblind. `pointerEvents: none` keeps it out of the way
        of map gestures, and it mirrors to the locate-me side under RTL. */}
    <div data-testid="map-geo-status" role="status" aria-live="polite" style={{
      position: 'absolute', bottom: mapGeoStatusBottomCss, left: 12, right: 12, zIndex: 9,
      display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start',
      direction: isRTL ? 'rtl' : 'ltr', pointerEvents: 'none',
      opacity: geoStatusText ? 1 : 0,
      transition: 'bottom 0.3s ease, opacity 0.2s ease',
    }}>
      {geoStatusText ? <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: '100%',
        padding: '7px 11px', borderRadius: 8,
        border: `1.5px solid ${visitorTheme.mapControlLine}`, background: visitorTheme.mapGlass52,
        backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
        boxShadow: `0 2px 8px ${visitorTheme.scrim11}`,
        fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: visitorTheme.chipInk,
        textAlign: isRTL ? 'right' : 'left',
      }}>
        {geoPending
          ? <Loader2 style={{ width: 14, height: 14, flexShrink: 0, color: visitorTheme.meta, animation: 'spin 1s linear infinite' }} />
          : <NavigationOff style={{ width: 14, height: 14, flexShrink: 0, color: visitorTheme.meta }} />}
        <span>{geoStatusText}</span>
      </span> : null}
    </div>

    {/* Area-aware return pill */}
    <button type="button" data-testid="map-return-area-pill" data-caza={returnAreaId} aria-label={t(`Return map to ${catalogCazaLabel(returnAreaId, 'en')}`, `أعد الخريطة إلى ${catalogCazaLabel(returnAreaId, 'ar')}`)} onClick={() => {
      const area = CAZA_MAP_AREAS.find(candidate => candidate.id === returnAreaId) || CAZA_MAP_AREAS[0];
      mapInstanceRef.current?.panTo(area.returnCenter);
      mapInstanceRef.current?.setZoom(RETURN_AREA_ZOOM);
    }} style={{
      position: 'absolute', bottom: cardsVisible && visibleCards.length > 0 ? mapControlBottomVisibleCss : mapControlBottomHiddenCss, left: '50%', transform: 'translateX(-50%)', zIndex: 8,
      height: 44, padding: '0 14px', borderRadius: 8, border: `1.5px solid ${visitorTheme.mapPillLine}`,
      background: visitorTheme.mapGlass48,
      backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
      boxShadow: `0 2px 8px ${visitorTheme.scrim11}`, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6,
      opacity: outsideBounds ? 1 : 0, pointerEvents: outsideBounds ? 'auto' : 'none',
      animation: outsideBounds ? 'homePulse 2s ease-in-out infinite' : 'none',
      transition: 'bottom 0.3s ease, opacity 0.3s ease',
    }}>
      <Compass style={{ width: 16, height: 16, color: visitorTheme.mapAccent }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: visitorTheme.chipInk }}>{displayCazaShort(returnAreaId, isRTL)}</span>
    </button>

    {/* Card toggle button */}
    {visibleCards.length > 0 && <button type="button" aria-label={cardsVisible ? t('Hide map place cards', 'إخفاء بطاقات الخريطة') : t('Show map place cards', 'إظهار بطاقات الخريطة')} aria-expanded={cardsVisible} onClick={() => setCardsVisible(v => !v)} style={{
      position: 'absolute', bottom: cardsVisible ? mapControlBottomVisibleCss : mapControlBottomHiddenCss, [isRTL ? 'left' : 'right']: 12, zIndex: 8,
      width: 44, height: 44, borderRadius: 9999, border: `1.5px solid ${visitorTheme.mapControlLine}`,
      background: visitorTheme.mapGlass52,
      backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
      boxShadow: `0 2px 8px ${visitorTheme.scrim11}`, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'bottom 0.3s ease, transform 0.1s ease',
    }} {...pressScaleHandlers}>
      {cardsVisible
        ? <ChevronDown style={{ width: 20, height: 20, color: visitorTheme.meta }} />
        : <ChevronRight style={{ width: 20, height: 20, color: visitorTheme.meta, transform: 'rotate(-90deg)' }} />}
    </button>}

    {/* Card carousel */}
    {/* `inert` is applied the INSTANT the drawer folds, and carries no transition; the delayed
        `visibility` below cannot do this job on its own, because it stays `visible` for the whole
        0.3s slide-out and a keyboard user who presses the toggle and then Tab lands inside the
        drawer during it (cold review, #434 — measured focusable at t+0/50/120/200/290ms).
        `inert: ''` rather than `inert={true}`: React 18.3 does not treat it as a boolean
        attribute and would warn, while the empty string renders the correct HTML form. */}
    {visibleCards.length > 0 && <div ref={carouselRef} className="map-carousel" {...(cardsVisible ? {} : { inert: '' })} style={{ position: 'absolute', bottom: mapCarouselBottomCss, left: 0, right: 0, zIndex: 10, display: 'flex', gap: 10, overflowX: 'auto', overflowY: 'visible', scrollSnapType: 'x mandatory', padding: mapCarouselPadding, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', transform: cardsVisible ? 'translateY(0)' : `translateY(calc(100% + ${mapCarouselBottom}px + env(safe-area-inset-bottom)))`,
      // `pointer-events: none` stops the mouse and the finger and NOTHING else — the folded cards
      // stayed in the accessibility tree and in the tab order, so Enter on an off-screen heart
      // really saved a favourite (#434). `visibility: hidden` is what removes a subtree from
      // both. The delayed visibility transition keeps the slide-out animation: it flips to
      // `hidden` only once the 0.3s transform has carried the drawer off the screen, and back to
      // `visible` with no delay on the way in.
      visibility: cardsVisible ? 'visible' : 'hidden',
      transition: cardsVisible ? 'transform 0.3s ease' : 'transform 0.3s ease, visibility 0s linear 0.3s',
      pointerEvents: cardsVisible ? 'auto' : 'none' }}>
      {visibleCards.map(loc => <div key={`${loc.type}-${loc.id}`} data-testid="map-carousel-card" style={{ flexShrink: 0, width: 'calc(100vw - 80px)', maxWidth: 340, height: mapCardHeight, borderRadius: 20, overflow: 'hidden', position: 'relative', scrollSnapAlign: 'center', boxShadow: `0 16px 32px ${mapPalette.scrim30}` }}>
        <MapCardImage src={loc.image} category={loc.category} name={displayName(loc, isRTL)} caption={displayPhotoCaption(loc, isRTL)} attribution={displayPhotoAttribution(loc, isRTL)} isRTL={isRTL} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        <div data-testid="map-carousel-text-scrim" data-scrim-start={mapPalette.scrim70} data-scrim-mid={mapPalette.scrim30} data-scrim-end={mapPalette.scrim08} style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${mapPalette.scrim70} 0%, ${mapPalette.scrim30} 56%, ${mapPalette.scrim08} 100%)` }} />
        <button type="button" aria-label={t(`Open ${displayName(loc, false)}`, `افتح ${displayName(loc, true)}`)} onClick={() => { loc.type === 'place' ? openPlaceDetail(loc) : setSelBiz(loc); }} style={{ position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }} />
        <button type="button" aria-label={isFav(loc.id, loc.type === 'place' ? 'place' : 'business') ? t('Remove place from saved trip', 'إزالة المكان من الرحلة المحفوظة') : t('Save place to trip', 'حفظ المكان في الرحلة')} aria-pressed={isFav(loc.id, loc.type === 'place' ? 'place' : 'business')} onClick={e => { e.stopPropagation(); toggleFav(loc.id, loc.type === 'place' ? 'place' : 'business'); }} style={{ position: 'absolute', top: 8, [isRTL ? 'left' : 'right']: 8, zIndex: 2, width: 44, height: 44, borderRadius: 9999, border: 'none', cursor: 'pointer', background: visitorTheme.glass20, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Heart style={{ width: 16, height: 16, color: visitorTheme.onPhoto, fill: isFav(loc.id, loc.type === 'place' ? 'place' : 'business') ? visitorTheme.onPhoto : 'none' }} />
        </button>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 16, textAlign: isRTL ? 'right' : 'left' }}>
          <h3 style={{ color: visitorTheme.onPhoto, fontWeight: 700, fontSize: 17, lineHeight: 1.3, marginBottom: 5 }}>{displayName(loc, isRTL)}</h3>
          <p style={{ color: visitorTheme.glass80, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ letterSpacing: 0, color: visitorTheme.glass80 }}>{displayCategoryLabel(loc.category, isRTL)}</span>
            <span style={{ color: visitorTheme.glass40 }}>·</span>
            <MapPin style={{ width: 10, height: 10 }} />{displayVillage(loc, isRTL)}
            {loc.rating > 0 ? <><Star style={{ width: 10, height: 10, color: visitorTheme.star, fill: visitorTheme.star, marginLeft: 4 }} /><span>{loc.rating}</span></> : null}
          </p>
        </div>
        {selectedMarker && selectedMarker.id === loc.id && selectedMarker.type === loc.type && <div style={{ position: 'absolute', inset: 0, borderRadius: 20, border: `2px solid ${visitorTheme.onPhoto}`, pointerEvents: 'none' }} />}
      </div>)}
    </div>}
    {/* Values, not var(): `@keyframes` are a global at-rule wherever the <style> element sits, so
        the palette is interpolated per mode at render time instead. */}
    <style>{`.map-screen { overflow: hidden; overflow: clip; } .map-carousel::-webkit-scrollbar { display: none; } @keyframes geoPulse { 0%,100% { box-shadow: 0 0 0 0 ${mapPalette.geoPulse}; } 50% { box-shadow: 0 0 0 10px ${mapPalette.geoPulseFade}; } } @keyframes homePulse { 0%,100% { box-shadow: ${mapPalette.shadowMapControl}, 0 0 0 0 ${mapPalette.homePulse}; } 50% { box-shadow: ${mapPalette.shadowMapControl}, 0 0 0 8px ${mapPalette.homePulseFade}; } } ${MARKER_TIER_KEYFRAMES} .map-screen .gm-style > div:last-child { bottom: 56px !important; }`}</style>
  </div>;

  // FavsScreen state lifted to parent to prevent remount flicker
  const allSaved = React.useMemo(
    () => getFavoritedCatalogItems({ places: publicDisplayPlaces, businesses: publicDisplayBusinesses }, favs),
    [publicDisplayPlaces, publicDisplayBusinesses, favs]
  );
  const favsEmpty = allSaved.length === 0;
  const favsTotalCount = allSaved.length;
  const favsGroups = React.useMemo(() => {
    const catOrder = CATEGORIES.map(c => c.id);
    const g = {};
    allSaved.forEach(i => { if (!g[i.category]) g[i.category] = []; g[i.category].push(i); });
    const ordered = {};
    catOrder.forEach(c => { if (g[c]) ordered[c] = g[c]; });
    Object.keys(g).forEach(c => { if (!ordered[c]) ordered[c] = g[c]; });
    return ordered;
  }, [allSaved]);
  const favsVillageCount = React.useMemo(() => new Set(allSaved.map(i => i.village).filter(Boolean)).size, [allSaved]);
  const favsCategoryCount = Object.keys(favsGroups).length;
  const favsVillageLabel = favsVillageCount === 1 ? t('village', 'قرية') : t('villages', 'قرى');
  const savedAskPlanCount = savedAskPlans.plans.length;
  const tripEmpty = favsEmpty && savedAskPlanCount === 0;

  const shareTrip = async () => {
    const tripSource = [...allSaved, ...savedAskPlans.plans.flatMap(record => record.stops || [])];
    let text = `${t(`My ${displayCazaName(tripSource, false)} Trip`, `رحلتي في ${displayCazaName(tripSource, true)}`)}:\n\n`;
    savedAskPlans.plans.forEach(record => {
      text += `${t('Plan', 'خطة')}: ${localizedAskZghartaText(record.plan.title, lang)}\n`;
      record.stops.forEach((stop, index) => {
        const stopTitle = localizedAskZghartaText(stop.title, lang) || displayCazaStopFallback(stop, isRTL);
        const stopVillage = stop.village ? displayStandaloneVillage(stop.village, isRTL) : '';
        text += `${index + 1}. ${stopTitle}${stopVillage ? ` — ${stopVillage}` : ''}\n`;
      });
      text += '\n';
    });
    allSaved.forEach(i => { text += `${catEmoji[i.category] || '📍'} ${displayName(i, isRTL)} — ${displayVillage(i, isRTL)}\n`; });
    text += `\n${t(`${displayCazaName(tripSource, false)} field guide, North Lebanon.`, `دليل ميداني ل${displayCazaName(tripSource, true)}، شمال لبنان.`)}`;
    if (navigator.share) { try { await navigator.share({ title: t(`My ${displayCazaShort(tripSource, false)} Trip`, `رحلتي في ${displayCazaShort(tripSource, true)}`), text }); } catch {} }
    else { try { await navigator.clipboard.writeText(text); alert(t('Copied to clipboard!', 'تم النسخ!')); } catch {} }
  };

  const viewAllOnMap = () => { setMapVillageFilter([]); setZoneId(''); setTab('map'); };

  const openSavedItem = item => { item.type === 'place' ? openPlaceDetail(item) : setSelBiz(item); };

  const FavsScreen = () => <div style={{ ...screenContainer, background: premiumTheme.colors.paper }}>
      <div style={{ ...stickyHeader, background: premiumTheme.colors.paper, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none' }}>
        <div style={{ padding: '24px 20px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <div style={{ minWidth: 0, flexBasis: '100%', textAlign: isRTL ? 'right' : 'left' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 0', color: premiumTheme.colors.accent, marginBottom: 4 }}>
              <ListChecks style={{ width: 12, height: 12, color: premiumTheme.colors.accent, flexShrink: 0 }} />
              <span style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent, fontSize: 10 })}>{t('Trip folio', 'ملف الرحلة')}</span>
            </div>
            <h1 style={{ fontSize: 34, fontWeight: 700, color: visitorTheme.title, lineHeight: 1.02, marginBottom: 2, overflowWrap: 'anywhere' }}>{t('Saved', 'المحفوظات')}</h1>
            {!tripEmpty && <p style={{ fontSize: 12, lineHeight: 1.35, color: visitorTheme.warmMeta, margin: 0, fontWeight: 700 }}>{favsTotalCount} {t('saved stops', 'محطات محفوظة')} · {savedAskPlanCount} {t('plans', 'خطط')}</p>}
          </div>
          {!tripEmpty && <div style={{ display: 'flex', gap: 7, flexShrink: 0, flexWrap: 'wrap', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
            <button onClick={viewAllOnMap} aria-label={t('View saved places on map', 'عرض المحفوظات على الخريطة')} style={{ minHeight: 44, padding: '7px 10px', background: visitorTheme.ghostSurface, borderRadius: 9999, border: `1px solid ${visitorTheme.accent18}`, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: visitorTheme.chipInk, display: 'flex', alignItems: 'center', gap: 5, boxShadow: 'none' }}><MapIcon style={{ width: 13, height: 13, color: visitorTheme.accent }} />{t('Map', 'خريطة')}</button>
            <button onClick={shareTrip} aria-label={t('Share saved trip', 'مشاركة الرحلة المحفوظة')} style={{ minHeight: 44, padding: '7px 10px', background: visitorTheme.accent, borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: visitorTheme.onAccent, display: 'flex', alignItems: 'center', gap: 5, boxShadow: 'none' }}><Share2 style={{ width: 13, height: 13 }} />{t('Share', 'مشاركة')}</button>
          </div>}
        </div>
      </div>
      {savedAskPlanCount > 0 && <section style={{ padding: '16px 16px 6px', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <div>
            <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent, marginBottom: 4 })}>{t('Trip plans', 'خطط الرحلة')}</p>
            <h2 style={{ fontFamily: fontFor(isRTL, 'display'), fontSize: 24, lineHeight: 1.05, color: premiumTheme.colors.ink }}>{t('Saved plans', 'الخطط المحفوظة')}</h2>
          </div>
          <span style={{ padding: '6px 10px', borderRadius: 9999, background: visitorTheme.accentTint, color: premiumTheme.colors.accent, fontSize: 12, fontWeight: 800 }}>{savedAskPlanCount}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {savedAskPlans.plans.map(record => {
            const firstItem = record.plan.sections?.flatMap(section => section.items || [])[0];
            const stopsCount = record.stops.length;
            const hasMapTarget = record.stops.some(stop => stop.ref?.type === 'place' || stop.ref?.type === 'business');
            return <article key={record.id} onClick={() => firstItem && openAskPlanItem(firstItem)} style={{ borderRadius: 18, background: visitorTheme.fallbackWash, border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: `0 12px 26px ${visitorTheme.shade06}`, padding: 14, cursor: firstItem ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <div style={{ width: 42, height: 42, borderRadius: 14, background: visitorTheme.accentTintStrong, color: premiumTheme.colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Sparkles style={{ width: 20, height: 20 }} /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h3 style={{ fontFamily: fontFor(isRTL, 'display'), fontSize: 21, lineHeight: 1.08, color: premiumTheme.colors.ink, marginBottom: 5 }}>{localizedAskZghartaText(record.plan.title, lang)}</h3>
                  <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 13, lineHeight: 1.45, marginBottom: 8 }}>{localizedAskZghartaText(record.plan.summary, lang)}</p>
                  <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent })}>{stopsCount} {t('stops', 'محطات')}</p>
                </div>
              </div>
              {record.stops.length > 0 && <ol style={{ margin: '11px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {record.stops.slice(0, 3).map((stop, index) => <li key={stop.itemId} data-testid="saved-plan-stop" style={{ display: 'flex', gap: 8, alignItems: 'center', color: premiumTheme.colors.ink, fontSize: 13, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 9999, background: visitorTheme.accentTint, color: premiumTheme.colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{index + 1}</span>
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1, alignItems: isRTL ? 'flex-end' : 'flex-start' }}>
	                    <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{localizedAskZghartaText(stop.title, lang) || displayCazaStopFallback(stop, isRTL)}</span>
	                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
	                      {stop.village && <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: premiumTheme.colors.mutedInk, fontSize: 11, fontWeight: 600 }}>{displayStandaloneVillage(stop.village, isRTL)}</span>}
	                    </span>
                  </span>
                </li>)}
              </ol>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                {hasMapTarget && <button onClick={(ev) => { ev.stopPropagation(); viewAskPlanRecordOnMap(record); }} style={{ minHeight: 44, padding: '8px 11px', borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: premiumTheme.colors.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}><MapIcon style={{ width: 13, height: 13 }} />{t('Map', 'خريطة')}</button>}
                <button onClick={(ev) => { ev.stopPropagation(); shareAskPlanRecord(record); }} style={{ minHeight: 44, padding: '8px 11px', borderRadius: 9999, border: `1px solid ${premiumTheme.colors.warmLine}`, background: premiumTheme.colors.surface, color: premiumTheme.colors.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}><Share2 style={{ width: 13, height: 13 }} />{t('Share', 'مشاركة')}</button>
                <button onClick={(ev) => { ev.stopPropagation(); removeAskPlan(record.id); }} style={{ minHeight: 44, padding: '8px 11px', borderRadius: 9999, border: `1px solid ${visitorTheme.removeLine}`, background: visitorTheme.removeBg, color: visitorTheme.removeInk, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}><X style={{ width: 13, height: 13 }} />{t('Remove', 'إزالة')}</button>
              </div>
            </article>;
          })}
        </div>
      </section>}
      {tripEmpty ? <div style={{ padding: '32px 20px 28px' }}>
        <div style={{ background: 'transparent', border: 'none', borderRadius: 0, padding: '20px 0', textAlign: isRTL ? 'right' : 'left' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: visitorTheme.accentTint, color: premiumTheme.colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <Bookmark style={{ width: 23, height: 23 }} />
          </div>
          <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent, marginBottom: 5 })}>{t('Saved places', 'الأماكن المحفوظة')}</p>
          <h2 style={{ color: premiumTheme.colors.ink, fontSize: 23, lineHeight: 1.08, fontWeight: 700, marginBottom: 8 }}>{t('No saved stops yet', 'لا توجد محطات محفوظة بعد')}</h2>
          <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 14, lineHeight: 1.55, marginBottom: 16 }}>{t('Open the directory or ask for a route, then save the places you want to keep.', 'افتح الدليل أو اطلب مسارا، ثم احفظ الأماكن التي تريد الاحتفاظ بها.')}</p>
          <button onClick={() => setTab('explore')} style={{ minHeight: 44, padding: '10px 14px', background: visitorTheme.accent, color: visitorTheme.onAccent, border: 'none', borderRadius: 9999, cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>{t('Open directory', 'افتح الدليل')}<ChevronRight style={{ width: 16, height: 16, transform: isRTL ? 'rotate(180deg)' : 'none' }} /></button>
        </div>
      </div> : <div style={{ padding: '16px 16px 28px' }}>
        {allSaved.length > 0 && <div style={{ padding: '4px 0 24px', marginBottom: 24, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, textAlign: isRTL ? 'right' : 'left' }}>
          <h2 style={{ fontSize: 25, lineHeight: 1.2, fontWeight: 600, color: premiumTheme.colors.ink, marginBottom: 8 }}>{t(`Your ${displayCazaShort(allSaved, false)} folio`, `ملف رحلتك في ${displayCazaShort(allSaved, true)}`)}</h2>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: premiumTheme.colors.mutedInk }}>{t('Keep your places together. Open a stop for details, or see your saved places on the map.', 'احتفظ بأماكنك معا. افتح محطة للتفاصيل، أو اعرض أماكنك المحفوظة على الخريطة.')}</p>
        </div>}
        {Object.entries(favsGroups).map(([cat, items], groupIndex) => {
          const category = CATEGORIES.find(c => c.id === cat);
          const CatI = catIcons[cat] || MapPin;
          const groupInk = categoryInkVars(cat);
          const catColor = groupInk.ink;
          const catBg = groupInk.chipBg;
          const catLabel = category ? t(category.labelEn, category.labelAr) : cat;
          return <section key={cat} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <div style={{ width: 40, height: 40, borderRadius: 14, background: catBg, border: `1px solid ${groupInk.chipLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}><CatI style={{ width: 18, height: 18, color: catColor }} /></div>
                <div style={{ minWidth: 0, textAlign: isRTL ? 'right' : 'left' }}>
                  <p style={{ fontSize: 11, color: visitorTheme.pastFaint, fontWeight: 700, letterSpacing: 0, textTransform: 'uppercase' }}>{t('Section', 'قسم')} {String(groupIndex + 1).padStart(2, '0')}</p>
                  <h3 style={{ fontSize: 18, color: visitorTheme.title, fontWeight: 700, lineHeight: 1.1 }}>{catLabel}</h3>
                </div>
              </div>
              <span style={{ flexShrink: 0, padding: '6px 10px', borderRadius: 9999, background: visitorTheme.glass86, border: `1px solid ${visitorTheme.warm12}`, color: visitorTheme.warmMeta, fontSize: 12, fontWeight: 700 }}>{items.length} {t('saved', 'محفوظ')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map(i => <article key={`${i.type}-${i.id}`} onClick={() => openSavedItem(i)} style={{ background: premiumTheme.colors.surface, borderRadius: 20, overflow: 'hidden', border: `1px solid ${premiumTheme.colors.warmLine}`, boxShadow: 'none', cursor: 'pointer' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isRTL ? '1fr 104px' : '104px 1fr', minHeight: 136 }}>
                  <div style={{ position: 'relative', minHeight: 136, order: isRTL ? 2 : 0 }}>
                    <PlaceImage src={i.image} category={i.category} name={displayName(i, isRTL)} caption={displayPhotoCaption(i, isRTL)} attribution={displayPhotoAttribution(i, isRTL)} compact isRTL={isRTL} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                  </div>
                  <div style={{ minWidth: 0, padding: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10, textAlign: isRTL ? 'right' : 'left' }}>
                    <div style={{ minWidth: 0 }}>
                      <h4 style={{ fontWeight: 700, color: visitorTheme.title, fontSize: 16, lineHeight: 1.18, marginBottom: 6, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{displayName(i, isRTL)}</h4>
                      <p style={{ minWidth: 0, fontSize: 12, color: visitorTheme.warmMeta, display: 'flex', alignItems: 'center', gap: 4, justifyContent: isRTL ? 'flex-end' : 'flex-start', lineHeight: 1.3 }}><MapPin style={{ width: 12, height: 12, flexShrink: 0, color: visitorTheme.accent }} /><span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayVillage(i, isRTL)}</span></p>
                    </div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <button onClick={(ev) => { ev.stopPropagation(); openSavedItem(i); }} style={{ minHeight: 44, padding: '8px 12px', borderRadius: 9999, border: `1px solid ${visitorTheme.accent18}`, background: visitorTheme.ghostSurface, color: visitorTheme.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>{t('Open', 'افتح')}<ChevronRight style={{ width: 14, height: 14, transform: isRTL ? 'rotate(180deg)' : 'none' }} /></button>
                      <button onClick={(ev) => { ev.stopPropagation(); toggleFav(i.id, i.type === 'place' ? 'place' : 'business'); }} aria-label={t('Remove from saved', 'إزالة من المحفوظات')} style={{ minHeight: 44, padding: '8px 10px', borderRadius: 9999, border: `1px solid ${visitorTheme.removeLine}`, background: visitorTheme.removeBg, color: visitorTheme.removeInk, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}><X style={{ width: 14, height: 14 }} />{t('Remove', 'إزالة')}</button>
                    </div>
                  </div>
                </div>
              </article>)}
            </div>
          </section>;
        })}
      </div>}
    </div>;

  const PlaceDetailScreen = ({ place: p, onBack }) => {
    const [failedPhotoSrcs, setFailedPhotoSrcs] = useState([]);
    const nearby = getNearby(p.coordinates, p.id);
    const CatI = catIcons[p.category] || MapPin;
    const catColor = categoryInkVars(p.category).ink;
    const categoryLabel = getCategoryDisplay(p.category);
    const saved = isFav(p.id, 'place');
    const placeName = displayName(p, isRTL);
    const placeVillage = displayVillage(p, isRTL);
    const editorNote = displayEditorNote(p, isRTL);
    const body = displayDescription(p, isRTL);
    const showPhotoSourceDisclaimer = hasPendingOrGoogleDisplayMedia(p);
    const hasPlaceCoordinates = p.coordinates?.lat != null && p.coordinates?.lng != null;
    const displayPhotos = getCatalogDisplayPhotos(p);
    const usablePhotos = displayPhotos.filter(photo => !failedPhotoSrcs.includes(photo.src));
    const primaryPhoto = usablePhotos[0] || { src: '' };
    const secondaryPhotos = usablePhotos.slice(1, 5);
    const dataRows = [
      { label: t('Hours', 'الساعات'), render: <OpenHoursStack openHours={p.openHours} fallbackLabel={t('Hours vary', 'تختلف الساعات')} isRTL={isRTL} /> },
      { label: t('Cash / card', 'نقد / بطاقة'), value: t('Confirm locally', 'تحقق محليا') },
    ];

    return <main style={{ minHeight: '100vh', background: premiumTheme.colors.paper, direction: isRTL ? 'rtl' : 'ltr', fontFamily: fontFor(isRTL, 'body'), color: premiumTheme.colors.ink, paddingBottom: 34 }}>
      <div style={{ position: 'relative' }}>
        <PlaceImage src={primaryPhoto.src} category={p.category} name={placeName} caption={primaryPhoto.caption || displayPhotoCaption(p, isRTL)} attribution={displayPhotoAttribution(p, isRTL, primaryPhoto)} isRTL={isRTL} style={{ width: '100%', height: 288, border: 'none' }} onPhotoUnavailable={() => setFailedPhotoSrcs(current => [...current, primaryPhoto.src])} />
        <button aria-label={t('Back', 'رجوع')} onClick={onBack} style={{ position: 'absolute', top: 16, [isRTL ? 'right' : 'left']: 16, ...modalStyles.heroButton }}><ArrowLeft style={{ width: 20, height: 20, color: premiumTheme.colors.ink, transform: isRTL ? 'rotate(180deg)' : 'none' }} /></button>
        <button aria-label={t('Share', 'مشاركة')} onClick={() => shareLoc(placeName, placeVillage, p.coordinates, p)} style={{ position: 'absolute', top: 16, [isRTL ? 'left' : 'right']: 16, ...modalStyles.heroButton }}><Share2 style={{ width: 18, height: 18, color: premiumTheme.colors.ink }} /></button>
      </div>
      <div style={{ padding: '16px 22px 0', textAlign: isRTL ? 'right' : 'left' }}>
        <PhotoCredit attribution={displayPhotoAttribution(p, isRTL, primaryPhoto)} isRTL={isRTL} />
        <h1 style={{ fontFamily: fontFor(isRTL, 'display'), fontSize: 32, lineHeight: 1.12, fontWeight: 700, letterSpacing: 0, color: premiumTheme.colors.ink, marginBottom: 12, overflowWrap: 'anywhere' }}>{placeName}</h1>
        <p style={{ fontFamily: fontFor(isRTL, 'display'), color: premiumTheme.colors.mutedInk, fontSize: 17, lineHeight: 1.55, marginBottom: 16 }}>{editorNote}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: `1px solid ${premiumTheme.colors.warmLine}`, borderBottom: `1px solid ${premiumTheme.colors.warmLine}`, padding: '12px 0', marginBottom: 18, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: catColor, fontSize: 13, fontWeight: 500 }}><CatI style={{ width: 14, height: 14 }} />{categoryLabel}</span>
          <span style={{ color: premiumTheme.colors.hairline }}>·</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: premiumTheme.colors.mutedInk, fontSize: 13 }}><MapPin style={{ width: 13, height: 13 }} />{placeVillage}</span>
          <CazaAreaBadge source={p} isRTL={isRTL} />
        </div>

        <div style={modalStyles.actionPanel}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(138px, 1fr))', gap: 10 }}>
            <button onClick={() => toggleFav(p.id, 'place')} style={{ ...primaryBtn, background: saved ? premiumTheme.colors.ink : premiumTheme.colors.accent }}>
              {saved ? <Check style={{ width: 18, height: 18 }} /> : <Plus style={{ width: 18, height: 18 }} />}
              {saved ? t('In my trip', 'ضمن رحلتي') : t('Add to my trip', 'أضف إلى رحلتي')}
            </button>
            {hasPlaceCoordinates ? <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.coordinates.lat},${p.coordinates.lng}`, '_blank')} style={secondaryBtn}><Navigation style={{ width: 18, height: 18 }} />{t('Directions', 'الاتجاهات')}</button> : <button onClick={() => shareLoc(placeName, placeVillage, p.coordinates, p)} style={secondaryBtn}><Share2 style={{ width: 18, height: 18 }} />{t('Share', 'مشاركة')}</button>}
          </div>
          {hasPlaceCoordinates && <button onClick={showOnMap} style={{ ...secondaryBtn, width: '100%', marginTop: 10 }}><MapIcon style={{ width: 18, height: 18 }} />{t('Show on map', 'على الخريطة')}</button>}
        </div>

        {showPhotoSourceDisclaimer && <p data-testid="place-photo-source-disclaimer" style={{ margin: '0 0 20px', color: premiumTheme.colors.mutedInk, fontSize: 12, lineHeight: 1.5 }}>{photoSourceDisclaimer(isRTL)}</p>}

        <section style={{ marginBottom: 24 }}>
          {dataRows.map((row, idx) => <div key={row.label} style={{ padding: '13px 0', borderBottom: idx < dataRows.length - 1 ? `1px solid ${premiumTheme.colors.warmLine}` : 'none', display: 'grid', gridTemplateColumns: '108px minmax(0, 1fr)', gap: 14, alignItems: row.render ? 'start' : 'baseline', minWidth: 0 }}>
            <p style={smallCapsStyle(isRTL, { color: premiumTheme.colors.mutedInk, fontSize: 12, textTransform: 'none' })}>{row.label}</p>
            {row.render ? <div style={{ minWidth: 0 }}>{row.render}</div> : <p style={{ minWidth: 0, fontSize: 15, lineHeight: 1.45, color: premiumTheme.colors.ink, fontWeight: 400, overflowWrap: 'anywhere' }}>{row.value}</p>}
          </div>)}
        </section>

        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: fontFor(isRTL, 'display'), fontSize: 26, fontWeight: 700, letterSpacing: 0, marginBottom: 10 }}>{t('Why go', 'لماذا تذهب')}</h2>
          <p style={{ fontSize: 17, lineHeight: 1.68, color: premiumTheme.colors.ink, marginBottom: 16 }}>{body}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
            {getEditorialLabels(p, isRTL).map(label => <span key={label} style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent })}>{label}</span>)}
          </div>
        </section>

        <section style={{ marginBottom: 24 }}>
          {secondaryPhotos.length > 0 ? <div data-testid="place-detail-photo-gallery" style={{ display: 'grid', gridTemplateColumns: secondaryPhotos.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 10 }}>
            {secondaryPhotos.map((photo, index) => <div key={`${photo.src}-${index}`}>
              <PlaceImage src={photo.src} category={p.category} name={placeName} caption={photo.caption || displayPhotoCaption(p, isRTL)} attribution={displayPhotoAttribution(p, isRTL, photo)} isRTL={isRTL} style={{ width: '100%', height: 144, border: `1px solid ${premiumTheme.colors.warmLine}` }} />
              <PhotoCredit attribution={displayPhotoAttribution(p, isRTL, photo)} isRTL={isRTL} />
            </div>)}
          </div> : <PlaceImage src={primaryPhoto.src} category={p.category} name={placeName} caption={primaryPhoto.caption || displayPhotoCaption(p, isRTL)} attribution={displayPhotoAttribution(p, isRTL, primaryPhoto)} isRTL={isRTL} style={{ width: '100%', minHeight: 178, border: `1px solid ${premiumTheme.colors.warmLine}` }} />}
        </section>

        {renderNearbySection(nearby)}
      </div>
    </main>;
  };

  const BizModal = ({ business: b }) => {
    const [failedPhotoSrcs, setFailedPhotoSrcs] = useState([]);
    const nearby = getNearby(b.coordinates, b.id);
    const CatI = catIcons[b.category] || MapPin;
    const bizInk = categoryInkVars(b.category);
    const catColor = bizInk.ink;
    const catBg = bizInk.chipBg;
    const categoryLabel = getCategoryDisplay(b.category);
    const businessName = displayName(b, isRTL);
    const businessVillage = displayVillage(b, isRTL);
    const businessDescription = displayDescription(b, isRTL);
    const businessSpecialties = (b.specialties || []).filter(specialty => textMatchesActiveLanguage(specialty, isRTL));
    // Businesses carry opening hours exactly as places do, and the detail page is where a
    // visitor decides whether to set out. Rendered only when the row actually holds hours —
    // an empty tile saying nothing is worse than no tile.
    const businessHours = String(b.openHours || '').trim();
    const hasPrimaryActions = b.phone || b.website;
    const hasMapActions = hasUsableCoordinates(b);
    const displayPhotos = getCatalogDisplayPhotos(b);
    const usablePhotos = displayPhotos.filter(photo => !failedPhotoSrcs.includes(photo.src));
    const primaryPhoto = usablePhotos[0] || { src: '' };
    const secondaryPhotos = usablePhotos.slice(1, 5);
    return <>
      <div style={{ position: 'relative', height: 288 }}>
        <PlaceImage src={primaryPhoto.src} category={b.category} name={displayName(b, isRTL)} caption={primaryPhoto.caption || displayPhotoCaption(b, isRTL)} attribution={displayPhotoAttribution(b, isRTL, primaryPhoto)} isRTL={isRTL} style={{ width: '100%', height: '100%' }} onPhotoUnavailable={() => setFailedPhotoSrcs(current => [...current, primaryPhoto.src])} />
        <div style={{ position: 'absolute', top: 16, [isRTL ? 'left' : 'right']: 16, display: 'flex', gap: 8 }}>
          <button aria-label={t('Share', 'مشاركة')} onClick={() => shareLoc(businessName, businessVillage, b.coordinates, b)} style={modalStyles.heroButton}><Share2 style={{ width: 18, height: 18, color: visitorTheme.title }} /></button>
          <button aria-label={isFav(b.id, 'business') ? t('Remove from trip', 'إزالة من الرحلة') : t('Add to trip', 'أضف إلى الرحلة')} onClick={() => toggleFav(b.id, 'business')} style={{ ...modalStyles.heroButton, background: isFav(b.id, 'business') ? premiumTheme.colors.ink : modalStyles.heroButton.background, borderColor: isFav(b.id, 'business') ? premiumTheme.colors.ink : modalStyles.heroButton.border }}><Bookmark style={{ width: 20, height: 20, color: isFav(b.id, 'business') ? visitorTheme.onInk : visitorTheme.title, fill: isFav(b.id, 'business') ? visitorTheme.onInk : 'none' }} /></button>
        </div>
        <div style={{ position: 'absolute', bottom: 16, [isRTL ? 'right' : 'left']: 16, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
          <span style={modalStyles.heroChip}><CatI style={{ width: 14, height: 14 }} />{categoryLabel}</span>
          {b.priceRange && <span style={modalStyles.heroChip}>{b.priceRange}</span>}
        </div>
      </div>
      <div style={{ ...modalStyles.content, textAlign: isRTL ? 'right' : 'left' }}>
        <PhotoCredit attribution={displayPhotoAttribution(b, isRTL, primaryPhoto)} isRTL={isRTL} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
          {businessVillage && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%', padding: '6px 0', color: premiumTheme.colors.mutedInk, fontSize: 12, fontWeight: 700 }}><MapPin style={{ width: 13, height: 13, flexShrink: 0 }} /><span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{businessVillage}</span></span>}
          <CazaAreaBadge source={b} isRTL={isRTL} />
          {b.verified && <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 9999, background: visitorTheme.accentTintStrong, color: visitorTheme.accent, fontSize: 12, fontWeight: 700 }}>{t('Verified', 'موثق')}</span>}
        </div>
        <h1 id="business-detail-title" style={modalStyles.title}>{businessName}</h1>
        <AreaSummary item={b} isRTL={isRTL} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
          {/* The accent, not `catColor`, and now the same treatment PlaceDetailScreen already
              gives its own editorial labels. A raw category hue is not theme surface, so it does
              not follow the palette: #dc2626 reads 4.83:1 on the light modal paper and only
              3.70:1 on the dark one — the one place where the theme, and not a pre-existing
              category-hue shortfall, made a text node fail AA (browser QA, #423). No signal is
              lost: the category badge three lines above still carries the hue, and the owner is
              colourblind, so the tint was never the cue (#421 D7). */}
          {getEditorialLabels(b, isRTL).map(label => <span key={label} style={smallCapsStyle(isRTL, { color: premiumTheme.colors.accent })}>{label}</span>)}
        </div>
        {(hasPrimaryActions || hasMapActions) && <div style={modalStyles.actionPanel}>
          {hasPrimaryActions && <div style={{ display: 'grid', gridTemplateColumns: b.phone && b.website ? 'repeat(auto-fit, minmax(138px, 1fr))' : '1fr', gap: 10, marginBottom: hasMapActions ? 10 : 0 }}>
            {b.phone && <a href={`tel:${b.phone}`} style={primaryBtn}><Phone style={{ width: 18, height: 18 }} />{t('Call', 'اتصل')}</a>}
            {b.website && <a href={b.website} target="_blank" rel="noopener noreferrer" style={secondaryBtn}><Globe style={{ width: 18, height: 18 }} />{t('Website', 'الموقع')}</a>}
          </div>}
          {hasMapActions && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(138px, 1fr))', gap: 10 }}>
            <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${b.coordinates.lat},${b.coordinates.lng}`, '_blank')} style={secondaryBtn}><Navigation style={{ width: 18, height: 18 }} />{t('Directions', 'الاتجاهات')}</button>
            <button onClick={() => showOnMap()} style={secondaryBtn}><MapIcon style={{ width: 18, height: 18 }} />{t('On Map', 'الخريطة')}</button>
          </div>}
          {hasMapActions && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.name + ' ' + b.village + ' Lebanon')}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 11, minHeight: 44, padding: '11px 0', background: 'transparent', color: premiumTheme.colors.mutedInk, border: 'none', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
            <ExternalLink style={{ width: 14, height: 14 }} />{t('View Google reviews', 'عرض تقييمات غوغل')}
          </a>}
        </div>}

        {businessDescription && <p style={modalStyles.bodyText}>{businessDescription}</p>}
        {secondaryPhotos.length > 0 && <section data-testid="business-detail-photo-gallery" style={{ display: 'grid', gridTemplateColumns: secondaryPhotos.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 10, marginBottom: 18 }}>
          {secondaryPhotos.map((photo, index) => <div key={`${photo.src}-${index}`}>
            <PlaceImage src={photo.src} category={b.category} name={businessName} caption={photo.caption || displayPhotoCaption(b, isRTL)} attribution={displayPhotoAttribution(b, isRTL, photo)} isRTL={isRTL} style={{ width: '100%', height: 132, border: `1px solid ${premiumTheme.colors.warmLine}` }} />
            <PhotoCredit attribution={displayPhotoAttribution(b, isRTL, photo)} isRTL={isRTL} />
          </div>)}
        </section>}
        {(b.priceRange || hasMapActions || businessHours || businessSpecialties.length > 0) && <div style={modalStyles.metaPanel}>
          {(b.priceRange || hasMapActions) && <div style={{ display: 'grid', gridTemplateColumns: b.priceRange && hasMapActions ? '1fr 1fr' : '1fr', gap: 10 }}>
            {b.priceRange && <div style={modalStyles.metaTile}><ShoppingBag style={{ width: 20, height: 20, color: catColor, marginBottom: 8 }} /><p style={{ fontSize: 12, color: premiumTheme.colors.mutedInk, fontWeight: 500, marginBottom: 3 }}>{t('Price', 'السعر')}</p><p style={{ fontSize: 15, fontWeight: 700, color: visitorTheme.title }}>{b.priceRange}</p></div>}
            {hasMapActions && <div style={modalStyles.metaTile}><Navigation style={{ width: 20, height: 20, color: catColor, marginBottom: 8 }} /><p style={{ fontSize: 12, color: premiumTheme.colors.mutedInk, fontWeight: 500, marginBottom: 3 }}>{t('Location', 'الموقع')}</p><p style={{ fontSize: 14, fontWeight: 700, color: visitorTheme.title, lineHeight: 1.35 }}>{b.coordinates.lat.toFixed(4)}, {b.coordinates.lng.toFixed(4)}</p></div>}
          </div>}
          {businessHours && <div data-testid="business-hours-tile" style={modalStyles.metaTile}>
            <Clock style={{ width: 20, height: 20, color: catColor, marginBottom: 8 }} />
            <p style={{ fontSize: 12, color: premiumTheme.colors.mutedInk, fontWeight: 500, marginBottom: 6 }}>{t('Hours', 'الساعات')}</p>
            <OpenHoursStack openHours={businessHours} fallbackLabel={t('Hours vary', 'تختلف الساعات')} isRTL={isRTL} />
          </div>}
          {businessSpecialties.length > 0 && <div style={modalStyles.metaTile}>
            <h3 style={{ ...modalStyles.sectionTitle, fontSize: 15, marginBottom: 10 }}>{t('Specialties', 'التخصصات')}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
              {businessSpecialties.map((s, i) => <span key={i} style={{ color: catColor, fontSize: 14, fontWeight: 500, padding: '3px 0' }}>{s}</span>)}
            </div>
          </div>}
        </div>}
        {renderNearbySection(nearby)}
      </div>
    </>;
  };

  if (loading) return <div data-visitor-theme={themeMode} style={{ ...themeRootStyle, maxWidth: 448, margin: '0 auto', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: premiumTheme.colors.paper }}><div style={{ textAlign: 'center' }}>
    <div style={{ width: 80, height: 80, borderRadius: 8, background: premiumTheme.colors.surface, border: `1px solid ${premiumTheme.colors.warmLine}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'}}><Compass style={{ width: 40, height: 40, color: premiumTheme.colors.accent }} /></div>
    <h1 style={{ fontSize: 28, fontWeight: 500, color: premiumTheme.colors.ink, marginBottom: 6, fontFamily: type.display.family }}>Zgharta Caza</h1>
    <p style={{ color: premiumTheme.colors.mutedInk, fontSize: 14, marginBottom: 24 }}>North Lebanon · شمال لبنان</p>
    <Loader2 style={{ width: 24, height: 24, color: premiumTheme.colors.accent, animation: 'spin 1s linear infinite', margin: '0 auto' }} />
  </div><style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style></div>;
  if (error) return <div data-visitor-theme={themeMode} style={{ ...themeRootStyle, maxWidth: 448, margin: '0 auto', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: visitorTheme.errorBg, padding: 24 }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div><h2 style={{ color: visitorTheme.errorInk, marginBottom: 8 }}>{t('Connection Error', 'خطأ في الاتصال')}</h2><p style={{ color: visitorTheme.meta, marginBottom: 16, fontSize: 14 }}>{error}</p><button onClick={fetchData} style={{ padding: '12px 24px', background: visitorTheme.accent, color: visitorTheme.onAccent, border: 'none', borderRadius: 9999, cursor: 'pointer' }}>{t('Try Again', 'حاول مجدداً')}</button></div></div>;

  // The provider renders no DOM of its own; it publishes the RESOLVED palette as values for the
  // two module-level image components, which are the only things left that need one (#428). The
  // loading and error shells are outside it because neither mounts a PlaceImage.
  if (selectedPlace) {
    return <VisitorPaletteContext.Provider value={mapPalette}><div data-visitor-theme={themeMode} style={{ ...themeRootStyle, maxWidth: 448, margin: '0 auto', background: premiumTheme.colors.paper, minHeight: '100vh', fontFamily: fontFor(isRTL, 'body') }}>
      <PlaceDetailScreen place={selectedPlace} onBack={closePlaceDetail} />
    </div></VisitorPaletteContext.Provider>;
  }

  const detailBackgroundProps = detailId == null ? {} : { inert: '', 'aria-hidden': 'true' };
  return <VisitorPaletteContext.Provider value={mapPalette}><div data-visitor-theme={themeMode} style={{ ...themeRootStyle, maxWidth: 448, margin: '0 auto', background: visitorTheme.rootBg, minHeight: '100vh', ...(tab === 'map' ? { height: '100vh', overflow: 'hidden' } : {}), fontFamily: tab === 'map' ? mapFontFor(isRTL) : fontFor(isRTL, 'body') }}>
    <div data-testid="active-screen-container" {...detailBackgroundProps}>
      {tab === 'guide' && <GuideScreen places={publicDisplayPlaces} businesses={publicDisplayBusinesses} events={events} lang={lang} isRTL={isRTL} t={t} setTab={setTab} setLang={setLang} askPanel={<AskZghartaPanel {...askPanelProps} surface="guide" />} themeChoice={themeChoice} onChooseTheme={setThemeMode} onOpenItem={item => publicDisplayBusinesses.includes(item) ? setSelBiz(item) : openPlaceDetail(item)} />}
      {tab === 'explore' && <ExploreScreen places={publicDisplayPlaces} businesses={publicDisplayBusinesses} lang={lang} isRTL={isRTL} t={t} catFilter={catFilter} setCatFilter={setCatFilter} villageFilter={mapVillageFilter} setVillageFilter={setMapVillageFilter} zoneId={zoneId} setZoneId={value => { setZoneId(value); clearSelectedMarker(); }} setSelPlace={openPlaceDetail} setSelBiz={setSelBiz} />}
      {tab === 'events' && <EventsScreen events={events} lang={lang} isRTL={isRTL} t={t} setSelEvent={setSelEvent} setTab={setTab} onShareWeek={shareEventsWeek} screenContainer={screenContainer} />}
      {tab === 'favorites' && FavsScreen()}
    </div>
    <div data-testid="map-screen-container" data-visitor-theme={themeMode} {...detailBackgroundProps} style={{ ...(tab !== 'map' ? { display: 'none' } : {}) }}>{MapScreen()}</div>
    {detailId != null && <div
      data-testid={selEvent ? 'event-modal' : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={selEvent ? 'event-detail-title' : 'business-detail-title'}
      style={modalContainer}
    >
      <button ref={detailCloseButtonRef} type="button" aria-label={t('Close', 'إغلاق')} onClick={closeDetail} style={{ ...modalBackBtn, zIndex: 2 }}><ArrowLeft style={{ width: 20, height: 20, color: visitorTheme.title, transform: isRTL ? 'rotate(180deg)' : 'none' }} /></button>
      {selBiz && <BizModal business={selBiz} />}
      {selEvent && <EventModal event={selEvent} />}
    </div>}
    <nav data-visitor-theme={themeMode} {...detailBackgroundProps} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40, maxWidth: 448, margin: '0 auto', paddingBottom: 'env(safe-area-inset-bottom)', ...(tab === 'map' ? { background: visitorTheme.mapNavBg, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderTop: `1px solid ${visitorTheme.mapNavLine}` } : { background: premiumTheme.colors.surface, borderTop: `1px solid ${premiumTheme.colors.warmLine}` }) }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 2, padding: '6px 8px 7px' }}>
        {[{ id: 'map', icon: MapIcon, l: t('Discover', 'اكتشف'), aria: t('Discover map', 'اكتشف الخريطة') }, { id: 'explore', icon: Search, l: t('Directory', 'الأماكن'), aria: t('Places directory', 'دليل الأماكن') }, { id: 'events', icon: Calendar, l: t('Events', 'فعاليات'), aria: t('Events calendar', 'رزنامة الفعاليات') }, { id: 'guide', icon: Compass, l: t('Guide', 'دليل'), aria: t('Trip guide', 'دليل الرحلة') }, { id: 'favorites', icon: Heart, l: t('Saved', 'محفوظ'), aria: t('Saved places', 'الأماكن المحفوظة') }].map(navItem => {
          const active = tab === navItem.id;
          const activeTone = { color: visitorTheme.accentBright, bg: visitorTheme.navActiveBg };
          return <button key={navItem.id} aria-label={navItem.aria} title={navItem.aria} aria-current={active ? 'page' : undefined} onClick={() => { setTab(navItem.id); clearPlaceDetail(); setSelBiz(null); setSelEvent(null); }} style={{ minWidth: 0, minHeight: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px 2px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 14, color: active ? activeTone.color : (tab === 'map' ? visitorTheme.navMapInk : visitorTheme.meta), transition: 'color 0.2s ease' }}>
            <div style={{ width: 42, height: 28, background: active ? activeTone.bg : 'transparent', borderRadius: 10, transition: 'background 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <navItem.icon style={{ width: 22, height: 22, strokeWidth: active ? 2.2 : 1.8, transition: 'stroke-width 0.2s ease', fill: active && navItem.id === 'favorites' ? 'currentColor' : 'none' }} />
            </div>
            <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, lineHeight: 1.2, marginTop: 3, fontWeight: active ? 600 : 500 }}>{navItem.l}</span>
          </button>;
        })}
      </div>
    </nav>
  </div></VisitorPaletteContext.Provider>;
}
